// Airline ICAO 3-letter operator designators -> airline name, keyed by the code that prefixes an
// ICAO flight callsign (e.g. "AAL123" -> American Airlines). Used to resolve an operator name from
// a live callsign when FlightRadar24 doesn't give us one directly (see
// resolveOperatorFromCallsign below).
//
// Derived from OpenFlights' community-maintained airlines dataset (ODbL-licensed), not the paid
// ICAO Doc 8585 (Designators for Aircraft Operating Agencies) -- same category of source as
// aircraft-type-designators.ts's ICAO Doc 8643 mirror:
// https://github.com/jpatokal/openflights/blob/master/data/airlines.dat
//
// Kept every row with a valid 3-letter ICAO code and a name. Where multiple rows share an ICAO
// code (airline renames, defunct/reused designators), the entry marked "Active" in the source
// data wins; otherwise the first one encountered is kept.
export const AIRLINE_ICAO_PREFIXES: Record<string, string> = {
    AAA: "Ansett Australia",
    AAB: "Abelag Aviation",
    AAC: "Army Air Corps",
    AAD: "Aero Aviation Centre Ltd.",
    AAF: "Aigle Azur",
    AAG: "Air Atlantique",
    AAH: "Aloha Airlines",
    AAI: "Air Aurora",
    AAJ: "Air Alma",
    AAK: "Alaska Island Air",
    AAL: "American Airlines",
    AAM: "Aviation Management Corporation",
    AAN: "Amsterdam Airlines",
    AAO: "Atlantis Airlines (USA)",
    AAP: "Aerovista Airlines",
    AAQ: "Copterline",
    AAR: "Asiana Airlines",
    AAS: "Askari Aviation",
    AAT: "Austrian Airtransport",
    AAU: "Australia Asia Airlines",
    AAV: "Astro Air International",
    AAW: "Afriqiyah Airways",
    AAX: "Afric'air Express",
    AAY: "Allegiant Air",
    AAZ: "Angus Aviation",
    ABA: "Artem-Avia",
    ABB: "African Business and Transportations",
    ABD: "Air Atlanta Icelandic",
    ABE: "Aban Air",
    ABF: "Aerial Oy",
    ABG: "Abakan-Avia",
    ABH: "Hokuriki-Koukuu Company",
    ABI: "Aviabus",
    ABJ: "Abaet",
    ABK: "Alberta Citylink",
    ABL: "Air Busan",
    ABM: "Aero Albatros",
    ABN: "Air Fret Senegal",
    ABO: "APSA Colombia",
    ABP: "Aba Air",
    ABQ: "Airblue",
    ABR: "Air Contractors",
    ABS: "Transwest Air",
    ABT: "Air Brousse",
    ABU: "Aerovias Bueno",
    ABV: "Antrak Air",
    ABW: "AirBridge Cargo",
    ABX: "Airborne Express",
    ABY: "Air Arabia",
    ABZ: "ATA Brasil",
    ACA: "Air Canada",
    ACC: "Avcard Services",
    ACD: "Academy Airlines",
    ACE: "Race Cargo Airlines",
    ACF: "Centro de Formacion Aeronautica de Canarias",
    ACG: "Air Partner",
    ACI: "Air Caledonie International",
    ACK: "Nantucket Airlines",
    ACL: "Itali Airlines",
    ACM: "Air Caledonia",
    ACN: "Central American Airlines",
    ACO: "Aero Comondu",
    ACP: "Astral Aviation",
    ACQ: "Nuevo Continente",
    ACR: "Aerocenter",
    ACS: "Air Cess",
    ACU: "Air Cargo Transportation System",
    ACV: "Air Charter Service",
    ACW: "Royal Air Force",
    ACX: "Air Charters",
    ACY: "Atlas Cargo Lines",
    ADA: "Airservices Australia",
    ADB: "Antonov Airlines",
    ADC: "Air Atlantic Dominicana",
    ADD: "Advanced Air Co.",
    ADE: "Ada Air",
    ADG: "Aerea Flying Training Organization",
    ADH: "Air One",
    ADI: "Audeli Air",
    ADJ: "Abicar",
    ADK: "ADC Airlines",
    ADL: "Aero Dynamics",
    ADM: "Aerolineas Dominicanas",
    ADN: "Aerodienst GmbH",
    ADO: "Hokkaido International Airlines",
    ADP: "Aerodiplomatic",
    ADQ: "Avion Taxi",
    ADR: "Adria Airways",
    ADS: "Aviones de Sonora",
    ADT: "Air Dorval",
    ADU: "Airdeal Oy",
    ADV: "Advance Air Charters",
    ADW: "Air Andaman",
    ADX: "Anderson Aviation",
    ADY: "Aerodyne",
    AEA: "Air Europa",
    AEB: "Aero Benin",
    AEC: "Aerocesar",
    AED: "Aerotrans Airlines",
    AEE: "Aegean Airlines",
    AEF: "Aero Lloyd (YP)",
    AEG: "Aerofumigaciones Sam",
    AEH: "Aviaexpress",
    AEI: "Aeroexpreso Interamericano",
    AEJ: "Air Express",
    AEK: "Aerocon",
    AEL: "Air Europe",
    AEM: "Aero Madrid",
    AEN: "Aeroenlaces Nacionales",
    AEO: "Aeroservicios Ejecutivos Del Occidente",
    AEP: "Aerotec",
    AEQ: "Air Express",
    AER: "Alaska Central Express",
    AES: "ACES Colombia",
    AET: "Aeronautical Radio of Thailand",
    AEU: "Astraeus",
    AEV: "Aeroventas",
    AEW: "Aerosvit Airlines",
    AEX: "Airway Express",
    AEY: "Air Italy",
    AEZ: "Aerial Transit Company",
    AFA: "Alfa Air",
    AFB: "American Falcon",
    AFC: "African West Air",
    AFD: "Panorama Flight Service",
    AFE: "Airfast Indonesia",
    AFG: "Ariana Afghan Airlines",
    AFH: "Air Fecteau",
    AFI: "Africaone",
    AFJ: "Alliance",
    AFK: "Africa Air Links",
    AFL: "Aeroflot Russian Airlines",
    AFO: "Aero Empresa Mexicana",
    AFP: "Portuguese Air Force",
    AFQ: "Alba Servizi Aerotrasporti",
    AFR: "Air France",
    AFS: "Air Data",
    AFU: "Afrinat International Airlines",
    AFV: "Air Afrique Vacancies",
    AFW: "Afrique Regional Airways",
    AFX: "Airfreight Express",
    AFY: "Africa Chartered Services",
    AFZ: "Africa Freight Services",
    AGA: "Aeronaves Del Centro",
    AGB: "Air Service Gabon",
    AGC: "Arab Agricultural Aviation Company",
    AGE: "Servicios Aereos de Los Angeles",
    AGF: "Atlantic Gulf Airlines",
    AGG: "Algoma Airways",
    AGH: "Altagna",
    AGM: "Air Guam",
    AGN: "Air Gabon",
    AGO: "Angola Air Charter",
    AGP: "AERFI Group",
    AGQ: "Aerogala",
    AGR: "United States Department Of Agriculture",
    AGT: "Amadeus Global Travel Distribution",
    AGU: "Angara Airlines",
    AGV: "Air Glaciers",
    AGW: "Aero Gambia",
    AGX: "Aviogenex",
    AGY: "Aero Flight Service",
    AGZ: "Agrolet-Mci",
    AHA: "Air Alpha Greenland",
    AHC: "Azal Avia Cargo",
    AHD: "Czech Air Handling",
    AHE: "Airport Helicopter Basel",
    AHF: "Aspen Helicopters",
    AHG: "Aerochago Airlines",
    AHH: "Airplanes Holdings",
    AHI: "Servicios Aereos de Chihuahua Aerochisa",
    AHK: "Air Hong Kong",
    AHM: "Garrison Aviation",
    AHO: "Air Hamburg (AHO)",
    AHP: "Aerochiapas",
    AHR: "Air Adriatic",
    AHS: "Air Viggi San Raffaele",
    AHT: "HTA Helicopteros",
    AHU: "ABC Air Hungary",
    AHW: "Aeromist-Kharkiv",
    AHY: "Azerbaijan Airlines",
    AIA: "Avies",
    AIB: "Airbus Industrie",
    AIC: "Air India Limited",
    AIE: "Air Inuit",
    AIF: "Compania Aerea de Valencia",
    AIG: "Air Inter Gabon",
    AIH: "Alpine Air Chile",
    AII: "Air Integra",
    AIJ: "ABC Aerolineas",
    AIK: "African Airlines International Limited",
    AIL: "Air Illinois",
    AIM: "Trabajos Aereos Murcianos",
    AIN: "African International Airways",
    AIO: "United States Air Force",
    AIP: "Alpine Air Express",
    AIQ: "Thai AirAsia",
    AIR: "Airlift International",
    AIS: "Air Sureste",
    AIT: "Airest",
    AIU: "Alicante Internacional Airlines",
    AIV: "Airvias S/A Linhas Aereas",
    AIW: "Atlantic Island Airways",
    AIX: "Aircruising Australia",
    AIY: "Aircrew Check and Training Australia",
    AIZ: "Arkia Israel Airlines",
    AJA: "A J Services",
    AJB: "Aero JBR",
    AJC: "Bar Harbor Airlines",
    AJE: "Aero Jet Express",
    AJF: "Avia Consult Flugbetriebs",
    AJI: "Ameristar Jet Charter",
    AJJ: "A2 Jet Leasing",
    AJK: "Allied Air",
    AJM: "Air Jamaica",
    AJO: "Aeroejecutivo",
    AJP: "Aero Jets Corporativos",
    AJS: "Aeroejecutivos Colombia",
    AJT: "Amerijet International",
    AJU: "Air Jetsul",
    AJV: "ANA & JP Express",
    AJW: "Alpha Jet International",
    AJX: "Air Japan",
    AJY: "Ajet",
    AKA: "Air Korea Co. Ltd.",
    AKB: "Aktjubavia",
    AKC: "Arca Aerovias Colombianas Ltda.",
    AKF: "Anikay Air Company",
    AKG: "84 Squadron Royal Air Force @ RAF Akrotiri",
    AKH: "Akhal",
    AKI: "Ibk-Petra",
    AKK: "Aklak Air",
    AKL: "Air Kiribati",
    AKM: "Mak Air",
    AKN: "Alkan Air",
    AKR: "Arctic Air",
    AKT: "Karat",
    AKW: "Angkor Airways",
    AKX: "Air Nippon Network Co. Ltd.",
    AKY: "Yak-Service",
    AKZ: "AK Navigator LLC",
    ALB: "Aero Albatros",
    ALC: "Southern Jersey Airways",
    ALD: "Albion Aviation",
    ALE: "Aeroalas Colombia",
    ALF: "Allied Command Europe (Mobile Force)",
    ALG: "Air Logistics",
    ALJ: "Heli Ambulance Team",
    ALK: "SriLankan Airlines",
    ALL: "Aerovallarta",
    ALM: "Air ALM",
    ALN: "Air Lincoln",
    ALO: "Allegheny Commuter Airlines",
    ALP: "Alpliner AG",
    ALQ: "Altair Aviation (1986)",
    ALR: "Lineas Aereas Alaire S.L.",
    ALS: "Naka Nihon Airlines Service",
    ALU: "Air Luxor STP",
    ALV: "Alas de Venezuela",
    ALW: "Alas Nacionales",
    ALX: "Hewa Bora Airways",
    ALY: "Alyeska Air Service",
    ALZ: "Alta Flights (Charters) Ltd.",
    AMA: "ATMA",
    AMB: "Deutsche Rettungsflugwacht",
    AMC: "Air Malta",
    AMD: "Aerolineas Medellin",
    AME: "Spanish Air Force",
    AMF: "Ameriflight",
    AMG: "Air Minas Linhas A",
    AMH: "Alan Mann Helicopters Ltd.",
    AMI: "Air Maldives",
    AMJ: "Aviation Amos",
    AMK: "Amerer Air",
    AML: "Air Malawi",
    AMM: "Aeroputul International Marculesti",
    AMN: "Air Montenegro",
    AMO: "Air Montreal (Air Holdings Inc.)",
    AMP: "Aero Transporte S.A. (ATSA)",
    AMQ: "Aeromedicare Ltd.",
    AMR: "Air Specialties Corporation",
    AMS: "Air Muskoka",
    AMT: "ATA Airlines",
    AMU: "Air Macau",
    AMV: "AMC Airlines",
    AMW: "Air Midwest",
    AMX: "AeroMéxico",
    AMY: "Air Ambar",
    AMZ: "Amiya Airline",
    ANA: "All Nippon Airways",
    ANB: "Air Navigation And Trading Co. Ltd.",
    ANC: "Anglo Cargo",
    AND: "National Jet Service",
    ANE: "Air Nostrum",
    ANG: "Air Niugini",
    ANH: "Alajnihah for Air Transport",
    ANI: "Air Atlantic (Nig) Limited",
    ANK: "Air Nippon",
    ANL: "Nacoia Lda",
    ANM: "Antares Airtransport",
    ANO: "Airnorth",
    ANQ: "Aerol",
    ANS: "Andes Lineas Aereas",
    ANT: "Air North Charter - Canada",
    ANU: "Andalus Lineas Aereas",
    ANV: "Air Nevada",
    ANW: "Aviacion Del Noroeste",
    ANX: "Secretaria de Marina",
    ANZ: "Air New Zealand",
    AOA: "Alcon Servicios Aereos",
    AOC: "AVCOM",
    AOD: "Aero Vodochody",
    AOE: "Air One Executive",
    AOF: "Atair Pty Ltd.",
    AOG: "Aero VIP",
    AOI: "Astoria",
    AOK: "Aeroatlantico Colombia",
    AOL: "Angkor Airlines",
    AOM: "AOM French Airlines",
    AON: "Aero Entreprise",
    AOO: "As",
    AOP: "Aeropiloto",
    AOR: "Afro International Ent. Limited",
    AOS: "Servicios Aerecs Del Sol",
    AOT: "Asia Overnight Express",
    AOU: "Air Tractor",
    AOV: "Aero Vision",
    AOW: "Air Andaman (2Y)",
    AOX: "Aerotaxi Del Valle",
    APA: "Air Park Aviation Ltd.",
    APC: "Airpac Airlines",
    APD: "Sabre Pacific",
    APE: "Parcel Express",
    APF: "Amapola Flyg AB",
    APG: "Air People International",
    APH: "Alpha Aviation",
    API: "ASA Pesada",
    APJ: "Air Print",
    APL: "Appalachian Flying Service",
    APM: "Airpac",
    APO: "Aeropro",
    APP: "Aerolineas Pacifico Atlantico",
    APQ: "Aspen Aviation",
    APT: "LAP Colombia - Lineas Aereas Petroleras",
    APU: "Aeropuma",
    APV: "Air Plan International",
    APW: "Arrow Air",
    APX: "Apex Air Cargo",
    APY: "APA Internacional",
    AQA: "Aeroatlas",
    AQL: "Aquila Air Ltd.",
    AQN: "Air Queensland",
    AQO: "Aluminum Company Of America",
    AQT: "Aviones de Renta de Quintana Roo",
    AQU: "AirQuarius Aviation",
    AQZ: "Aerodyne Charter Company",
    ARA: "Arik Air",
    ARB: "Avia Air N.V.",
    ARC: "Air Routing International Corp.",
    ARD: "Aerocondor",
    ARE: "Aires",
    ARF: "Aero Flight",
    ARG: "Aerolineas Argentinas",
    ARH: "Arrowhead Airways",
    ARI: "Aero Vics",
    ARJ: "Aerojet de Costa Rica",
    ARK: "Aero Link Air Services S.L.",
    ARL: "Airlec - Air Aquitaine Transport",
    ARM: "Aeromarket Express",
    ARO: "Arrow Aviation Ltd.",
    ARP: "RPA-Aviataxi Ltd.",
    ARQ: "Armstrong Air",
    ARR: "Air Armenia",
    ARS: "Aeromet Servicios",
    ART: "Aerotal Aerolineas Territoriales de Colombia Ltda",
    ARU: "Aruba Airlines",
    ARV: "Aravco Ltd.",
    ARW: "Aria",
    ARX: "Air Xpress",
    ARY: "Argosy Airways",
    ARZ: "Air Resorts",
    ASA: "Alaska Airlines",
    ASB: "Air-Spray 1967 Ltd.",
    ASC: "Air Star Corporation",
    ASD: "Air Sinai",
    ASE: "Airstars",
    ASF: "Air Schefferville",
    ASG: "African Star Airways (PTY) Ltd.",
    ASH: "Mesa Airlines",
    ASI: "Aerosun International",
    ASJ: "Air Satellite",
    ASK: "La Ronge Aviation Services",
    ASL: "Air Serbia",
    ASM: "Awesome Flight Services (PTY) Ltd.",
    ASN: "Air and Sea Transport",
    ASO: "Aero Slovakia",
    ASP: "Airsprint",
    ASQ: "Atlantic Southeast Airlines",
    ASR: "All Star Airlines",
    ASS: "Air Class",
    AST: "Aerlineas Del Oeste",
    ASU: "Compania Boliviana de Transporte Aereo Privado Aerosur",
    ASV: "Astravia-Bissau Air Transports Ltd.",
    ASW: "Air Southwest Ltd.",
    ASX: "Air Special",
    ASY: "Royal Australian Air Force",
    ASZ: "Astrakhan Airlines",
    ATA: "Air Transport Association",
    ATB: "Atlantair Ltd.",
    ATC: "Air Tanzania",
    ATD: "Aerotours Dominicana",
    ATE: "Atlantis Transportation Services",
    ATF: "Compania Aerotecnicas Fotograficas",
    ATG: "Aerotrans Airline",
    ATH: "Air Travel Corp.",
    ATI: "Aero-Tropics Air Services",
    ATJ: "Air Traffic GmbH",
    ATK: "AeroTACA",
    ATL: "Aerocentral",
    ATM: "Airlines Of Tasmania",
    ATN: "Air Transport International",
    ATP: "ASTRAL Colombia - Aerotransportes Especiales Ltda.",
    ATQ: "Air Transport Schiphol",
    ATR: "Atlas Airlines",
    ATS: "Air Transport Service",
    ATT: "Aer Turas",
    ATU: "Atlant Aerobatics Ltd.",
    ATV: "Avanti Air",
    ATW: "Aero Trades (Western) Ltd.",
    ATX: "Warwickshire Aerocentre Ltd.",
    AUA: "Austrian Airlines",
    AUB: "Augsburg Airways",
    AUC: "Transaustralian Air Express",
    AUD: "Audi Air",
    AUF: "Augusta Air Luftfahrtunternehmen",
    AUH: "Abu Dhabi Amiri Flight",
    AUI: "Ukraine International Airlines",
    AUJ: "Business Flight Salzburg",
    AUK: "AUOS",
    AUL: "Aeroflot-Nord",
    AUM: "Air Atlantic Uruguay",
    AUN: "Aviones Unidos",
    AUO: "Empresa (Aero Uruguay)",
    AUP: "Avia Business Group",
    AUR: "Aurigny Air Services",
    AUS: "Aus-Air",
    AUT: "Austral Lineas Aereas",
    AUU: "Aurora Aviation",
    AUX: "Air Uganda International Ltd.",
    AUY: "Aerolineas Uruguayas",
    AUZ: "Australian Airlines",
    AVA: "Avianca - Aerovias Nacionales de Colombia",
    AVB: "Aviation Beaufort",
    AVD: "Alamo Aviacion",
    AVE: "Avensa",
    AVF: "Aviair Aviation Ltd.",
    AVG: "Air Falcon",
    AVH: "AV8 Helicopters",
    AVJ: "Avia Traffic Company",
    AVK: "AV8 Helicopters",
    AVM: "Aviacion Ejecutiva Mexicana",
    AVN: "Air Vanuatu",
    AVO: "Aviation at Work",
    AVP: "Avcorp Registrations",
    AVQ: "Aviation Services",
    AVR: "Active Aero Charter",
    AVS: "Avialsa T-35",
    AVT: "Asia Avia Airlines",
    AVU: "Avia Sud A",
    AVV: "Airvantage Incorporated",
    AVW: "Aviator Airways",
    AVX: "Aviapaslauga",
    AVY: "Aerovaradero",
    AVZ: "Air Valencia",
    AWA: "Asia Wings",
    AWB: "Airways International",
    AWC: "Titan Airways",
    AWD: "Providence Aviation Services",
    AWE: "America West Airlines",
    AWI: "Air Wisconsin",
    AWJ: "Sahel Airlines",
    AWK: "Airwork",
    AWL: "Australian Wetleasing",
    AWM: "Asian Wings Airways",
    AWN: "Air Niamey",
    AWO: "Awood Air Ltd.",
    AWQ: "Indonesia AirAsia",
    AWR: "Arctic Wings And Rotors Ltd.",
    AWS: "Arab Wings",
    AWT: "Air West",
    AWU: "Aeroline GmbH",
    AWV: "Airwave Transport",
    AWW: "Air Wales",
    AWY: "Aeroway",
    AWZ: "AirWest",
    AXB: "Air India Express",
    AXC: "Indochina Airlines",
    AXD: "Air Express",
    AXE: "Air Explore",
    AXF: "Asian Express Airlines",
    AXH: "Aeromexhaga",
    AXI: "Aeron International Airlines",
    AXK: "African Express Airways",
    AXL: "Air Exel",
    AXM: "AirAsia",
    AXN: "Alexandair",
    AXP: "Aeromax",
    AXQ: "Action Airlines (Action Air Charter)",
    AXR: "Axel Rent",
    AXS: "Altus Airlines",
    AXV: "Aviaxess",
    AXX: "Avioimpex A.D.p.o.",
    AXY: "Axis Airways",
    AXZ: "Aereonautica militare",
    AYB: "Belgian Army",
    AYD: "Aladia Airlines",
    AYK: "Aykavia Aircompany",
    AYM: "Airman",
    AYN: "Atlantic Airlines",
    AYR: "Flight Training Europe",
    AYS: "Awsaj Aviation Services",
    AYT: "Ayeet Aviation & Tourism",
    AYU: "Yuhi Air Lines",
    AYZ: "Atlant-Soyuz Airlines",
    AZA: "Alitalia",
    AZB: "Azamat",
    AZE: "Arcus-Air Logistic",
    AZF: "Air Zermatt AG",
    AZG: "Sakaviaservice",
    AZI: "Azzurra Air",
    AZK: "Azalhelikopter",
    AZL: "Africa One",
    AZM: "Aerocozumel",
    AZN: "Amaszonas",
    AZP: "Arizona Pacific Airways",
    AZQ: "Silk Way Airlines",
    AZR: "Zenith Air",
    AZS: "Aviacon Zitotrans Air Company",
    AZT: "Azimut",
    AZU: "Azul",
    AZV: "Azov Avia Airlines",
    AZW: "Air Zimbabwe",
    AZX: "Air Max Africa",
    AZY: "Arizona Airways",
    AZZ: "Azza Transport",
    BAA: "Balkan Agro Aviation",
    BAB: "Bahrain Air BSC (Closed)",
    BAC: "BAC Leasing Limited",
    BAE: "BAE Systems",
    BAF: "Belgian Air Force",
    BAG: "dba",
    BAH: "The Amiri Flight",
    BAJ: "Baker Aviation",
    BAK: "Blackhawk Airways",
    BAL: "Britannia Airways",
    BAM: "Business Air Services",
    BAN: "British Antarctic Survey",
    BAP: "Trans International Express Aviation",
    BAR: "Bradly Air (Charter) Services",
    BAS: "Aero Services",
    BAT: "Premiair",
    BAU: "Bissau Airlines",
    BAV: "Bay Aviation Ltd",
    BAW: "British Airways",
    BAX: "Best Aero Handling Ltd",
    BAY: "Bayon Airlines",
    BAZ: "BAE Systems Flight Training",
    BBA: "Bannert Air",
    BBB: "SwedJet Airways",
    BBC: "Biman Bangladesh Airlines",
    BBD: "Bluebird Cargo",
    BBE: "Ababeel Aviation",
    BBG: "Bluebird Airways (BZ)",
    BBL: "IBM Euroflight Operations",
    BBO: "Flybaboo",
    BBR: "Santa Barbara Airlines",
    BBS: "Beibars CJSC",
    BBT: "Air Bashkortostan",
    BBV: "Bravo Airlines",
    BBZ: "Blue Bird Aviation",
    BCB: "Carib Express",
    BCC: "BusinessAir",
    BCF: "BACH Flugbetriebsges",
    BCH: "Phillips Air",
    BCI: "Blue Islands",
    BCK: "Priority Aviation Company",
    BCL: "British Caribbean Airways",
    BCN: "Ocean Air",
    BCR: "British Charter",
    BCS: "European Air Transport",
    BCT: "BCT Aviation",
    BCV: "Business Aviation Center",
    BCY: "CityJet",
    BDA: "Blue Dart Aviation",
    BDF: "Bissau Discovery Flying Club",
    BDG: "Mississippi State University",
    BDM: "Air Bandama",
    BDN: "DERA Boscombe Down",
    BDR: "Badr Airlines",
    BDS: "South Asian Airlines",
    BEA: "Best Aviation Ltd",
    BEC: "State Air Company Berkut",
    BED: "Belgorod Aviation Enterprise",
    BEE: "Flybe",
    BEF: "Balear Express",
    BEH: "Bel Air Helicopters",
    BEK: "Berkut Air",
    BER: "Air Berlin",
    BES: "Aero Services Executive",
    BET: "BETA - Brazilian Express Transportes Aereos",
    BEU: "Bateleur Air",
    BEZ: "Kingfisher Air Services",
    BFA: "Presidence Du Faso",
    BFC: "Basler Flight Service",
    BFF: "Air Baffin",
    BFG: "Bear Flight",
    BFL: "Buffalo Airways",
    BFN: "Compangnie Nationale Naganagani",
    BFO: "Bombardier",
    BFR: "Burkina Airlines",
    BFS: "Business Flight Sweden",
    BFW: "Bahrain Defence Force",
    BGA: "Airbus Transport International",
    BGD: "Air Bangladesh",
    BGF: "Aviodetachment-28",
    BGG: "Aero BG",
    BGH: "BH Air",
    BGI: "British Gulf International",
    BGK: "British Gulf International-Fez",
    BGL: "Benin Golf Air",
    BGM: "Bugulma Air Enterprise",
    BGR: "Budget Air Bangladesh",
    BGT: "Bergen Air Transport",
    BGY: "Bingo Airways",
    BHA: "Buddha Air",
    BHC: "Aerotaxis De La Bahia",
    BHI: "Balkh Airlines",
    BHL: "Bristow Helicopters",
    BHN: "Bristow Helicopters Nigeria",
    BHO: "Bhoja Airlines",
    BHP: "Belair Airlines",
    BHR: "Bighorn Airways",
    BHS: "Bahamasair",
    BHT: "Bright Air",
    BHV: "Specavia Air Company",
    BHY: "Bosphorus European Airways",
    BIB: "Michelin Air Services",
    BID: "Binair",
    BIE: "Air Mediterranee",
    BIG: "Big Island Air",
    BIH: "British International Helicopters",
    BIL: "Billund Air Center",
    BIN: "Boise Interagency Fire Center",
    BIR: "Bird Leasing",
    BIS: "Irbis Air",
    BIV: "Aviaservice",
    BIZ: "Bizjet Ltd",
    BJA: "Baja Air",
    BJC: "Baltic Jet Aircompany",
    BJK: "Atlantic Airlines",
    BJS: "Business Jet Solutions",
    BJT: "ACM Aviation",
    BKA: "Bankair",
    BKF: "BF-Lento OY",
    BKH: "RAF Barkston Heath",
    BKJ: "Barken International",
    BKL: "Aircompany Barcol",
    BKP: "Bangkok Airways",
    BKR: "Civil Air Patrol South Carolina Wing",
    BKV: "Bukovyna",
    BKY: "Blue Sky America",
    BLA: "All Charter Limited",
    BLB: "Blue Bird Aviation",
    BLC: "Bellesavia",
    BLE: "Blue Line",
    BLF: "Blue1",
    BLG: "Belgavia",
    BLH: "Blue Horizon Travel Club",
    BLI: "Thyssen Krupp AG",
    BLJ: "Blue Jet",
    BLK: "Westcoast Energy",
    BLL: "Baltic Airlines",
    BLM: "Blue Airways",
    BLN: "Bali International Air Service",
    BLR: "Atlantic Coast Airlines",
    BLS: "Bearskin Lake Air Service",
    BLT: "Baltic Aviation",
    BLU: "IMP Aviation Services",
    BLV: "Bellview Airlines",
    BLW: "Wermlandsflyg AB",
    BLX: "TUIfly Nordic",
    BLY: "Starair",
    BLZ: "Aero Barloz",
    BMA: "bmi",
    BMD: "British Medical Charter",
    BME: "Briggs Marine Environmental Services",
    BMH: "Bristow Masayu Helicopters",
    BMI: "bmibaby",
    BMJ: "Bemidji Airlines",
    BMK: "GST Aero Aircompany",
    BML: "Bismillah Airlines",
    BMM: "Atlas Blue",
    BMN: "Bowman Aviation",
    BMR: "British Midland Regional",
    BMV: "Alatau Airlines",
    BMW: "BMW",
    BMX: "Banco de Mexico",
    BNB: "Aero Banobras",
    BNC: "Sundance Air",
    BND: "Bond Offshore Helicopters",
    BNE: "Benina Air",
    BNF: "Braniff International Airways",
    BNG: "BN Group Limited",
    BNI: "Alberni Airways",
    BNL: "Blue Nile Ethiopia Trading",
    BNR: "Bonair Aviation",
    BNS: "Bancstar - Valley National Corporation",
    BNT: "Bentiu Air Transport",
    BNV: "Benane Aviation Corporation",
    BNW: "British North West Airlines",
    BNX: "LAI - Linea Aerea IAACA",
    BNZ: "Aerolineas Bonanza",
    BOA: "Boniair",
    BOC: "Aerobona",
    BOD: "Bond Air Services",
    BOE: "Boeing",
    BOF: "Bordaire",
    BOI: "Aboitiz Air",
    BOL: "Transportes Aereos Bolivianos",
    BON: "Air Bosna",
    BOO: "Bookajet Limited",
    BOS: "OpenSkies",
    BOT: "Air Botswana",
    BOU: "Bouraq Indonesia Airlines",
    BOV: "Boliviana de Aviacion (OB)",
    BOX: "Tiphook PLC",
    BPA: "Blue Panorama Airlines",
    BPK: "Berkhut ZK",
    BPO: "Bundepolizei-Fliegertruppe",
    BPS: "Budapest Aircraft Services/Manx2",
    BPT: "Bonus Aviation",
    BPX: "British Petroleum Exploration",
    BQB: "Buquebus Líneas Aéreas",
    BRA: "Braathens",
    BRB: "BRA-Transportes Aereos",
    BRD: "Brock Air Services",
    BRE: "Breeze Ltd",
    BRF: "Air Bravo",
    BRG: "Bering Air",
    BRK: "Briansk State Air Enterprise",
    BRL: "Air Brasd'or",
    BRM: "Air 500",
    BRN: "Branson Airlines",
    BRO: "BASE Regional Airlines",
    BRP: "Airport Bratsk",
    BRQ: "El-Buraq Air Transport",
    BRR: "Mountain Air Service",
    BRS: "Brazilian Air Force",
    BRT: "British Regional Airlines",
    BRU: "Belavia Belarusian Airlines",
    BRV: "Bravo Air Congo",
    BRW: "Bright Aviation Services",
    BRX: "Buffalo Express Airlines",
    BRY: "Burundayavia",
    BRZ: "Samara Airlines",
    BSA: "Black Stallion Airways",
    BSB: "Air Wings",
    BSC: "Bistair - Fez",
    BSD: "Blue Star Airlines",
    BSI: "Brasair Transportes Aereos",
    BSJ: "Blue Swann Aviation",
    BSK: "Miami Air International",
    BSM: "Blue Sky Aviation",
    BSN: "Connectair Charters",
    BSP: "Global Airways",
    BSR: "Guine Bissaur Airlines",
    BSS: "Bissau Aero Transporte",
    BST: "Best Air",
    BSW: "Blue Sky Airways",
    BSX: "Bassaka airlines",
    BSY: "Big Sky Airlines",
    BTA: "ExpressJet",
    BTC: "BAL Bashkirian Airlines",
    BTH: "Baltijas Helicopters",
    BTI: "Air Baltic",
    BTK: "Baltyka",
    BTL: "Baltia Air Lines",
    BTM: "Air Batumi",
    BTP: "Veritair Ltd",
    BTQ: "Boutique Air (Priv)",
    BTR: "Botir-Avia",
    BTS: "Aerotaxis Albatros",
    BTT: "BT-Slavuta",
    BTU: "Rolls-Royce plc",
    BTV: "Metro Batavia",
    BUB: "Air Bourbon",
    BUC: "Bulgarian Air Charter",
    BUE: "Orebro Aviation",
    BUL: "Blue Airlines",
    BUN: "Buryat Airlines Aircompany",
    BUR: "Air Bucharest",
    BUU: "Baikotovitchestrian Airlines ",
    BUZ: "Buzz Stansted",
    BVA: "Buffalo Airways",
    BVC: "Bulgarian Aeronautical Centre",
    BVN: "Baron Aviation Services",
    BVR: "ACM Air Charter",
    BVT: "Berjaya Air",
    BVU: "Bellview Airlines",
    BVV: "Spark+ Joint-Stock Company",
    BWA: "Caribbean Airlines",
    BWG: "Blue Wings",
    BWI: "Blue Wing Airlines",
    BWL: "BWA (2002) Ltd",
    BWY: "Fleet Requirements Air Direction Unit",
    BXA: "Bahrain Executive Air Services",
    BXH: "Bar XH Air",
    BXI: "Brussels International Airlines",
    BXR: "Redding Aero Enterprises",
    BXS: "Spantax S.A.",
    BYA: "Berry Aviation",
    BYC: "Northern Airlines Sanya",
    BYE: "Bayu Indonesia Air",
    BYL: "Bylina Joint-Stock Company",
    BYR: "Berytos Airlines",
    BYT: "Patriot Aviation Limited",
    BZA: "Bizair Fluggesellschaft",
    BZD: "Canadian Global Air Ambulance",
    BZE: "BRAZIL AIR",
    BZF: "Jet Aviation Business Jets",
    BZH: "Brit Air",
    BZL: "Aero Brazil",
    BZS: "Aero Biniza",
    BZZ: "Butane Buzzard Aviation Corporation",
    CAA: "Atlantic Southeast Airlines",
    CAB: "Chesapeake Air Service",
    CAC: "Conquest Airlines",
    CAD: "Chilliwack Aviation",
    CAE: "Colibri Aviation",
    CAG: "China National Aviation Corporation",
    CAH: "Charlan Air Charter",
    CAI: "Corendon Airlines",
    CAJ: "Air Caraibes Atlantique",
    CAK: "Congo Air",
    CAL: "China Airlines",
    CAM: "Camai Air",
    CAN: "Crest Aviation",
    CAO: "Air China Cargo",
    CAP: "CanXplorer",
    CAQ: "Cityair (Chester) Limited",
    CAR: "Inter RCA",
    CAS: "Christman Air System",
    CAT: "Civil Air Transport",
    CAV: "Calm Air",
    CAW: "Comair",
    CAX: "Central Air Express",
    CAY: "Cayman Airways",
    CAZ: "Cat Aviation",
    CBA: "Civil Aviation Inspectorate of the Czech Republic",
    CBB: "Cheboksary Airenterprise JSC",
    CBC: "Caribair",
    CBD: "Lockheed Martin Aeronautics",
    CBE: "Aerovias Caribe",
    CBF: "China Northern Airlines",
    CBG: "GX Airlines",
    CBI: "CABI",
    CBL: "Cumberland Airways (Nicholson Air Service)",
    CBM: "Cherokee Express",
    CBN: "Swedish Civil Aviation Administration",
    CBO: "Aerotaxi del Cabo",
    CBR: "Cabair College of Air Training",
    CBS: "Air Columbus",
    CBT: "Catalina Flying Boats",
    CBV: "Aero Cabo",
    CBY: "Coningsby Flying Training Unit",
    CCA: "Air China",
    CCB: "CARICOM AIRWAYS (BARBADOS) INC.",
    CCC: "CCML Airlines",
    CCE: "Cairo Air Transport Company",
    CCF: "CCF Manager Airline",
    CCG: "Central Connect Airlines",
    CCH: "Chilchota Taxi Aereo",
    CCI: "Capital Cargo International Airlines",
    CCK: "Flight Trac",
    CCM: "Corse-Mediterranee",
    CCP: "Champion Air",
    CCQ: "Capital City Air Carriers",
    CCV: "Centro De Helicopteros Corporativos",
    CCW: "Coptrade Air Transport",
    CCX: "Colt International",
    CCY: "Cherry Air",
    CDA: "Aerocardal",
    CDE: "Comed Group",
    CDG: "Shandong Airlines",
    CDI: "Cards Air Services",
    CDL: "Sunbird Airlines",
    CDM: "Carga Aerea Dominicana",
    CDN: "Canadian Airlines",
    CDP: "Aero Condor Peru",
    CDR: "Canadian Regional Airlines",
    CDS: "Spectrem Air",
    CDT: "Real Aero Club de Reus-Costa Dorado",
    CDU: "Aerotrans",
    CDV: "Airline Skol",
    CDY: "Heliaviation Limited",
    CEA: "RegionsAir",
    CEB: "Cebu Pacific",
    CEC: "Celtic Airways",
    CED: "CEDTA (Compania Ecuatoriana De Transportes Aereos)",
    CEE: "Servicios Aereos Centrales",
    CEF: "Czech Air Force",
    CEG: "Cega Aviation",
    CEL: "CEIBA Intercontinental",
    CEM: "Central Mongolia Airways",
    CEO: "Comfort Express Virtual Charters",
    CEP: "Chipola Aviation",
    CER: "Cetraca Aviation Service",
    CES: "China Eastern Airlines",
    CET: "Centrafrican Airlines",
    CEV: "Cente D'Essais En Vol",
    CEX: "Capitol Air Express",
    CEY: "Air Century",
    CFA: "China Flying Dragon Aviation",
    CFC: "Canadian Forces",
    CFD: "Cranfield University",
    CFE: "BA CityFlyer",
    CFF: "Aerofan",
    CFG: "Condor Flugdienst",
    CFH: "Care Flight",
    CFI: "Flight Inspection Center of the General Administration of Civil Aviation in China",
    CFJ: "Fujian Airlines",
    CFL: "Swedish Airlines",
    CFM: "ACEF",
    CFN: "Church Fenton Flying Training Unit",
    CFP: "Compania de Aviacion Faucett",
    CFR: "Africa One",
    CFS: "Empire Airlines",
    CFT: "Jet Freighters",
    CFU: "United Kingdom Civil Aviation Authority",
    CFV: "Aero Calafia",
    CFZ: "Zhongfei General Aviation",
    CGA: "Congressional Air",
    CGB: "Air Cargo Belize",
    CGC: "Cal Gulf Aviation",
    CGD: "Charlotte Air National Guard",
    CGE: "Nelson Aviation College",
    CGG: "Walmart Aviation",
    CGH: "Guizhou Airlines",
    CGI: "Rusair JSAC",
    CGK: "Click Airways",
    CGL: "Seagle Air",
    CGM: "Cargoman",
    CGN: "Changan Airlines",
    CGO: "Chicago Air",
    CGP: "Cargo Plus Aviation",
    CGR: "Compagnia Generale Ripreseaeree",
    CGS: "Centre of Applied Geodynamica",
    CGU: "Chinguetti Airlines",
    CGV: "Aero Clube Do Algarve",
    CGW: "Air Great Wall",
    CGX: "United States Coast Guard Auxiliary",
    CGY: "Corporacion Paraguaya De Aeronautica",
    CHA: "Central Flying Service",
    CHB: "West Air China",
    CHC: "China Ocean Helicopter Corporation",
    CHD: "223 Flight Unit State Airline",
    CHE: "Top Flight Air Service",
    CHF: "Chitaavia",
    CHG: "AC Challenge Aero",
    CHH: "Hainan Airlines",
    CHI: "Cougar Helicopters",
    CHJ: "Aircompany Chaika",
    CHK: "Chalk's Ocean Airways",
    CHL: "Cohlmia Aviation",
    CHM: "Chemech Aviation",
    CHN: "Channel Island Aviation",
    CHO: "Chrome Air Services",
    CHP: "Consorcio Aviaxsa",
    CHQ: "Chautauqua Airlines",
    CHR: "Air Charter Services",
    CHS: "Challenge Aviation",
    CHU: "Church Aircraft",
    CHV: "Air Charter Professionals",
    CHW: "Charter Air",
    CHZ: "Cherline",
    CIA: "Civil Aviation Authority",
    CIB: "Condor",
    CIC: "ICC Canada",
    CID: "Asia Continental Airlines",
    CIE: "Czech Government Flying Service",
    CIF: "CB Airways UK ( Interliging Flights )",
    CIG: "Sirius-Aero",
    CII: "Cityfly",
    CIK: "Country International Airlines",
    CIL: "Cecil Aviation",
    CIM: "Cimber Air",
    CIO: "Il Ciocco International Travel Service",
    CIP: "City-Air Germany",
    CIR: "Arctic Circle Air Service",
    CIU: "Cielos Airlines",
    CIV: "Civil Aviation Authority of New Zealand",
    CIW: "Civair Airways",
    CIX: "City Connexion Airlines",
    CJA: "CanJet",
    CJC: "Colgan Air",
    CJE: "Aeroservices Corporate",
    CJG: "Zhejiang Airlines",
    CJI: "Corporate Jets",
    CJR: "Caverton Helicopters",
    CJS: "Commonwealth Jet Service",
    CJT: "Cargojet Airways",
    CKA: "Cook Inlet Aviation",
    CKC: "CKC Services",
    CKE: "Corporate Aviation Services",
    CKK: "China Cargo Airlines",
    CKL: "Aviation Charter Services",
    CKM: "BKS Air (Rivaflecha)",
    CKR: "Crown Air Systems",
    CKS: "Kalitta Air",
    CLA: "Comlux Aviation",
    CLB: "Flight Precision Limited",
    CLD: "Clowes Estates Limited",
    CLE: "Colemill Enterprises",
    CLF: "Bristol Flying Centre",
    CLG: "Chalair Aviation",
    CLH: "Lufthansa CityLine",
    CLI: "Calima Aviacion",
    CLJ: "Cello Aviation",
    CLK: "Clark Aviation",
    CLL: "Aerovias Castillo",
    CLM: "Cargo Link (Caribbean)",
    CLN: "Barnes Olsen Aeroleasing",
    CLP: "Aero Club De Portugal",
    CLR: "Trans America",
    CLS: "Challenge Air Transport",
    CLT: "Caribbean Air Transport",
    CLU: "Triple Alpha",
    CLV: "Lom Praha Flying School",
    CLW: "Centralwings",
    CLX: "Cargolux",
    CLY: "Clay Lacy Aviation",
    CLZ: "Cloud 9 Air Charters",
    CMA: "Central European Airlines",
    CME: "Prince Edward Air",
    CMF: "Air Care Alliance",
    CMG: "Comet Airlines",
    CMH: "Commair Aviation",
    CMI: "Continental Micronesia",
    CMJ: "Mudanjiang General Aviation",
    CMK: "Chernomor-Avia",
    CML: "Commander Air Charter",
    CMM: "Compagnie Aerienne du Mali",
    CMN: "Eckles Aircraft",
    CMP: "Copa Airlines",
    CMR: "CAM Air Management",
    CMS: "Commercial Aviation",
    CMT: "Casement Aviation",
    CMU: "Northern Aviation Service",
    CMX: "El Caminante Taxi Aereo",
    CMY: "Cape Smythe Air",
    CMZ: "CM Stair",
    CNA: "Centavia",
    CNB: "Cityline Hungary",
    CNC: "Corporacion Aereo Cencor",
    CND: "Aero Continente Dominicana",
    CNE: "Air Toronto",
    CNF: "Canaryfly",
    CNG: "Coastal Airways",
    CNH: "Aquila Air",
    CNI: "Empresa Nacional De Servicios Aereos",
    CNJ: "Nanjing Airlines",
    CNK: "Sunwest Home Aviation",
    CNL: "Centennial Airlines",
    CNN: "Canadian World",
    CNO: "SAS Braathens",
    CNR: "Condor Aero Services",
    CNS: "Centennial Flight Centre",
    CNT: "CNET",
    CNU: "Air Consul",
    CNW: "China Northwest Airlines",
    CNX: "Allcanada Express",
    CNY: "Central Airways",
    COA: "Continental Airlines",
    COD: "Concordavia",
    COE: "Comtel Air",
    COF: "Confort Air",
    COH: "Coltishall Flying Training Unit",
    COL: "Columbia Airlines",
    COM: "Comair",
    CON: "Continental Oil",
    COO: "Corporate Airlink",
    COP: "Copper State Air Service",
    COT: "Cottesmore Flying Training Unit",
    COV: "Helicentre Coventry",
    COW: "Cowi",
    COY: "Coyne Aviation",
    COZ: "Cosmic Air",
    CPA: "Cathay Pacific",
    CPB: "Corpac Canada",
    CPD: "Capital Airlines",
    CPF: "ATS Private Company",
    CPG: "Corporacion Aeroangeles",
    CPH: "Champagne Airlines",
    CPI: "CAI",
    CPJ: "Baltimore Air Transport",
    CPL: "Chaparral Airlines",
    CPM: "Compagnie Mauritanienne Des Transports",
    CPN: "Caspian Airlines",
    CPO: "Corporate Aircraft Company",
    CPR: "Corporate Air",
    CPS: "Compass International Airways",
    CPT: "Corporate Air",
    CPV: "Air Corporate",
    CPW: "Chippewa Air Commuter",
    CPX: "Capital Air Service",
    CPZ: "Compass Airlines",
    CQC: "Central Queensland Aviation College",
    CQH: "Spring Airlines",
    CQN: "Chongqing Airlines",
    CRA: "Coronado Aerolineas",
    CRC: "Conair Aviation",
    CRD: "Air Corridor",
    CRE: "Cree Airways",
    CRF: "Air Central",
    CRG: "Cargoitalia",
    CRH: "CRI Helicopters Mexico",
    CRJ: "Air Cruzal",
    CRK: "Hong Kong Airlines",
    CRL: "Corsairfly",
    CRM: "Commander Mexicana",
    CRN: "Empresa Aerocaribbean",
    CRO: "Crown Airways",
    CRP: "Aerotransportes Corporativos",
    CRQ: "Air Creebec",
    CRR: "Carranza",
    CRS: "Comercial Aerea",
    CRT: "Caribintair",
    CRV: "Cargo Ivoire",
    CRW: "Crownair",
    CRX: "Cross Aviation",
    CRY: "Primavia Limited",
    CRZ: "Cruzeiro do Sul Servicos Aereos",
    CSA: "Czech Airlines",
    CSC: "Sichuan Airlines",
    CSD: "Courier Services",
    CSE: "CSE Aviation",
    CSF: "Clasair",
    CSH: "Shanghai Airlines",
    CSI: "Central Skyport",
    CSJ: "Castle Aviation",
    CSK: "Flightcraft",
    CSL: "California Air Shuttle",
    CSM: "Ratkhan Air",
    CSN: "China Southern Airlines",
    CSO: "Casino Airline",
    CSP: "Casper Air Service",
    CSQ: "IBC Airways",
    CST: "Coast Air",
    CSU: "Chari Aviation Services",
    CSV: "Coastal Travels",
    CSW: "Worldwide Air Charter Systems",
    CSX: "Choice Airways",
    CSY: "Shuangyang General Aviation",
    CSZ: "Shenzhen Airlines",
    CTA: "Aero Charter and Transport",
    CTE: "Air Tenglong",
    CTG: "Canadian Coast Guard",
    CTH: "China General Aviation Corporation",
    CTK: "East Midlands Helicopters",
    CTL: "Central Airlines",
    CTM: "Commandement Du Transport Aerien Militaire Francais",
    CTN: "Croatia Airlines",
    CTO: "Cape Air Transport",
    CTP: "Tashkent Aircraft Production Corporation",
    CTQ: "CTK Network Aviation",
    CTR: "Aerlineas Centauro",
    CTS: "Center-South",
    CTT: "Custom Air Transport",
    CTV: "Escuela De Pilotos Are Aviacion",
    CTW: "Cargo Three",
    CTY: "Cryderman Air Service",
    CTZ: "CATA L",
    CUA: "China United Airlines",
    CUB: "Cubana de Aviación",
    CUD: "Air Cudlua",
    CUI: "Cancun Air",
    CUK: "Polo Aviation",
    CUO: "Aerocuahonte",
    CUT: "Court Helicopters",
    CVA: "Air Chathams",
    CVC: "Centre-Avia",
    CVE: "Cabo Verde Express",
    CVF: "Dassault Falcon Jet Corporation",
    CVG: "Carill Aviation",
    CVL: "Coval Air",
    CVO: "Center Vol",
    CVR: "Chevron U.S.A",
    CVT: "Peran",
    CVU: "Grand Canyon Airlines",
    CVV: "Comeravia",
    CWA: "Canadian Western Airlines",
    CWC: "Centurion Air Cargo",
    CWD: "Caernarfon Airworld",
    CWE: "Celtic West",
    CWH: "Canadian Warplane Heritage Museum",
    CWK: "Comores Airlines",
    CWL: "Cranwell Flying Training Unit",
    CWM: "Air Marshall Islands",
    CWN: "Cardiff Wales Flying Club",
    CWP: "Australian Customs Service",
    CWS: "California Western",
    CWT: "Texas Airways",
    CWU: "Wuhan Airlines",
    CWW: "Canair",
    CWX: "Crow Executive Air",
    CWY: "Woodgate Executive Air Charter",
    CWZ: "Capitol Wings Airline",
    CXA: "Xiamen Airlines",
    CXH: "China Xinhua Airlines",
    CXI: "Shanxi Airlines",
    CXJ: "Xinjiang Airlines",
    CXN: "China Southwest Airlines",
    CXO: "Conroe Aviation Services",
    CXP: "Xtra Airways",
    CXS: "Boston-Maine Airways",
    CYA: "Cheyenne Airways",
    CYC: "Cyprair Tours",
    CYD: "Access Air",
    CYE: "Aerocheyenne",
    CYF: "Company Flight",
    CYG: "Yana Airlines",
    CYH: "Yunnan Airlines",
    CYL: "Air One Cityliner",
    CYM: "Compass Airlines (Australia)",
    CYN: "Zhongyuan Aviation",
    CYO: "Air Transport",
    CYP: "Cyprus Airways",
    CYS: "Cypress Airlines",
    CYT: "Crystal Shamrock",
    CYZ: "China Postal Airlines",
    CZV: "Via Conectia Airlines",
    DAA: "Decatur Aviation",
    DAB: "Dassault Aviation",
    DAC: "McDonnell Douglas",
    DAD: "Dorado Air",
    DAE: "DHL Aero Expreso",
    DAF: "Danish Air Force",
    DAG: "Dagestan Airlines",
    DAH: "Air Algerie",
    DAK: "First Flying",
    DAL: "Delta Air Lines",
    DAM: "Kyrgyzstan Department of Aviation",
    DAN: "Dan-Air London",
    DAO: "Daallo Airlines",
    DAP: "Aerovias DAP",
    DAR: "Danish Army",
    DAS: "Damascene Airways",
    DAT: "Brussels Airlines",
    DAU: "Dauair",
    DAV: "Dornier Aviation Nigeria",
    DAY: "Daya Aviation",
    DBA: "Air Alpha",
    DBD: "Air Niagara Express",
    DBJ: "Duchess of Britany (Jersey) Limited",
    DBK: "Dubrovnik Air",
    DBR: "Dutchbird",
    DCA: "Dreamcatcher Airways",
    DCC: "Caribbean Air Cargo",
    DCD: "Air 26",
    DCE: "Dutch Caribbean Express",
    DCL: "Transportes Aereos Don Carlos",
    DCM: "FLTPLAN",
    DCN: "Federal Armed Forces",
    DCO: "David Crawshaw Consultants Limited",
    DCS: "Daimler Chrysler Aviation",
    DCT: "Direct Flight",
    DCV: "Discover Air",
    DCX: "Daimler-Chrysler",
    DDA: "D & D Aviation",
    DEA: "Delta Aerotaxi",
    DEE: "Dixie Airways",
    DEF: "Aviation Defense Service",
    DEG: "Air Service Groningen",
    DEI: "Ecoair",
    DEL: "Carib Aviation",
    DER: "Deer Jet",
    DES: "Chilcotin Caribou Aviation",
    DET: "DETA Air",
    DEV: "Red Devils Parachute Display Team",
    DFA: "Aero Coach Aviation",
    DFS: "Dwyer Aircraft Services",
    DGO: "DGO Jet",
    DGT: "Digital Equipment Corporation",
    DGX: "Dasnair",
    DHA: "Dahla Airlines",
    DHC: "De Havilland",
    DHE: "DAP Helicopteros",
    DHI: "Adam Air",
    DHK: "DHL Air",
    DHL: "Astar Air Cargo",
    DHM: "Archer Aviation",
    DHV: "DHL Aviation",
    DHX: "DHL International",
    DIA: "Direct Air",
    DIC: "Aeromedica",
    DIN: "Aerodin",
    DIP: "Diplomatic Freight Services",
    DIR: "Dirgantara Air Service",
    DIS: "Di Air",
    DIX: "Dix Aviation",
    DJA: "Antinea Airlines",
    DJB: "Djibouti Airlines",
    DJE: "Durango Jet",
    DJS: "DayJet",
    DJT: "Denver Jet",
    DJU: "Air Djibouti",
    DKA: "Daka",
    DKE: "Jubilee Airways",
    DKH: "Juneyao Airlines",
    DKN: "Deccan Aviation",
    DKT: "Sioux Falls Aviation",
    DKY: "Servicios Aereos Elite",
    DLA: "Air Dolomiti",
    DLH: "Lufthansa",
    DLI: "Delta Express International",
    DLK: "Deccan Aviation (Lanka)",
    DLR: "Dala Air Services",
    DLS: "Aero Modelo",
    DLT: "Deutsche Luftverkehrsgesellschaft",
    DLU: "Aerolineas Del Sur",
    DMC: "Aerodinamica de Monterrey",
    DME: "Royal Flight",
    DMI: "Aeroservicios Dinamicos",
    DML: "Aerotaxis Dosmil",
    DMO: "Domodedovo Airlines",
    DNA: "Aerodespachos De El Salvador",
    DNC: "Aeroynamics Malaga",
    DND: "Eldinder Aviation",
    DNI: "Servicios Aereos Denim",
    DNJ: "Aerodynamics Incorporated",
    DNK: "D&K Aviation",
    DNL: "Dutch Antilles Express",
    DNM: "Denim Air",
    DNR: "Dynamair Aviation",
    DNU: "Danu Oro Transportas",
    DNV: "Aeroflot-Don",
    DNY: "Danish Navy",
    DOA: "Dominicana de Aviaci",
    DOB: "Dobrolet",
    DOC: "Norsk Luftambulanse",
    DOD: "USAF Air Mobility Operations Control Center",
    DOI: "U.S. Department of the Interior",
    DOJ: "Justice Prisoner and Alien Transportation System",
    DOM: "Dos Mundos",
    DON: "Donair Flying Club",
    DOP: "Dancopter",
    DOR: "Dornier",
    DOT: "Telnic Limited",
    DPL: "Dome Petroleum",
    DQA: "Island Aviation Services",
    DRB: "Didier Rousset Buy",
    DRC: "Triton Airlines",
    DRD: "Air Madrid",
    DRE: "Drummond Island Air",
    DRK: "Druk Air",
    DRM: "Airways Flight Training",
    DRN: "Central De Discos De Reynosa",
    DRO: "Aeronaves Del Noreste",
    DRT: "Darta",
    DRU: "Alrosa Mirny Air Enterprise",
    DRX: "Des R Cargo Express",
    DRY: "Deraya Air Taxi",
    DSA: "Danbury Airways",
    DSC: "Addis Air Cargo Services",
    DSH: "Dash Air Charter",
    DSK: "Aero Algarve",
    DSL: "Meridian Aviation",
    DSM: "LAN Argentina",
    DSN: "DESNA",
    DSO: "Dassault Falcon Service",
    DSQ: "Dasab Airlines",
    DSR: "DAS Air Cargo",
    DST: "Aex Air",
    DSU: "Delta State University",
    DSV: "Direct Aero Services",
    DSY: "Dennis Sky",
    DTA: "TAAG Angola Airlines",
    DTH: "Tassili Airlines",
    DTN: "Data International",
    DTR: "DAT Danish Air Transport",
    DTV: "Centre Airlines",
    DTY: "Destiny Air Services",
    DUB: "Dubai Airwing",
    DUK: "Ducair",
    DUN: "Dun'Air",
    DUO: "Duo Airways",
    DVA: "Discovery Airways",
    DVB: "Don Avia",
    DVI: "Aero Davinci International",
    DWA: "Dense Airways",
    DWD: "Deutsche Luftverkehrsgesellschaft (DLT)",
    DWN: "Dawn Air",
    DWR: "Delaware Skyways",
    DWT: "Darwin Airline",
    DWW: "Jet Courier Service",
    DXH: "East Star Airlines",
    DXP: "Dallas Express Airlines",
    DXT: "Dexter (DXT)",
    DYA: "Dynamic Airways",
    DYE: "Dynamic Air",
    DYN: "Aero Dynamics",
    EAA: "Eastok Avia",
    EAB: "Swiss Eagle",
    EAC: "Executive Air Charter",
    EAD: "EAA Escola De Aviacao Aerocondor",
    EAE: "Aeroservicios Ecuatorianos",
    EAF: "European Aviation Air Charter",
    EAG: "European Aeronautical Group UK",
    EAH: "Baltimore Airways",
    EAI: "Elite Air",
    EAK: "Euro-Asia Air",
    EAL: "European Air Express",
    EAM: "Embassy Airlines",
    EAN: "Skypower Express Airways",
    EAP: "Aero-Pyrenees",
    EAQ: "Eastern Australia Airlines",
    EAR: "Transporte Ejecutivo Aereo",
    EAS: "Executive Aerospace",
    EAT: "Air Transport",
    EAV: "Eastern Atlantic Virtual Airlines",
    EAX: "Eastern Air Executive",
    EAY: "Aero Airlines",
    EAZ: "Eastern Air",
    EBA: "Bond Aviation",
    EBC: "Aero Ejecutivo De Baja California",
    EBE: "Minebea Technologies",
    EBF: "MSR Flug-Charter",
    EBG: "Eurosense",
    EBJ: "European Business Jets",
    ECA: "Eurocypria Airlines",
    ECB: "European Coastal Airlines",
    ECC: "Crossair Europe",
    ECD: "Ecotour",
    ECE: "Air City",
    ECF: "Eurocopter",
    ECG: "Aero Ejecutivos RCG",
    ECI: "Eastern Carolina Aviation",
    ECJ: "East Coast Jets",
    ECL: "Aeronautica Castellana",
    ECM: "Aerolineas Comerciales",
    ECN: "Euro Continental AIE",
    ECQ: "Eco Air",
    ECS: "Executive Aircraft Charter and Charter Services",
    ECT: "East Coast Airways",
    ECU: "Ecuavia",
    ECV: "Ecuatoguineana De Aviacion (EGA)",
    ECX: "Ecomex Air Cargo",
    ECY: "Euroceltic Airways",
    EDA: "Aerolineas Nacionales Del Ecuador",
    EDC: "Edinburgh Air Charter",
    EDD: "Lineas Aereas Ejectuivas De Durango",
    EDI: "Eastern Atlantic Charters",
    EDJ: "Edwards Jet Center of Montana",
    EDL: "Polizeihubschrauberstaffel Bayern",
    EDO: "Elidolomiti",
    EDR: "Lineas Aereas Eldorado",
    EDW: "Edelweiss Air",
    EEA: "Empresa Ecuatoriana De Aviacion",
    EET: "Air Este",
    EEU: "Eurofly Service",
    EEZ: "Eurofly",
    EFA: "Far Eastern Air Transport",
    EFC: "Air Mana",
    EFD: "EFD Eisele Flugdienst",
    EFG: "Elifriulia",
    EFS: "EFAOS- Agencia De Viagens e Turismo",
    EFT: "Embassy Freight Company",
    EFY: "EasyFly",
    EGF: "American Eagle Airlines",
    EGH: "BBN-Airways",
    EGJ: "Eagle Jet Charter",
    EGL: "Capital Trading Aviation",
    EGN: "Eagle Aviation France",
    EGO: "Gauteng Air Cargo",
    EGR: "Eagle Air",
    EGS: "Eagles Airlines",
    EGU: "Eagle Aviation",
    EGV: "Enrique Gleisner Vivanco",
    EGX: "Eagle Air Company",
    EGY: "Egyptian Air Force",
    EHA: "East Hampton Aire",
    EHD: "E H Darby Aviation",
    EHM: "Euro Harmony",
    EHN: "East Horizon",
    EHR: "Valfell-Verkflug",
    EIA: "Evergreen International Airlines",
    EIC: "Express International Cargo",
    EIJ: "Efata Papua Airlines",
    EIN: "Aer Lingus",
    EIR: "Eirjet",
    EIS: "EIS Aircraft",
    EIX: "Ei Air Exports",
    EJA: "NetJets",
    EJC: "Grupo De Aviacion Ejecutiva",
    EJD: "Elite Jets",
    EJM: "Executive Jet Management",
    EJO: "Execujet Middle East",
    EJP: "Aeroservicios Ejecutivos Corporativos",
    EJS: "Eurojet Servis",
    EJT: "Eclipse Aviation",
    EJV: "Compania Ejecutiva",
    EJX: "Egyptian Aviation",
    EKA: "Equaflight Service",
    EKC: "East Kansas City Aviation",
    ELA: "Eastland Air",
    ELB: "Elieuro",
    ELC: "Small Planet Airlines",
    ELG: "Alpi Eagles",
    ELH: "Elilario Italia",
    ELJ: "Delta Air Elite",
    ELK: "ELK Airways",
    ELL: "Estonian Air",
    ELM: "Crelam",
    ELO: "Eurolot",
    ELR: "Elrom Aviation and Investments",
    ELS: "El Sal Air",
    ELT: "Elliott Aviation",
    ELV: "Transportes Aereos Nacionales De Selva Tans",
    ELW: "Yellow Wings Air Services",
    ELX: "Elan Express",
    ELY: "El Al Israel Airlines",
    EMA: "Egyptian Aviation Company",
    EMB: "Empresa Brasileira De Aeronautica",
    EMD: "Eaglemed (Ballard Aviation)",
    EME: "Eastern Metro Express",
    EMI: "Premium Air Shuttle",
    EMN: "Examiner Training Agency",
    EMP: "Empire Air Service",
    EMR: "Zenmour Airlines",
    EMS: "Aero Servicios Empresariales",
    EMT: "Emetebe",
    EMU: "East Asia Airlines",
    EMX: "Euromanx Airways",
    ENA: "Direccion General de Aviacion Civil y Telecomunicasciones",
    ENC: "Endecots",
    END: "Arrendoora y Transportadora Aerea",
    ENI: "Enimex",
    ENJ: "Enerjet",
    ENK: "Enkor JSC",
    ENR: "Scenic Air",
    ENS: "Entergy Services",
    ENT: "DAT Enterprise Limited",
    ENV: "Victoria Aviation",
    ENW: "Aeronaves Del Noroeste",
    ENY: "Envoy Air",
    ENZ: "Jota Aviation",
    EOA: "Elilombarda",
    EOL: "Airailes",
    EOM: "Aero Ermes",
    EOS: "Eliossola",
    EPA: "Shenzhen Donghai Airlines",
    EPB: "Eastern Pacific Aviation",
    EPC: "Espace Aviation Services",
    EPE: "Aero Empresarial",
    EPL: "Aero Transportes Empresariales",
    EPS: "Epps Air Service",
    EQC: "Ecuatorial Cargo",
    EQL: "Air S",
    EQT: "Equatorial Airlines",
    EQZ: "Equatair Air Services (Zambia)",
    ERC: "Esso Resources Canada",
    ERE: "Erie Airways",
    ERF: "Erfoto",
    ERG: "Avianergo",
    ERH: "Era Helicopters",
    ERI: "Aero Servicios Regiomontanos",
    ERJ: "Eurojet Italia",
    ERK: "Aerosec",
    ERM: "Aeromaan",
    ERO: "Sun D'Or",
    ERR: "Era Alaska",
    ERT: "Eritrean Airlines",
    ERV: "Yerevan-Avia",
    ERX: "Earth Airlines Services",
    ESB: "Aerosaba",
    ESC: "El Sol De America",
    ESE: "Ensenada Vuelos Especiales",
    ESF: "Estafeta Carga Aerea",
    ESI: "ESI Eliservizi Italiani",
    ESJ: "Eastern Sky Jets",
    ESK: "SkyEurope",
    ESL: "Russian Sky Airlines",
    ESN: "Euro Sun",
    ESO: "Avitat",
    ESR: "Eastar Jet",
    ESS: "Eos Airlines",
    EST: "Carga Express Internacional",
    ESU: "Aerolineas Ejecutivas Del Sureste",
    ESX: "Euroskylink",
    ESZ: "Aeronautica Le Esperanza",
    ETA: "Estrellas Del Aire",
    ETC: "ATTICO",
    ETD: "Etihad Airways",
    ETE: "Aero Siete",
    ETH: "Ethiopian Airlines",
    ETL: "Patterson Aviation Company",
    ETM: "Etram Air Wing",
    ETN: "Chim-Nir Aviation",
    ETP: "Empire Test Pilots' School",
    ETS: "Flygtransporter I Nykoping",
    ETV: "European Executive",
    EUC: "Eurocontrol",
    EUD: "Air Italy Egypt",
    EUG: "Euroguineana de Aviacion",
    EUJ: "EUjet",
    EUK: "Air Atlanta Europe",
    EUP: "Euroair",
    EUT: "European 2000 Airlines",
    EUU: "Euroamerican Air",
    EUV: "EuropeSky",
    EUY: "EU Airways",
    EVA: "EVA Air",
    EVC: "Comfort Express Virtual Charters Albany",
    EVE: "Air Evex",
    EVM: "Natural Environment Research Council",
    EVN: "Euraviation",
    EVR: "Aeronautical Academy of Europe",
    EWA: "East-West Airlines",
    EWG: "Eurowings",
    EWS: "Enterprise World Airways",
    EWW: "Emery Worldwide Airlines",
    EXA: "Execair Aviation",
    EXB: "Brazilian Army Aviation",
    EXC: "European Executive Express",
    EXE: "Executive Flight",
    EXF: "Eximflight",
    EXG: "Air Exchange",
    EXH: "G5 Executive",
    EXJ: "Executive Jet Charter",
    EXK: "Executive Airlines",
    EXL: "Sunshine Express Airlines",
    EXM: "United Kingdom Civil Aviation Authority",
    EXN: "Exin",
    EXP: "Business Express Delivery",
    EXR: "Expertos En Carga",
    EXS: "Jet2.com",
    EXT: "Night Express",
    EXU: "Executive Airlines",
    EXV: "Expo Aviation",
    EXW: "Executive Airlines Services",
    EXX: "International Air Corporation",
    EXY: "South African Express",
    EXZ: "East African Safari Air Express",
    EYE: "F.S. Air Service",
    EZA: "Eznis Airways",
    EZB: "Flugschule Eichenberger",
    EZE: "Eastern Airways",
    EZY: "easyJet",
    FAB: "First Air",
    FAC: "Atlantic Helicopters",
    FAD: "Rog-Air",
    FAE: "Freshaer",
    FAF: "Force Aerienne Francaise",
    FAG: "Argentine Air Force",
    FAH: "Farnair Hungary",
    FAJ: "Air Fiji",
    FAK: "Financial Airxpress",
    FAL: "Friendship Air Alaska",
    FAM: "Fumigacion Aerea Andaluza",
    FAN: "AF-Air International",
    FAO: "Falcon Air Express",
    FAP: "Parsons Airways Northern",
    FAR: "Falcon Air",
    FAS: "Freedom Airways",
    FAT: "Farnair Switzerland",
    FAU: "Falcon Airline",
    FAV: "Fair Aviation",
    FAW: "Falwell Aviation",
    FAX: "Midwest Air Freighters",
    FAY: "Fayban Air Services",
    FAZ: "Flint Aviation Services",
    FBA: "Fab Air",
    FBF: "Fine Airlines",
    FBL: "Fly Brasil",
    FBO: "TAG Farnborough Airport",
    FBW: "Aviation Data Systems",
    FCA: "First Choice Airways",
    FCB: "COBALT",
    FCC: "First Cambodia Airlines",
    FCE: "Flycolumbia",
    FCI: "Air Carriers",
    FCK: "Flight Calibration Service",
    FCL: "Florida Coastal Airlines",
    FCM: "Flybe Finland Oy",
    FCN: "Falcon Air",
    FCO: "Aerofrisco",
    FCP: "Flight Corporation",
    FCR: "Flying Carpet Company",
    FCS: "Facts Air",
    FCT: "Fly CI Limited",
    FCU: "Alfa 4",
    FCV: "Flight Centre Victoria",
    FDA: "African Airlines",
    FDB: "Fly Dubai",
    FDD: "Feeder Airlines",
    FDF: "IVV Femida",
    FDL: "Farmingdale State University",
    FDN: "Flying Dolphin Airlines",
    FDO: "France Douanes",
    FDP: "Flight Dispatch Services",
    FDR: "Federal Air",
    FDS: "African Medical and Research Foundation",
    FDX: "Federal Express",
    FEC: "Denver Express",
    FED: "Lineas Aereas Federales",
    FEE: "Fly Europa Limited",
    FEG: "FlyEgypt",
    FEI: "Eagle Air Iceland",
    FEO: "Aeroferinco",
    FER: "Feria Aviacion",
    FES: "Aero Taxis Y Servicios Alfe",
    FEX: "Flightexec",
    FFA: "Avialesookhrana",
    FFB: "Africair Service",
    FFC: "Fairoaks Flight Centre",
    FFD: "Stuttgarter Flugdienst",
    FFF: "Freedom Air Services",
    FFG: "Flugdienst Fehlhaber",
    FFH: "Shalom Air Services",
    FFI: "Infinit Air",
    FFL: "Intavia Limited",
    FFM: "Firefly",
    FFP: "Fischer Air Polska",
    FFR: "Fischer Air",
    FFS: "Florida Department of Agriculture",
    FFT: "Frontier Airlines",
    FFU: "GEC Marconi Avionics",
    FFV: "Fly540",
    FFY: "Fun Flying Thai Air Service",
    FGA: "Georgian Aviation Federation",
    FGC: "Departmento De Agricultura De La Generalitat De Cataluna",
    FGL: "Futura Gael",
    FGN: "Gendarmie Nationale",
    FGP: "Flying-Research Aerogeophysical Center",
    FGS: "Elitellina",
    FGT: "Aero Freight",
    FGY: "Froggy Corporate Aviation",
    FHE: "Hello",
    FHI: "FlyHigh Airlines Ireland (FH)",
    FHL: "Fast Helicopters",
    FHS: "Forth and Clyde Helicopter Services",
    FHY: "Free Bird Airlines",
    FIA: "Four Island Air",
    FIC: "Aerosafin",
    FIF: "Air Finland",
    FII: "Aerodata Flight Inspection",
    FIL: "Fly Line",
    FIN: "Finnair",
    FIR: "First Line Air",
    FIV: "Citationshares",
    FIX: "Airfix Aviation",
    FJC: "Falcon Jet Centre",
    FJE: "Silverjet",
    FJI: "Air Pacific",
    FJM: "Fly Jamaica Airways",
    FJS: "Florida Jet Service",
    FKA: "Flying kangaroo Airline",
    FKI: "FLM Aviation Mohrdieck",
    FKL: "Kelner Airways",
    FKS: "Focus Air",
    FLA: "Midway Express",
    FLB: "German Air Force - FLB",
    FLC: "FINFO Flight Inspection Aircraft",
    FLD: "Air Falcon",
    FLE: "Flair Airlines",
    FLF: "Friendship Airlines",
    FLG: "Pinnacle Airlines",
    FLH: "Sky Bus",
    FLI: "Atlantic Airways",
    FLK: "Flylink Express",
    FLL: "Federal Airlines",
    FLM: "Fly Air",
    FLN: "Flamingo Air-Line",
    FLO: "Flycom",
    FLP: "Aeroclub Flaps",
    FLR: "Fleetair",
    FLS: "Fairlines",
    FLT: "Flightline",
    FLU: "Flugschule Basel",
    FLW: "FLowair Aviation",
    FLX: "Flight Express",
    FLY: "Fly Me Sweden",
    FLZ: "Air Florida",
    FMC: "Claessens International Limited",
    FMG: "FMG Verkehrsfliegerschule Flughafen Paderborn-Lippstadt",
    FMR: "Flamingo Air",
    FMS: "Hadison Aviation",
    FMT: "Air Fret De Mauritanie",
    FMY: "Aviation Legere De L'Armee De Terre",
    FNA: "Norlandair",
    FNC: "Finalair Congo",
    FNF: "Finnish Air Force",
    FNG: "Frontier Guard",
    FNK: "Feniks Airline",
    FNL: "Oulun Tilauslento",
    FNM: "Avio Nord",
    FNO: "Aeroflota Del Noroeste",
    FNT: "L-3 Communications Flight Internation Aviation",
    FNV: "Transaviaservice",
    FNX: "Aero Fenix",
    FNY: "France Marine Nationale",
    FOB: "Ford Motor Company",
    FOC: "Office Federal De'Aviation Civile",
    FOF: "Fred. Olsen",
    FOI: "Flight Ops International",
    FOM: "Freedom Air",
    FOP: "Fokker",
    FOR: "Formula One Management",
    FOS: "Formosa Airlines",
    FOX: "FOX Linhas Aereas",
    FPA: "Flygprestanda",
    FPG: "TAG Aviation",
    FPO: "Europe Airpost",
    FPR: "Peruvian Air Force",
    FPS: "Flightpass Limited",
    FPT: "FlyPortugal",
    FPY: "African Company Airlines",
    FQR: "CheapFlyingInternational",
    FRA: "FR Aviation",
    FRC: "Icare Franche Compte",
    FRD: "Ford Motor Company",
    FRE: "Freedom Air",
    FRF: "Fly France",
    FRG: "Freight Runners Express",
    FRJ: "Afrijet Airlines",
    FRK: "Afrika Aviation Handlers",
    FRL: "Freedom Airlines",
    FRM: "Federal Armored Service",
    FRN: "Farnair Netherlands",
    FRQ: "Afrique Chart'air",
    FRR: "Fresh Air",
    FRT: "Aerofreight Airlines",
    FRW: "Farwest Airlines",
    FSA: "Foster Aviation",
    FSB: "FSB Flugservice & Development",
    FSC: "Four Star Aviation / Four Star Cargo",
    FSD: "EFS-Flugservice",
    FSH: "Flash Airlines",
    FSL: "Flight Safety Limited",
    FSR: "Flightstar Corporation",
    FST: "Aereos Limited",
    FSU: "Flight Support Sweden",
    FSW: "Faso Airways",
    FSX: "Flagship Express Services",
    FTA: "Frontier Flying Service",
    FTC: "Air Affaires Tchad",
    FTE: "Fotografia F3",
    FTI: "FTI Fluggesellschaft",
    FTM: "Flyteam Aviation",
    FTP: "Keystone Aerial Surveys",
    FTR: "Finist'air",
    FTS: "First Sabre",
    FTY: "ABC Bedarsflug",
    FUA: "Futura International Airways",
    FUJ: "Fujairah Aviation Centre",
    FUN: "Funtshi Aviation Service",
    FVA: "Cardinal/Air Virginia",
    FVG: "VG Airlines (IV)",
    FVM: "Flugfelag Vestmannaeyja",
    FVS: "Falcon Aviation Services",
    FWA: "Interstate Airline",
    FWC: "Freeway Air",
    FWD: "Fair Wind Air Charter",
    FWI: "Air Caraïbes",
    FWL: "Florida West International Airways",
    FWQ: "Flight West Airlines",
    FWY: "Fairways Corporation",
    FWZ: "Flugwerkzeuge Aviation Software",
    FXA: "Express Air",
    FXC: "Fortunair Canada",
    FXF: "VIP Air Charter",
    FXI: "Air Iceland",
    FXL: "Fly Excellent",
    FXR: "Foxair",
    FXT: "Flexflight",
    FXX: "Felix Airways",
    FXY: "Flexair",
    FYA: "Servicios Aereos Integrales / Flyant",
    FYE: "Easy Link Aviation Services",
    FYG: "Flying Service",
    FYH: "Flyhy Cargo Airlines",
    FYJ: "FLYJET",
    FYN: "Comfort Air",
    FYS: "American Flyers",
    FZA: "Fuzhou Airlines",
    FZW: "Fly Africa Zimbabwe",
    GAA: "Business Express",
    GAB: "Gendall Air",
    GAD: "South Coast Aviation",
    GAE: "Grand Aire Express",
    GAF: "German Air Force",
    GAG: "Global Air Operations",
    GAH: "Ga-Ma Helicoptere",
    GAI: "Moskovia Airlines",
    GAK: "Global Aviation and Services Group",
    GAL: "Galaxy Air",
    GAM: "German Army",
    GAN: "Gander Aviation",
    GAO: "Golden Air",
    GAP: "Air Philippines",
    GAQ: "Golfe Air Quebec",
    GAR: "Commodore Aviation",
    GAS: "Galena Air Service",
    GAT: "Gulf Air Inc",
    GAU: "Aerogaucho",
    GAV: "Granada Aviacion",
    GAX: "Full Express",
    GBA: "Gulf Air Bahrain",
    GBB: "Global Aviation Operations",
    GBE: "Gabon Express",
    GBJ: "Aero Business Charter",
    GBK: "Gabon Airlines",
    GBL: "GB Airways",
    GBN: "Atlantic Airlines",
    GBO: "Ogooue Air Cargo",
    GBR: "Rader Aviation",
    GBS: "Global Air Services Nigeria",
    GBT: "Gold Belt Air Transport",
    GBX: "GB Airlink",
    GCA: "Grand Cru Airlines",
    GCB: "Lignes Nationales Aeriennes - Linacongo",
    GCC: "GECAS",
    GCF: "Aeronor",
    GCK: "Aerogem Cargo",
    GCN: "Gulf Central Airlines",
    GCO: "Gemini Air Cargo",
    GCR: "Tianjin Airlines",
    GCS: "GCS Air Service",
    GDA: "GoldAir",
    GDB: "Gendarmerie Belge",
    GDC: "Grand China Air",
    GDD: "Golden Airlines",
    GDE: "Servicios Aereos Gadel",
    GDG: "S.P. Aviation",
    GDH: "Guneydogu Havacilik Isletmesi",
    GDK: "Goldeck-Flug",
    GDN: "Gerry's Dnata",
    GDQ: "Lincoln Air National Guard",
    GDR: "Gadair European Airlines",
    GEA: "Guinea Ecuatorial Airlines",
    GEC: "Lufthansa Cargo",
    GED: "Europe Air Lines",
    GEE: "Geesair",
    GEG: "Georgian Airlines",
    GEN: "GENSA",
    GER: "German International Air Lines",
    GES: "Gestair",
    GET: "GETRA",
    GFA: "Gulf Air",
    GFC: "Gail Force Express",
    GFD: "Gesekkschaft Fur Flugzieldarstellung",
    GFF: "Griffin Aviation",
    GFG: "Georgian National Airlines",
    GFI: "Caribbean Star Airlines",
    GFN: "The 955 Preservation Group",
    GFO: "Aerovias del Golfo",
    GFS: "Gulfstream Airlines",
    GFT: "Gulfstream International Airlines",
    GFW: "GFW Aviation",
    GFY: "Greenfly",
    GGA: "First Flying Squadron",
    GGC: "Cargo 360",
    GGE: "Gobierno De Guinea Ecuatorial",
    GGF: "Georgian Cargo Airlines Africa",
    GGL: "Aeronautica",
    GGN: "Air Georgian",
    GGS: "GATSA",
    GGV: "Houston Jet Services",
    GGZ: "Global Georgian Airways",
    GHA: "Ghana Airways",
    GHB: "Ghana International Airlines",
    GHI: "GH Stansted Limited",
    GHL: "Aviance",
    GHN: "Air Ghana",
    GHP: "Colvin Aviation",
    GHS: "Gatari Hutama Air Services",
    GHT: "Ghadames Air Transport",
    GHV: "Ground Handling Service de Mexico",
    GHY: "German Sky Airlines",
    GIA: "Garuda Indonesia",
    GIB: "GR-Avia",
    GIC: "Compagnie de Bauxites de Guinee",
    GIE: "Elysian Airlines",
    GIF: "Guinee Airlines",
    GIG: "Gacela Air Cargo",
    GIH: "Union des Transports Africains de Guinee",
    GIJ: "Guinea Airways",
    GIK: "Seba Airlines",
    GIL: "African International Transport",
    GIP: "Air Guinee Express",
    GIQ: "Guinee Paramount Airlines",
    GIY: "Probiz Guinee",
    GIZ: "Africa Airlines",
    GJA: "Globe Jet",
    GJB: "Trans-Air-Link",
    GJS: "GoJet Airlines",
    GJT: "Gestion Aerea Ajecutiva",
    GKA: "US Army Parachute Team",
    GLA: "Great Lakes Airlines",
    GLB: "Air Castle Corporation / Global Airways",
    GLC: "Global Aircargo",
    GLD: "Golden Star Air Cargo",
    GLE: "Goliaf Air",
    GLF: "Gulfstream Aerospace",
    GLG: "Aerolineas Galapagos (Aerogal)",
    GLJ: "Global Jet Austria",
    GLL: "Air Gemini",
    GLO: "Gol Transportes Aéreos",
    GLP: "Globus",
    GLQ: "El Quilada International Aviation",
    GLR: "Central Mountain Air",
    GLS: "Galaircervis",
    GLT: "Aero Charter",
    GLU: "Great Lakes Airways (Uganda)",
    GLW: "Global Wings",
    GMA: "Gama Aviation",
    GMC: "General Motors",
    GME: "Aguilas Mayas Internacional",
    GMG: "GM Helicopters",
    GMH: "Hughes Aircraft Company",
    GMI: "Germania",
    GMJ: "Gamisa Aviacion",
    GML: "G & L Aviation",
    GMM: "Aerotaxis Guamuchil",
    GMR: "Golden Myanmar Airlines",
    GMS: "Aeroservicios Gama",
    GMT: "Grupoaereo Monterrey",
    GNA: "Servicios Aereos Gana",
    GNC: "Guinea Cargo",
    GND: "Grand Airways",
    GNF: "Gandalf Airlines",
    GNJ: "Gain Jet Aviation",
    GNL: "135 Airways",
    GNN: "Georgian International Airlines",
    GNR: "Gambia International Airlines",
    GNS: "Eastern Executive Air Charter",
    GNT: "Amber Air",
    GNX: "Genex",
    GNY: "German Navy",
    GNZ: "General Aviation",
    GOA: "Alberta Government",
    GOB: "Dash Aviation",
    GOD: "GoDutch",
    GOE: "Go Fly (United Kingdom)",
    GOF: "Gof-Air",
    GOI: "Gofir",
    GOJ: "EuroJet Aviation",
    GOL: "Cardolaar",
    GOM: "Gomel Airlines",
    GOR: "Gorlitsa Airlines",
    GOS: "Goldfields Air Services",
    GOT: "Walter I Linkoping",
    GOW: "Go Air",
    GPA: "Golden Pacific Airlines",
    GPC: "Gulf Pearl Air Lines",
    GPD: "Tradewind Aviation",
    GPE: "GP Express Airlines",
    GPL: "DLR",
    GPM: "Grup Air-Med",
    GPR: "GPM Aeroservicio",
    GRA: "Great American Airways",
    GRD: "National Grid",
    GRE: "Air Scotland",
    GRG: "Air Georgia",
    GRI: "Air Cargo Center",
    GRL: "Air Greenland",
    GRN: "Rio Grande Air",
    GRO: "Allegro",
    GRP: "Great Plains Airlines",
    GRR: "Agroar - Trabalhos Aereos",
    GRS: "Golden Rule Airlines",
    GRT: "Gabon-Air-Transport",
    GRV: "Vernicos Aviation",
    GRX: "Aircompany Grodno",
    GRY: "New York State Police",
    GRZ: "Government of Zambia Communications Flight",
    GSA: "Garden State Airlines",
    GSJ: "Grossmann Jet Service",
    GSK: "Global Sky Aircharter",
    GSL: "Geographic Air Surveys",
    GSM: "Flyglobespan",
    GSP: "Airlift Alaska",
    GSS: "Global Supply Systems",
    GSV: "Agrocentr-Avia",
    GSW: "Sky Wings Airlines",
    GSY: "Guard Systems",
    GTA: "City Airways",
    GTC: "Altin Havayolu Tasimaciligi Turizm Ve Ticaret",
    GTH: "General Aviation Flying Services",
    GTI: "Atlas Air",
    GTP: "Aerotaxi Grupo Tampico",
    GTR: "Gestar",
    GTV: "Empresa de Aviacion Aerogaviota",
    GTX: "GTA Air",
    GTY: "National Aviation Company",
    GUA: "Aerotaxis de Aguascalientes",
    GUF: "Gulf African Airlines - Gambia",
    GUG: "Aviateca",
    GUI: "Aguilar Connect",
    GUJ: "Gujarat Airways",
    GUL: "Gull Air",
    GUM: "Gum Air",
    GUN: "Grant Aviation",
    GUS: "Guja",
    GUY: "Air Guyane",
    GVG: "Flygaktiebolaget Gota Vingar",
    GVI: "Air Victoria Georgia",
    GVN: "Gavina",
    GWA: "Great Western Air",
    GWI: "Germanwings",
    GWL: "Great Wall Airlines",
    GWN: "Gwyn Aviation",
    GWS: "General Airways",
    GWY: "USA3000 Airlines",
    GXA: "Grixona",
    GXG: "GermanXL",
    GXL: "Star XL German Airlines",
    GXY: "Galaxy Airlines",
    GYP: "Eagle Aviation",
    GZA: "Excellent Air",
    GZD: "Grizodubova Air Company",
    GZP: "Gazpromavia",
    HAA: "Helicopteros Agroforestal",
    HAC: "Henebury Aviation",
    HAD: "Air D'Ayiti",
    HAF: "Hellenic Air Force",
    HAG: "Hageland Aviation Services",
    HAH: "Air Comores International",
    HAI: "Century Aviation International",
    HAJ: "Hajvairy Airlines",
    HAK: "Pegasus Helicopters",
    HAL: "Hawaiian Airlines",
    HAM: "Haiti Ambassador Airlines",
    HAN: "Hansung Airlines",
    HAP: "Helicopteros Aero Personal",
    HAR: "Harbor Airlines",
    HAS: "Honduras Airlines",
    HAT: "Air Taxi",
    HAU: "Skyhaul",
    HAV: "Havilah Air Services",
    HAW: "Bangkok Aviation Center",
    HAX: "Benair",
    HAY: "Hamburg Airways",
    HBA: "Trail Lake Flying Service",
    HBC: "Haitian Aviation Line",
    HBH: "Hebei Airlines",
    HBI: "CHC Denmark",
    HBL: "Faroecopter",
    HBR: "Hebradran Air Services",
    HBT: "Polizeihubschrauberstaffel Thuringen",
    HBU: "Universal Avia",
    HCA: "Lake Havasu Air Service",
    HCB: "Helenair (Barbados)",
    HCC: "Holidays Czech Airlines",
    HCL: "Helenair Corporation",
    HCN: "Halcyon Air/Bissau Airways",
    HCP: "Helicopter",
    HCW: "Star1 Airlines",
    HCY: "Helios Airways",
    HDA: "Dragonair",
    HDC: "Helcopteros De Cataluna",
    HDI: "Hoteles Dinamicos",
    HDR: "Helikopterdrift",
    HEA: "Heliavia-Transporte Aereo",
    HEB: "Heli Bernina",
    HEC: "Heliservicio Campeche",
    HED: "Herritage Aviation Developments",
    HEI: "Aerohein",
    HEJ: "Hellas Jet",
    HEL: "Helicol",
    HEM: "CHC Helicopters",
    HEN: "Helicopteros Y Vehiculos Nacionales Aereos",
    HER: "Hex'Air",
    HET: "TAF Helicopters",
    HEX: "Honiara Cargo Express",
    HEY: "Hellenic Airways",
    HFR: "Heli France",
    HFY: "Hi Fly",
    HGA: "Hogan Air",
    HGD: "Hangard Aviation",
    HGH: "Atlantic Air Lift",
    HGK: "Fika Salaama Airlines",
    HGR: "Hangar 8",
    HHA: "Atlantic Airlines de Honduras",
    HHE: "Heli-Holland",
    HHH: "Helicsa",
    HHI: "Hamburg International",
    HHN: "Hahn Air",
    HHO: "Houston Helicopters",
    HHP: "Helenia Helicopter Service",
    HHS: "Hi-Jet Helicopter Services",
    HIA: "Canadian Eagle Aviation",
    HIB: "Helibravo Aviacao",
    HID: "Aviacion Ejecutiva De Hildago",
    HIF: "Heli-Iberica Fotogrametria",
    HIG: "Heli-Inter Guyane",
    HIN: "Holding International Group",
    HIP: "Starship",
    HIS: "Hispaniola Airways",
    HIT: "Helitalia",
    HJA: "Air Haiti",
    HJE: "Servicios Ejecutivos Gosa",
    HJL: "Hamlin Jet",
    HJT: "Al Rais Cargo",
    HKA: "Superior Aviation",
    HKE: "Hong Kong Express Airways",
    HKG: "Government Flying Service",
    HKH: "Air-Invest",
    HKI: "Hawkaire",
    HKL: "Hak Air",
    HKN: "Jim Hankins Air Service",
    HKR: "Hawk Air",
    HKS: "CHC Helikopter Service",
    HLA: "HC Airlines",
    HLB: "High-Line Airways",
    HLC: "Helicap",
    HLD: "Grampian Flight Centre",
    HLE: "UK HEMS",
    HLF: "Hapagfly",
    HLG: "Helog",
    HLH: "Hala Air",
    HLI: "Heli Securite",
    HLK: "Heli-Link",
    HLM: "Heli Medwest De Mexico",
    HLO: "Samaritan Air Service",
    HLP: "Helipistas",
    HLR: "Heli Air Services",
    HLS: "Haiti Air Freight",
    HLT: "Helitafe",
    HLU: "Heli Union Heli Prestations",
    HLW: "Heliworks",
    HLX: "TUIfly",
    HMA: "Air Tahoma",
    HMC: "Heliamerica De Mexico",
    HMD: "Charlie Hammonds Flying Service",
    HMF: "Norrlandsflyg",
    HMM: "Hamra Air",
    HMP: "Papair Terminal",
    HMR: "North American Charters",
    HMS: "Hemus Air",
    HMT: "Air Nova",
    HMV: "Homac Aviation",
    HMX: "Hawk De Mexico",
    HMY: "Harmony Airways",
    HNA: "Greek Navy",
    HNR: "Haiti National Airlines (HANA)",
    HNT: "Helicopteros Internacionales",
    HNX: "Hankook Airline",
    HOA: "Hola Airlines",
    HOL: "Holiday Airlines",
    HOM: "Aero Homex",
    HOR: "Horizon Air-Taxi",
    HOZ: "Horizontes Aereos",
    HPA: "Pearl Airways",
    HPJ: "Hop-A-Jet",
    HPL: "Heliportugal",
    HPO: "Almiron Aviation",
    HPR: "Rick Lucas Helicopters",
    HPS: "Horizon Plus",
    HPY: "Happy Air",
    HQO: "Avinor",
    HRA: "Heli-Iberica",
    HRB: "Haiti International Airline",
    HRH: "Royal Tongan Airlines",
    HRI: "Skyraidybos Mokymo Centras",
    HRM: "Hermes Airlines",
    HRT: "Chartright Air",
    HRZ: "Croatian Air Force",
    HSA: "East African Safari Air",
    HSE: "Compania Helicopteros Del Sureste",
    HSI: "Heliswiss",
    HSK: "Sky Europe Airlines",
    HSM: "Horizon Air for Transport and Training",
    HSO: "Campania Helicopteros De Transporte",
    HSP: "International Jet Charter",
    HSR: "Citylink Airlines",
    HSS: "Compania Transportes Aereos Del Sur",
    HST: "Hellenic Star Airways",
    HSU: "Helisul",
    HSV: "Svenska Direktflyg",
    HSY: "Sky Helicopteros",
    HTA: "Helitrans",
    HTC: "Haiti Trans Air",
    HTE: "Midwest Helicopters De Mexico",
    HTG: "Grossmann Air Service",
    HTH: "Helitt Líneas Aéreas",
    HTI: "Haiti International Air",
    HTL: "Jet Link Aviation",
    HTN: "Haiti North Airline",
    HTO: "Tango Bravo",
    HTP: "Heli Trip",
    HTR: "Holstenair Lubeck",
    HTS: "Helitrans Air Service",
    HTT: "Air Tchad",
    HUB: "Hub Airlines",
    HUC: "Aerloineas de Techuacan",
    HUD: "Horizons Unlimited",
    HUF: "Hungarian Air Force",
    HUR: "Miami Air Charter",
    HUS: "Huessler Air Service",
    HUT: "Aerotransportes Huitzilin",
    HUV: "Hunair Hungarian Airlines",
    HUY: "Aero Transportes Del Humaya",
    HVA: "Newair",
    HVK: "Turkish Air Force",
    HVL: "Heavylift International",
    HVN: "Vietnam Airlines",
    HVY: "Heavylift Cargo Airlines",
    HWD: "HPM Investments",
    HWY: "Highland Airways",
    HXA: "China Express Airlines",
    HYA: "Hyack Air",
    HYC: "Hydro Air Flight Operations",
    HYD: "Hydro-Québec",
    HYH: "Heli Hungary",
    HYM: "Himalayan Airlines",
    HYR: "Airlink Airways",
    HZA: "Horizon Airlines",
    HZL: "Hazelton Airlines",
    HZT: "Air Horizon",
    HZX: "Citic General Aviation",
    IAA: "Indonesian Airlines",
    IAC: "Indian Airlines",
    IAD: "Fly Wex",
    IAE: "AC Insat-Aero",
    IAF: "Israeli Air Force",
    IAG: "EPAG Groupe Air France",
    IAI: "Israel Aircraft Industries",
    IAJ: "Islandair Jersey",
    IAK: "International Air Cargo Corporation",
    IAM: "Aeronautica Militare",
    IAR: "Iliamna Air Taxi",
    IAS: "International Air Service",
    IAW: "Iraqi Airways",
    IAX: "International Air Services",
    IAY: "Deadalos Flugtbetriebs",
    IBB: "Binter Canarias",
    IBC: "Ibicenca Air",
    IBE: "Iberia Airlines",
    IBK: "Norwegian Air International (D8)",
    IBL: "IBL Aviation",
    IBR: "Ibertour Servicios Aereos",
    IBS: "Iberia Express",
    IBT: "Ibertrans Aerea",
    IBU: "Indigo",
    IBX: "Ibex Airlines",
    IBY: "International Business Aircraft",
    IBZ: "International Business Air",
    ICA: "Icaro",
    ICB: "Icebird Airline",
    ICC: "Instituto Cartografico de Cataluna",
    ICD: "Icaro",
    ICE: "Icelandair",
    ICG: "Icelandic Coast Guard",
    ICJ: "Icejet",
    ICL: "CAL Cargo Air Lines",
    ICM: "Air Inter Cameroun",
    ICN: "Inter-Canadian",
    ICP: "Intercopters",
    ICR: "Eagle Aero",
    ICS: "International Charter Services",
    ICT: "Intercontinental de Aviaci",
    ICX: "International Charter Xpress",
    IDA: "Indonesia Air Transport",
    IDE: "Independence Air",
    IDG: "IDG Technology Air",
    IDL: "Ildefonso Redriguez",
    IDP: "Independent Air Freighters",
    IDR: "Indicator Company",
    IDS: "Indonesia Sky",
    IDX: "Indonesa Air Aisa X",
    IEA: "Inter European Airways",
    IEP: "Elipiu'",
    IFA: "FAI Airservice",
    IFC: "Indian Air Force",
    IFF: "Interfreight Forwarding",
    IFI: "Air Lift",
    IFL: "IFL Group",
    IFS: "Italy First",
    IFT: "Interflight",
    IFX: "International Flight Training Academy",
    IGA: "Skytaxi",
    IGN: "Interguide Air",
    IGO: "IndiGo Airlines",
    IGS: "Isle Grande Flying School",
    IHE: "Interjet Helicopters",
    IHS: "Thryluthjonustan",
    IIA: "AIR INDOCHINE",
    IIG: "International Company for Transport",
    IIL: "India International Airways",
    IIN: "Inter Islands Airlines",
    IIR: "INAVIA Internacional",
    IJA: "International Jet Aviation Services",
    IJE: "Ivoire Jet Express",
    IJM: "IJM International Jet Management",
    IJS: "Silvair",
    IJT: "InteliJet Airways",
    IKA: "Itek Air",
    IKK: "IKI International Airlines",
    IKM: "Aero Survey",
    IKN: "IKON FTO",
    IKR: "Ikaros DK",
    IKY: "Intersky",
    ILC: "Island Link",
    ILF: "Island Air Charters",
    ILK: "Aero Airline",
    ILL: "Ilyich-Avia",
    ILN: "Interair South Africa",
    ILP: "Ilpo Aruba Cargo",
    ILS: "Servicios Aereos Ilsa",
    ILV: "Il-Avia",
    ILW: "Illinois Airways",
    ILY: "Fly Illi",
    IMA: "Inter-Mountain Airways",
    IME: "Airtime Charters",
    IMN: "Aerotaxis Cimarron",
    IMP: "Hellenic Imperial Airways",
    IMR: "Imaer",
    IMT: "Imtrec Aviation",
    IMX: "Zimex Aviation",
    INA: "Aero International",
    INC: "Insel Air International",
    IND: "Iona National Airways",
    INE: "International Europe",
    ING: "Aeroingenieria",
    INK: "Sincom-Avia",
    INL: "Intal Avia",
    INO: "Aeroservicios Intergrados de Norte",
    INP: "Peruvian Navy",
    INS: "Inflite The Jet Centre",
    INT: "Intair",
    INU: "Flyguppdraget Backamo",
    INV: "Inversija",
    INX: "Inter Express",
    IOA: "Iowa Airways",
    IOM: "Island Aviation and Travel",
    IOS: "Isles of Scilly Skybus",
    IPA: "IPEC Aviation",
    IPL: "Airpull Aviation",
    IPM: "IPM Europe",
    IPN: "Industri Pesawat Terbang Nusantara",
    IPT: "Interport Corporation",
    IPV: "Parmiss Airlines (IPV)",
    IQQ: "Caribbean Airways",
    IRA: "Iran Air",
    IRB: "Iranair Tours",
    IRC: "Iran Aseman Airlines",
    IRD: "Arvand Airlines",
    IRE: "Pariz Air",
    IRF: "TA-Air Airline",
    IRG: "Naft Air Lines",
    IRH: "Atlas Aviation Group",
    IRI: "Navid",
    IRJ: "Bonyad Airlines",
    IRK: "Kish Air",
    IRL: "Irish Air Corps",
    IRM: "Mahan Air",
    IRO: "CSA Air",
    IRP: "Payam Air",
    IRQ: "Qeshm Air",
    IRR: "Tara Air Line",
    IRT: "Irtysh Airlines",
    IRU: "Chabahar Airlines",
    IRV: "Safat Airlines",
    IRW: "Aram Airline",
    IRX: "Aria Tour",
    IRY: "Eram Air",
    IRZ: "Saha Airlines Services",
    ISD: "ISD Avia",
    ISF: "International Sabilisation Assistance Force",
    ISG: "Club Air",
    ISI: "Island Air",
    ISK: "Intersky",
    ISL: "Landsflug",
    ISM: "Auo Airclub AIST-M",
    ISN: "Interisland Airlines",
    ISR: "Israir",
    ISS: "Meridiana",
    IST: "Istanbul Airlines",
    ISV: "Islena De Inversiones",
    ISW: "Islas Airways",
    ISX: "Island Spirit",
    ITA: "Inter-Air",
    ITC: "International Air Carrier Association",
    ITE: "Aerotaxi",
    ITF: "Avita-Servicos Aereos",
    ITH: "International Trans-Air",
    ITK: "Interlink Airlines",
    ITN: "Industrias Titan",
    ITO: "Aero Citro",
    ITR: "Frontier Commuter",
    ITS: "Inter-State Aviation",
    ITU: "Intervuelos",
    ITW: "Inter Air",
    ITX: "Imair Airlines",
    IUS: "Icarus",
    IVA: "Innotech Aviation",
    IVE: "Air Executive",
    IVJ: "Flight International",
    IVR: "Burundaiavia",
    IVS: "Ivoire Aero Services",
    IVT: "Interaviatrans",
    IVW: "Ivoire Airways",
    IWA: "Apache Air",
    IWD: "Iberworld",
    IWS: "Aviainvest",
    IWY: "Interisland Airways Limited",
    IXO: "OCEAN AIR CARGO",
    IXR: "Ixair",
    IXX: "Dolphin Express Airlines",
    IYE: "Yemenia",
    IZA: "Izhavia",
    IZG: "Zagros Airlines",
    IZM: "Izair",
    JAA: "Japan Asia Airways",
    JAB: "Air Bagan",
    JAC: "Japan Air Commuter",
    JAD: "Aerojal",
    JAE: "Jade Cargo International",
    JAF: "Jetairfly",
    JAG: "Jetalliance",
    JAI: "Jet Airways",
    JAK: "Jana-Arka",
    JAL: "Japan Airlines",
    JAM: "Sunline Express",
    JAR: "Airlink",
    JAS: "Japan Air System",
    JAT: "Jat Airways",
    JAV: "Jordan Aviation",
    JAW: "Jamahiriya Airways",
    JAX: "Janair",
    JAZ: "JALways",
    JBA: "Helijet",
    JBR: "Job Air",
    JBU: "JetBlue Airways",
    JCB: "J C Bamford (Excavators)",
    JCC: "Jetcraft Aviation",
    JCF: "Jet Center Flight Training",
    JCH: "Trading Air Cargo",
    JCI: "Jordan International Air Cargo",
    JCK: "Jackson Air Services",
    JCM: "Secure Air Charter",
    JCR: "Rotterdam Jet Center",
    JCS: "Jetclub",
    JCT: "Jet Charter",
    JCX: "Jet Connection",
    JDA: "JDAviation",
    JDC: "Deere and Company",
    JDG: "Joanas Avialinijos",
    JDI: "Jet Service",
    JDP: "JDP Lux",
    JEA: "Jet Air",
    JEC: "Jett8 Airlines Cargo",
    JED: "Jet East International",
    JEE: "Ambjek Air Services",
    JEF: "Jetflite",
    JEI: "Jet Executive International Charter",
    JEJ: "Jets Ejecutivos",
    JEK: "Jet Link",
    JEL: "Tal Air Charters",
    JEM: "Emerald Airways",
    JEP: "Jets Personales",
    JES: "JS Aviation",
    JET: "Wind Jet",
    JEV: "Lagun Air",
    JEX: "JAL Express",
    JFC: "LTV Jet Fleet Corporation",
    JFK: "Keenair Charter -",
    JFL: "Jetfly Airlines",
    JFS: "Juanda Flying School",
    JFU: "Jet4You",
    JFY: "Foster Yeoman",
    JGD: "Jet G&D Aviation",
    JGN: "Jagson Airlines",
    JGO: "JetsGo",
    JHM: "JHM Cargo Expreso",
    JHN: "Johnson Air",
    JIA: "PSA Airlines",
    JIB: "Jibair",
    JIC: "Jetgo International",
    JIM: "Sark International Airways",
    JJA: "Jeju Air",
    JJM: "Regional Geodata Air",
    JJP: "Jetstar Japan ",
    JKK: "Spanair",
    JKY: "Helicopter & Aviation Services",
    JLA: "MIA Airlines",
    JLH: "Jetlink Holland",
    JLJ: "J-Air",
    JLN: "Eurojet Limited",
    JLX: "Jetlink Express",
    JMB: "Jambo Africa Airlines",
    JMC: "JMC Airlines",
    JMG: "JetMagic",
    JMJ: "Johnston Airways",
    JMM: "Joint Military Commission",
    JMR: "Alexandair",
    JMT: "Jomartaxi Aereo",
    JMX: "Air Jamaica Express",
    JNA: "Jin Air",
    JNH: "M & N Aviation",
    JNR: "Jet Norte",
    JNV: "Jetnova de Aviacion Ejecutiva",
    JNY: "Jenney Beechcraft",
    JOA: "Air Swift Aviation",
    JOB: "Aerojobeni",
    JOL: "Atyrau Air Ways",
    JON: "Johnsons Air",
    JOR: "Blue Air",
    JOS: "DHL de Guatemala",
    JOY: "Joy Air",
    JPA: "OSACOM",
    JPN: "Jeppesen UK",
    JPO: "Jetpro",
    JPQ: "Jett Paqueteria",
    JPR: "Aerosmith Aviation",
    JPU: "Jupiter Airlines",
    JRB: "Jc royal.britannica",
    JRF: "First Air Transport",
    JRI: "Jetrider International",
    JRN: "Jet Rent",
    JSA: "Jetstar Asia Airways",
    JSE: "Jets Y Servicios Ejecutivos",
    JSH: "Jetstream Air",
    JSI: "Jet Air Group",
    JSJ: "JS Air",
    JSM: "Jet Stream",
    JSP: "Palmer Aviation",
    JSR: "Jusur airways",
    JST: "Jetstar Airways",
    JSV: "Japan Aircraft Service",
    JSW: "Jigsaw Project",
    JTA: "Japan Transocean Air",
    JTC: "Jet Trans Aviation",
    JTE: "National Jet Express",
    JTG: "Jettime",
    JTI: "Cirrus Middle East",
    JTL: "Jetall Holdings",
    JTM: "Exxavia Limited",
    JTN: "Jettrain Corporation",
    JTO: "Jettor Airlines",
    JTR: "Executive Aviation Services",
    JTS: "Arrendamiento de Aviones Jets",
    JTT: "Jet-2000",
    JTU: "Zhetysu",
    JTX: "Jet Aspen Air Lines",
    JTY: "Jatayu Airlines",
    JUA: "Aero Juarez",
    JUB: "Jubba Airways",
    JUC: "Juba Cargo Services & Aviation Company",
    JUR: "Ju-Air",
    JUS: "USA Jet Airlines",
    JVK: "Jorvik",
    JWW: "JetWind",
    JWY: "Jetways of Iowa",
    JXA: "Jetex Aviation",
    JXT: "Jetstream Executive Travel",
    JXX: "Jetx Airlines",
    JZA: "Air Canada Jazz",
    JZR: "Jazeera Airways",
    KAA: "Asia Aero Survey and Consulting Engineers",
    KAC: "Kuwait Airways",
    KAD: "Air Kirovograd",
    KAE: "Kartika Airlines",
    KAH: "Kent Aviation",
    KAI: "Kaiser Air",
    KAJ: "Karthago Airlines",
    KAL: "Korean Air",
    KAM: "Air Mach",
    KAO: "Kazan Aviation Production Association",
    KAP: "Cape Air",
    KAR: "Kar-Air",
    KAS: "Kingston Air Services",
    KAT: "Kato Airline",
    KAV: "Air Kufra",
    KAW: "Kaz Air West",
    KBA: "Kenn Borek Air",
    KBR: "KoralBlue Airlines",
    KBV: "Kustbevakningen",
    KBZ: "Air KBZ",
    KCA: "Trans-Kiev",
    KCE: "Irving Oil Transport",
    KCU: "Skyline Ulasim Ticaret A.S.",
    KDA: "Kendell Airlines",
    KDC: "K D Air Corporation",
    KDR: "Royal Daisy Airlines",
    KDZ: "Flightworks",
    KEA: "Korea Express Air",
    KEE: "Keystone Air Services",
    KEK: "Arkhabay",
    KEN: "Kenmore Air",
    KES: "Kallat El Saker Air Company",
    KEY: "Key Airlines",
    KFA: "Kelowna Flightcraft Air Charter",
    KFC: "Kremenchuk Flight College",
    KFK: "Aero Charter Krifka",
    KFR: "Kingfisher Airlines",
    KFS: "Kalitta Charters",
    KFT: "Air Kraft Mir",
    KGA: "Kyrgyzstan Airlines",
    KGB: "Kyrgz General Aviation",
    KGC: "Peach Air",
    KGD: "Air Concorde",
    KGL: "Kogalymavia Air Company",
    KGO: "Korongo Airlines",
    KGT: "Knights Airlines",
    KGZ: "Kyrgyz Airlines",
    KHA: "Kitty Hawk Aircargo",
    KHB: "Dalavia",
    KHC: "Kitty Hawk Airways",
    KHE: "Kanfey Ha'emek Aviation",
    KHH: "Alexandria Airlines",
    KHK: "Kharkiv Airlines",
    KHM: "Cambodia Airlines",
    KHO: "Khors Aircompany",
    KHP: "Khoezestan Photros Air Lines",
    KHR: "Khazar",
    KHX: "Knighthawk Express",
    KHY: "Khyber Afghan Airlines",
    KIA: "Kiwi International Air Lines",
    KIE: "Afit",
    KIL: "Kuban Airlines",
    KIN: "Kinloss Flying Training Unit",
    KIP: "Kinnarps",
    KIS: "Contact Air",
    KIW: "Royal New Zealand Air Force",
    KIZ: "Kanaf-Arkia Airlines",
    KJC: "Krasnojarsky Airlines",
    KKA: "Kazavia",
    KKB: "Air South",
    KKK: "Atlasjet",
    KKS: "Salem",
    KLA: "Air Lithuania",
    KLB: "Air Mali International",
    KLC: "KLM Cityhopper",
    KLD: "Air Klaipeda",
    KLE: "Rusaero",
    KLG: "Karlog Air Charter",
    KLH: "KLM Helicopter",
    KLM: "KLM Royal Dutch Airlines",
    KLO: "Flight-Ops International",
    KLR: "Columbus Air Transport",
    KLS: "Kal Star Aviation",
    KLX: "Kelix Air",
    KLZ: "Aerokaluz",
    KMA: "Komiaviatrans State Air Enterprise",
    KMB: "Delta Engineering Aviation",
    KMC: "Kahama Mining Corporation",
    KMF: "Kam Air",
    KMG: "Kosmas Air",
    KMI: "K-Mile Air",
    KMP: "Kampuchea Airlines",
    KMR: "Western Pacific Airlines",
    KMV: "Komiinteravia",
    KNA: "Knight Air",
    KND: "Kan Air",
    KNE: "Nas Air",
    KNG: "King Aviation",
    KNI: "KD Avia",
    KNM: "Komsomolsk-on-Amur Air Enterprise",
    KNS: "Kinshasa Airways",
    KNX: "Knighthawk Air Express",
    KOA: "Koanda Avacion",
    KOB: "Koob-Corp - 96 KFT",
    KOE: "Northland Aviation",
    KOK: "Horizon Air Service",
    KOL: "SOCHI AIR",
    KOM: "Kom Activity",
    KOP: "Servicios Aereos Copters",
    KOQ: "Kostromskie avialinii",
    KOR: "Air Koryo",
    KOS: "Kosova Airlines",
    KOV: "Orlan-2000",
    KOY: "Araiavia",
    KPA: "Kunpeng Airlines",
    KPH: "Kazan Helicopters",
    KQA: "Kenya Airways",
    KRA: "Transcontinental Airlines",
    KRB: "Karibu Airways Company",
    KRC: "New Zealand Air Defence Force",
    KRE: "AeroSucre",
    KRF: "United Kingdom Royal VIP Flights",
    KRG: "Krimaviamontag",
    KRH: "United Kingdom Royal VIP Flight",
    KRI: "Krylo Airlines",
    KRM: "Crimea Universal Avia",
    KRN: "Kaz Agros Avia",
    KRO: "Kroonk Air Agency",
    KRP: "Carpatair",
    KRT: "Air Kokshetau",
    KRV: "Khoriv-Avia",
    KRY: "Russkie Krylya",
    KSA: "K S Avia",
    KSI: "Air Kissari",
    KSM: "Kosmos",
    KSP: "Servicios Aereos Expecializados En Transportes Petroleros",
    KSS: "Raytheon Travel Air",
    KST: "PTL Luftfahrtunternehemen",
    KSU: "Kansas State University",
    KSY: "KSY",
    KSZ: "Sunrise Airways",
    KTA: "Kirov Air Enterprise",
    KTB: "Transaviabaltika",
    KTC: "Kyrgyz Trans Avia",
    KTK: "Katekavia",
    KTL: "P & P Floss Pick Manufacturers",
    KTN: "Aeronavigaciya",
    KTR: "HT Helikoptertransport",
    KTS: "Transair-Gyraintiee",
    KTV: "Kata Transportation",
    KUH: "Kush Air",
    KUS: "National Airlines",
    KVK: "Olimp Air",
    KVR: "Alliance Avia",
    KVS: "Kevis",
    KWA: "Vozdushnaya Academy",
    KWN: "Kwena Air",
    KWY: "Key Air",
    KYA: "Alghanim",
    KYB: "Skybridge Airops",
    KYC: "Av Atlantic",
    KYM: "Krym",
    KYR: "Sky Aeronautical Services",
    KYV: "Kibris T",
    KZA: "Kurzemes Avio",
    KZE: "Euro-Asia Air International",
    KZH: "Zhez Air",
    KZK: "Air Kazakhstan",
    KZM: "Phoebus Apolloa Zambia",
    KZR: "Air Astana",
    KZS: "Kazaviaspas",
    KZU: "Kuzu Airlines Cargo",
    KZW: "Khalifa Airways",
    LAA: "Libyan Arab Airlines",
    LAB: "L.A.B. Flying Service",
    LAC: "Lockeed Aircraft Corporation",
    LAD: "Lebanon Airport Development Corporation",
    LAF: "Latvian Air Force",
    LAG: "Aerovias De Lagos",
    LAH: "L A Helicopter",
    LAJ: "British Mediterranean Airways",
    LAL: "Labrador Airways",
    LAM: "Linhas A",
    LAN: "LAN Airlines",
    LAO: "Lao Airlines",
    LAP: "TAM Mercosur",
    LAQ: "Lebanese Air Transport",
    LAR: "Lawrence Aviation",
    LAT: "Lebanese Air Transport (Charter)",
    LAU: "L",
    LAV: "AlbaStar",
    LAW: "Link Airways of Australia",
    LAY: "Layang-Layang Aerospace",
    LBC: "Albanian Airlines",
    LBH: "Laker Airways (Bahamas)",
    LBI: "Albisa",
    LBL: "Line Blue",
    LBQ: "Quest Diagnostics",
    LBR: "Elbe Air Transport",
    LBT: "Nouvel Air Tunisie",
    LBW: "Albatros Airways",
    LBY: "Belle Air",
    LCA: "Leconte Airlines",
    LCB: "LC Busre",
    LCC: "The Lancair Company",
    LCD: "Lineas Aereas Azteca",
    LCG: "Lignes Aeriennes Congolaises",
    LCH: "Lynch Flying Service",
    LCM: "Lineas Aereas Comerciales",
    LCN: "Lineas Aereas Canedo LAC",
    LCO: "LAN Cargo",
    LCR: "Libyan Arab Air Cargo",
    LCS: "RAF Leuchars",
    LCT: "Compania De Actividades Y Servicios De Aviacion",
    LCY: "London City Airport Jet Centre",
    LDA: "Lauda Air",
    LDE: "LADE - Lineas Aereas Del Estado",
    LDG: "Aerolineas Aereas Ejecutivas De Durango",
    LDI: "Lauda Air Italy",
    LDL: "Aerologic",
    LDN: "Al-Donas Airlines",
    LDR: "Aero Lider",
    LEA: "Unijet",
    LEC: "Linex",
    LED: "Blom Geomatics",
    LEE: "Leeming Flying Training Unit",
    LEG: "The Army Aviation Heritage Foundation",
    LEJ: "FSH Luftfahrtunternehmen",
    LEL: "Leonsa De Aviacion",
    LEM: "Aleem",
    LEN: "Lentini Aviation",
    LEP: "Laughlin Express",
    LER: "Linea Aerea de Servicio Ejecutivo Regional",
    LET: "Aerolineas Ejecutivas",
    LEU: "Lions-Air",
    LEX: "L'Express",
    LFA: "Air Alfa",
    LFC: "Aero Control Air",
    LFE: "Luxflight Executive",
    LFI: "National Airways Corporation",
    LFL: "Executive Air",
    LFO: "Deutsches Zentrum fur Luft-und Raumfahrt EV",
    LFP: "Alfa Aerospace",
    LFR: "Atlantic Airfreight Aviation",
    LFT: "Aerolift Company",
    LGD: "Legend Airlines",
    LGL: "Luxair",
    LGN: "Aerolaguna",
    LGT: "Longtail Aviation",
    LGU: "Servicios Aereos Ejecutivos De La Laguna",
    LGW: "Luftfahrtgesellschaft Walter",
    LHC: "London Helicopter Centres",
    LHN: "Express One International",
    LHR: "Al Ahram Aviation",
    LHS: "Luhansk",
    LHT: "Lufthansa Technik",
    LIA: "Leeward Islands Air Transport",
    LIB: "Ploizeihubschrauberstaffel Hamburg",
    LID: "Alidaunia",
    LIE: "Al-Dawood Air",
    LIF: "Rocky Mountain Holdings",
    LIJ: "Lineas Aereas San Jose",
    LIL: "FlyLal",
    LIO: "LÍO Flugmennt",
    LIQ: "Lid Air",
    LIR: "Minsk Aircraft Overhaul Plant",
    LIS: "Eastern Express",
    LIV: "Air Livonia",
    LIX: "LionXpress",
    LJA: "Air Jamahiriya Company",
    LJJ: "Luchsh Airlines ",
    LJY: "L J Aviation",
    LKA: "Lao Capricorn Air",
    LKD: "Lignes Aeriennes Du Tchad",
    LKE: "Lucky Air",
    LKL: "Lakeland Aviation",
    LKN: "Lankair",
    LKP: "American Aviation",
    LKR: "Laker Airways",
    LKS: "Airlink Solutions",
    LKW: "Top Sky International",
    LKY: "Air Solutions",
    LLA: "Servico Leo Lopex",
    LLB: "Lloyd Aereo Boliviano",
    LLC: "FlyLAL Charters",
    LLL: "Lao Air Company",
    LLM: "Yamal Airlines",
    LLO: "Operation Enduring Freedom",
    LLR: "Alliance Air",
    LLS: "Servicios Aereos Estrella",
    LMA: "Aerolima",
    LMC: "Linea Aerea Mexicana de Carga",
    LME: "Lignes Mauritaniennes Air Express",
    LMG: "South African Air Force",
    LMJ: "Masterjet",
    LMK: "Grantex Aviation",
    LML: "Alamia Air",
    LMM: "LCM AIRLINES",
    LMN: "Lineas Aereas Monarca",
    LMO: "Sky Limo Corporation",
    LMP: "Air Plus Argentina",
    LMR: "Lamra",
    LMS: "Lomas Helicopters",
    LMT: "Almaty Aviation",
    LMU: "AlMasria Universal Airlines",
    LMX: "Aerolineas Mexicanas J S",
    LMY: "Air Almaty",
    LMZ: "Air Almaty ZK",
    LNA: "Lnair Air Services",
    LNC: "LAN Dominica",
    LNE: "Aerolane",
    LNG: "Lockheed Martin Aeronautics Company",
    LNI: "Lion Mentari Airlines",
    LNK: "Airlink",
    LNP: "Linea Aerea SAPSA",
    LNT: "Aerolineas Internacionales",
    LNX: "London Executive Aviation",
    LOC: "Locair",
    LOD: "Malmoe Air Taxi",
    LOF: "Trans States Airlines",
    LOG: "Logan Air",
    LOK: "Alok Air",
    LOO: "LSM Airlines",
    LOP: "Linton-on-Ouse Flying Training Unit",
    LOR: "Leo-Air",
    LOS: "Lossiemouth Flying Training Unit",
    LOT: "LOT Polish Airlines",
    LOU: "Air Saint Louis",
    LOV: "London Flight Centre (Stansted)",
    LPA: "Pal Aerolineas",
    LPC: "Alpine Aviation",
    LPD: "UK Royal/HRH Duke of York",
    LPE: "LAN Peru",
    LPL: "Lease-a-Plane International",
    LPN: "Laoag International Airlines",
    LPR: "L",
    LPV: "Air Alps Aviation",
    LRA: "Little Red Air Service",
    LRB: "L R Airlines",
    LRC: "LACSA",
    LRD: "Laredo Air",
    LRO: "Alrosa-Avia",
    LRR: "Lorraine Aviation",
    LRS: "SANSA",
    LRT: "Lincoln Airlines",
    LRW: "Al Rida Airways",
    LSA: "LANSA",
    LSC: "Los Cedros Aviacion",
    LSE: "Linea De Aeroservicios",
    LSK: "Aurela",
    LSM: "Aerobusinessservice",
    LSP: "Spectrum Aviation Incorporated",
    LSR: "Alsair",
    LSS: "Lone Star Airlines",
    LSU: "Laus",
    LSY: "Lindsay Aviation",
    LTA: "Trans Atlantis",
    LTC: "LatCharter",
    LTD: "Southern Airways Express",
    LTE: "LTE International Airways",
    LTF: "Lufttaxi Fluggesellschaft",
    LTI: "Aerotaxis Latinoamericanos",
    LTL: "Benin Littoral Airways",
    LTO: "LTU Austria",
    LTR: "Lufttransport",
    LTS: "Flight Inspections and Systems",
    LTU: "Air Lituanica",
    LTW: "Luchtvaartmaatschappij Twente",
    LTY: "Liberty Airways",
    LUC: "Alninati Aeronautics",
    LUK: "LUKoil-Avia",
    LUR: "Atlantis European Airways",
    LUT: "Luft Carago",
    LUV: "Luxembourg Air Rescue",
    LUZ: "Luzair",
    LVB: "IRS Airlines",
    LVD: "Luftfahrt-Vermietungs-Dienst",
    LVG: "Livingston",
    LVN: "Aliven",
    LVR: "Aviavilsa",
    LVT: "La Valenciana Taxi Aereo",
    LWD: "Leisure Air",
    LXA: "Luxaviation",
    LXF: "Lynx Air International",
    LXG: "Air Luxor GB",
    LXJ: "Bombardier Business Jet Solutions",
    LXO: "Luxor Air",
    LXP: "LAN Express",
    LXR: "Air Luxor",
    LYC: "Lynden Air Cargo",
    LYD: "Lydd Air",
    LYF: "Lithuanian Air Force",
    LYM: "Key Lime Air",
    LYN: "Kyrgyzstan",
    LYT: "Apatas Air",
    LYW: "Libyan Airlines",
    LYX: "Lynx Aviation",
    LZA: "Lanza Air",
    LZB: "Bulgaria Air",
    LZP: "Air Ban",
    LZR: "Air Lazur",
    LZT: "Lanzarote Aerocargo",
    MAA: "MasAir",
    MAB: "Millardair",
    MAC: "Malta Air Charter",
    MAD: "Maple Air Services",
    MAE: "Mali Air",
    MAF: "Mission Aviation Fellowship",
    MAH: "Malév",
    MAI: "Mauritania Airlines International",
    MAJ: "Majestic Airlines",
    MAK: "MAT Macedonian Airlines",
    MAL: "Morningstar Air Express",
    MAM: "Aerodromo De La Mancha",
    MAN: "Mannion Air Charter",
    MAQ: "Mac Aviation",
    MAR: "March Helicopters",
    MAS: "Malaysia Airlines",
    MAT: "Maine Aviation",
    MAU: "Air Mauritius",
    MAV: "Maldivo Airlines",
    MAW: "Mustique Airways",
    MAX: "Max-Aviation",
    MAZ: "Mines Air Services Zambia",
    MBA: "Avag Air",
    MBB: "Air Manas",
    MBC: "Airjet Exploracao Aerea De Carga",
    MBE: "Martin-Baker",
    MBI: "Mountain Bird",
    MBL: "First City Air",
    MBN: "Zambian Airways",
    MBO: "Mobil Oil",
    MBR: "Brazilian Navy Aviation",
    MBS: "Mbach Air",
    MBV: "Aeriantur-M",
    MCA: "MCA Airlines",
    MCB: "Air Mercia",
    MCC: "MCC Aviation",
    MCD: "Air Medical",
    MCE: "Marshall Aerospace",
    MCF: "MAC Fotografica",
    MCG: "SOS Helikoptern Gotland",
    MCH: "McAlpine Helicopters",
    MCK: "Macair Airlines",
    MCL: "Medical Aviation Services",
    MCM: "Heli-Air-Monaco",
    MCN: "Mac Dan Aviation Corporation",
    MCO: "Aerolineas Marcos",
    MCP: "Marcopolo Airways",
    MCS: "Macedonian Airlines",
    MCT: "Transportacion Aerea Del Mar De Cortes",
    MCV: "MTC Aviacion",
    MCX: "Cargo Express",
    MCY: "Ambulance Air Africa",
    MDA: "Mandarin Airlines",
    MDB: "Monde Air Charters",
    MDC: "Atlantic Aero and Mid-Atlantic Freight",
    MDE: "Mauritanienne De Transport Aerien",
    MDF: "Mediterranean Air Freight",
    MDG: "Air Madagascar",
    MDH: "Madina Air",
    MDJ: "Jetran Air",
    MDL: "Mandala Airlines",
    MDM: "Medavia Company",
    MDN: "Mudan Airlines",
    MDO: "Domenican Airlines",
    MDP: "Medallion Air",
    MDR: "Compania Mexicana De Aeroplanos",
    MDS: "McNeely Charter Services",
    MDT: "Sundt Air",
    MDV: "Moldavian Airlines",
    MDW: "Midway Airlines",
    MDX: "Aerosud Charter",
    MDY: "Mediterranean Airways",
    MEA: "Middle East Airlines",
    MEC: "Mercury Aircourier Service",
    MED: "Ontario Ministry of Health",
    MEE: "Elimediterranea",
    MEF: "Air Meridan",
    MEI: "Merlin Airways",
    MEJ: "Medjet International",
    MEK: "Med-Trans of Florida",
    MEL: "Mega Linhas Aereas",
    MEM: "Meridian Limited",
    MEP: "Midwest Airlines",
    MER: "Methow Aviation",
    MES: "Mesaba Airlines",
    MET: "Meteorological Research Flight",
    MEX: "Metro Express",
    MEY: "Justair Scandinavia",
    MFA: "Martyn Fiddler Associates",
    MFC: "Moncton Flying Club",
    MFL: "Aero McFly",
    MFR: "Midline Air Freight",
    MFS: "Miller Flying Services",
    MFT: "Multiflight",
    MFZ: "Mofaz Air",
    MGA: "MG Aviacion",
    MGB: "Coulson Flying Service",
    MGE: "Asia Pacific Airlines",
    MGG: "Elmagal Aviation Services",
    MGI: "Marghi Air",
    MGK: "Mega",
    MGL: "MIAT Mongolian Airlines",
    MGM: "Transporte Aero MGM",
    MGO: "Punto Fa",
    MGR: "Magna Air",
    MGS: "Aeromagar",
    MGX: "Montenegro Airlines",
    MHA: "Mountain High Aviation",
    MHC: "Aero Jomacha",
    MHL: "Meridian Airlines",
    MHN: "Manhattan Air",
    MHS: "Air Memphis",
    MHU: "Air Memphis",
    MIA: "Mauria",
    MIC: "Mint Airways",
    MID: "Midland Airport Services",
    MIE: "Aero Premier De Mexico",
    MIF: "Miras",
    MIG: "Russian Aircraft Corporation-MiG",
    MIM: "Mimino",
    MIR: "Miramichi Air Service",
    MIS: "Midstate Airlines",
    MIT: "Flight Line",
    MIZ: "Desarrollo Milaz",
    MJB: "Magic Blue Airlines",
    MJG: "Michael Airlines",
    MJL: "Jet Line International",
    MJM: "Eti 2000",
    MJN: "Royal Air Force of Oman",
    MJP: "Air Majoro",
    MJR: "Midamerica Jet",
    MJT: "Mex-Jet",
    MJX: "Euroline",
    MKA: "MK Airline",
    MKD: "MAT Airways",
    MKG: "Air Mekong",
    MKH: "Air Marrakech Service",
    MKK: "Malaya Aviatsia Dona",
    MKL: "McCall Aviation",
    MKN: "Mekong Airlines",
    MKO: "Markoss Aviation",
    MKS: "Pimichikamac Air",
    MKU: "Island Air (WP)",
    MKY: "Monky Aerotaxis",
    MLA: "40-Mile Air",
    MLB: "Manaf International Airways",
    MLC: "Malila Airlift",
    MLD: "Air Moldova",
    MLE: "Moldaeroservice",
    MLF: "Amal Airlines",
    MLG: "Malagasy Airlines",
    MLH: "Mahalo Air",
    MLI: "Air Mali",
    MLK: "Millennium Air",
    MLN: "Air Madeleine",
    MLO: "Servicios Aereos Milenio",
    MLS: "Mall Airways",
    MLU: "Zracno Pristaniste Mali Losinj",
    MLX: "Malawi Express",
    MMC: "Aermarche",
    MMD: "Air Alsie",
    MMM: "Myanmar Airways International",
    MMP: "AMP Incorporated",
    MMR: "Musrata Air Transport",
    MMS: "SAAD (A320) Limited",
    MMX: "Airmax",
    MMZ: "EuroAtlantic Airways",
    MNA: "Merpati Nusantara Airlines",
    MNB: "MNG Airlines",
    MNC: "MIT Airlines",
    MNE: "Aerolineas Amanecer",
    MNG: "Aero Mongolia",
    MNH: "Monarch Airlines",
    MNI: "Aeromilenio",
    MNJ: "Menajet",
    MNL: "Miniliner",
    MNO: "Mango",
    MNP: "Spirit of Manila Airlines",
    MNR: "Mann Air",
    MNS: "Ministic Air",
    MNT: "Montserrat Airways",
    MNV: "Mauritanienne Aerienne Et Navale",
    MNX: "Manx Airlines",
    MNY: "Mooney Aircraft Corporation",
    MNZ: "Murmansk Aircompany",
    MOC: "Air Monarch Cargo",
    MOH: "Tigerfly",
    MON: "Monarch Airlines",
    MOP: "Aeropublicitaria De Angola",
    MOR: "Aerolineas De Morelia",
    MOS: "Misr Overseas Airways",
    MOV: "VIM Airlines",
    MOW: "Mowhawk Airlines",
    MPA: "Mid-Pacific Airlines",
    MPC: "Mountain Pacific Air",
    MPD: "Air Plus Comet",
    MPE: "Canadian North",
    MPH: "Martinair",
    MPI: "Mosphil Aero",
    MPJ: "MAP-Management and Planung",
    MPL: "Master Planner",
    MPO: "Transportes Aereos Amparo",
    MPR: "Empire Aviation Services",
    MPS: "Metroplis",
    MPT: "Miapet-Avia",
    MPX: "Aeromexpress",
    MQT: "Air ITM",
    MRA: "Martinaire",
    MRD: "Meridian Air Cargo",
    MRE: "Namibia Commerical Aviation",
    MRF: "Mauritanienne Air Fret",
    MRG: "MANAG'AIR",
    MRH: "Marham Flying Training Unit",
    MRI: "Servicios Aereos Moritani",
    MRK: "Markair",
    MRL: "Aeromorelos",
    MRM: "Aerocharter",
    MRN: "Missions Gouvernemtales Francaises",
    MRO: "Morrison Flying Service",
    MRP: "Abas",
    MRR: "San Juan Airlines",
    MRS: "Marusya Airways",
    MRT: "Air Mauritanie",
    MRW: "Mars RK",
    MRX: "Herman's Markair Express",
    MRZ: "Medical Air Rescue Services",
    MSA: "Mistral Air",
    MSC: "Air Cairo",
    MSE: "EgyptAir Express",
    MSF: "Max Sea Food",
    MSG: "Servico Aereo Regional",
    MSH: "US Marshals Service",
    MSI: "Motor Sich",
    MSK: "Air Sport",
    MSL: "Marsland Aviation",
    MSM: "Aeromas",
    MSN: "Missionair",
    MSO: "Aerol",
    MSP: "Servicio De Viglancia Aerea Del Ministerio De Seguridad Publica",
    MSQ: "Meta Linhas A",
    MSR: "Egyptair",
    MSS: "Morris Air Service",
    MSV: "Aero-Kamov",
    MSW: "Master Airways",
    MSX: "Egyptair Cargo",
    MSY: "Massey University School of Aviation",
    MTA: "GAK/Mitchell Aero",
    MTB: "Aerotaxis Metropolitanos",
    MTC: "Mountain Air Company",
    MTD: "MacKnight Airlines",
    MTE: "Aeromet Linea Aerea",
    MTF: "Interjet",
    MTG: "Servicios Aereos MTT",
    MTH: "Massachusetts Institute of Technology",
    MTI: "Monerrey Air Taxi",
    MTJ: "Metrojet",
    MTK: "Air Metack",
    MTL: "RAF-Avia",
    MTN: "Mountain Air Cargo",
    MTO: "Manitoulin Air Services",
    MTP: "Island Helicopters",
    MTR: "Metroflight",
    MTS: "Mantrust Asahi Airways",
    MTV: "Mountain Valley Air Service",
    MTW: "Mauritania Airways",
    MTX: "Multi Taxi",
    MTY: "Air Montegomery",
    MTZ: "Mali Airways",
    MUA: "Murray Air",
    MUI: "Trans Air",
    MUN: "Aeromundo Ejecutivo",
    MUR: "Aerolinea Muri",
    MVA: "Mississippi Valley Airways",
    MVD: "Kavminvodyavia",
    MVG: "Moldova",
    MVI: "Metro Business Aviation",
    MVK: "Helicopter Training & Hire",
    MVL: "Mavial Magadan Airlines",
    MVM: "Air Cargo America",
    MVN: "Marvin Limited",
    MVR: "Maverick Airways",
    MVY: "VIM-Aviaservice",
    MWA: "Midwest Airlines (Egypt)",
    MWI: "Malaysia Wings",
    MWR: "Raslan Air Service",
    MWS: "Malta Wings",
    MWT: "Midwest Aviation Division",
    MWY: "Mauritanienne Airways",
    MXA: "Mexicana de Aviaci",
    MXB: "Mex Blue",
    MXC: "Compania Mexicargo",
    MXD: "Malindo Air",
    MXE: "Mocambique Expresso",
    MXI: "MexicanaLink",
    MXJ: "Maxjet Airways",
    MXL: "Maxair",
    MXO: "Aerotaxi Mexicano",
    MXP: "May Air Xpress",
    MXQ: "Transportes Aereos Mexiquenses",
    MXS: "Maxsus-Avia",
    MXT: "Mexico Transportes Aereos",
    MXU: "Maximus Air Cargo",
    MXX: "Merchant Express Aviation",
    MYA: "Myflug",
    MYD: "Maya Island Air",
    MYI: "Mayair",
    MYO: "Dominguez Toledo (Grupo Mayoral)",
    MYP: "Mann Yadanarpon Airlines",
    MYS: "Aero Yaqui Mayo",
    MYT: "MyTravel Airways",
    MYW: "My Way Airlines",
    MZK: "AVC Airlines",
    MZL: "Aerovias Montes Azules",
    MZS: "Mahfooz Aviation",
    NAA: "National Oceanic and Atmospheric Administration",
    NAB: "Mina Airline Company",
    NAC: "Northern Air Cargo",
    NAD: "Seulawah Nad Air",
    NAE: "Nations Air Express Inc",
    NAF: "Royal Netherlands Air Force",
    NAH: "Nahanni Air Services Ltd",
    NAI: "North Adria Aviation",
    NAJ: "North American Jet Charter Group",
    NAK: "Arik Niger",
    NAL: "National Airlines",
    NAM: "Nortland Air Manitoba",
    NAN: "National Airlines",
    NAP: "Napier Air Service Inc",
    NAR: "Air Continental Inc",
    NAS: "Nasair",
    NAT: "North Atlantic Air Inc",
    NAU: "Antanik-Air",
    NAV: "Nav Flight Planning",
    NAX: "Norwegian Air Shuttle",
    NAY: "Navegacion Servicios Aereos Canarios SA",
    NAZ: "Servicios Aereos del Nazas SA de CV",
    NBE: "Novosibirsk Aviaenterprise",
    NBK: "Albarka Air",
    NBL: "Nobil Air",
    NBN: "North British Airlines",
    NBR: "Haughey Air",
    NBS: "Nimbus Aviation",
    NCA: "Nippon Cargo Airlines",
    NCB: "North Caribou Flying Service Ltd",
    NCC: "North Coast Air Services Ltd",
    NCE: "Northcoast Executive Airlines",
    NCF: "Norfolk County Flight College",
    NCG: "Nederlandse Kustwacht",
    NCH: "Chanchangi Airlines",
    NCL: "ACA-Ancargo Air Sociedade de Transporte de Carga Lda",
    NCM: "Nas Air",
    NCN: "National Airlines",
    NCO: "Natalco Air Lines",
    NCP: "Capital Airlines Limited",
    NCR: "National Air Cargo",
    NCS: "Simpson Air Ltd",
    NDA: "Northern Airways",
    NDC: "FlyNordic",
    NDF: "Namibian Defence Force",
    NDN: "Transportes Aereos Cielos Andinos",
    NDS: "Nordstree (Australia)",
    NDU: "University of North Dakota",
    NEA: "New England Airlines",
    NEB: "State of Nebraska",
    NEC: "Necon Air",
    NEE: "Northeast Airlines",
    NEF: "Nord-Flyg",
    NEG: "Linea Aerea de Fumig Aguas Negras",
    NEL: "Aero Servicios de Nuevo Laredo",
    NEN: "North-East Airlines",
    NER: "Air Newark",
    NES: "Nordeste Linhas Aereas Regionais",
    NET: "Network Aviation Services",
    NEX: "Northern Executive Aviation",
    NEZ: "New England Air Express",
    NFA: "North Flying",
    NFC: "North Atlantic Cargo",
    NFD: "Nürnberger Flugdienst",
    NFF: "Aircraft Support and Services",
    NFL: "Northaire Feight Lines",
    NFS: "Afrique CArgo Service Senegal",
    NFT: "Nefteyugansk Aviation Division",
    NGA: "Nigeria Airways",
    NGB: "Nordic Global Airlines",
    NGC: "Angoservice",
    NGE: "Angel Airlines",
    NGF: "Angel Flight America",
    NGK: "Oriental Air Bridge",
    NGO: "Air-Angol",
    NGP: "Air Nigeria",
    NGR: "Nigerian Air Force",
    NGV: "Angoavia",
    NGX: "Nigerian Global",
    NHG: "NHT Lineas Aereas",
    NHK: "Federal Aviation Administration",
    NHL: "Northumbria Helicopters",
    NHR: "Nuevo Horizonte Internacional",
    NHT: "New Heights 291",
    NHZ: "Nada Air Service",
    NIA: "Nile Air",
    NIC: "Northern Illinois Commuter",
    NID: "Aeroni",
    NIE: "Aeroejecutiva Nieto",
    NIG: "Aero Contractors",
    NIN: "Nurman Avia Indopura",
    NIR: "Norsk Flyrjeneste",
    NIS: "Nicaraguense De Aviacion",
    NIT: "Midwest Aviation",
    NJC: "Nashville Jet Charters",
    NJS: "National Jet Systems",
    NKF: "Barents AirLink",
    NKL: "Nakheel Aviation",
    NKS: "Spirit Airlines",
    NKV: "Nikolaev-Air",
    NKZ: "Aerokuzbass",
    NLA: "Neiltown Air",
    NLC: "Nelair Charters",
    NLG: "NEL Cargo",
    NLH: "Norwegian Long Haul AS",
    NLK: "Elbrus-Avia Air Enterprise",
    NLL: "Northafrican Air Transport",
    NLS: "Nationale Luchtvaartschool",
    NLT: "Newfoundland Labrador Air Transport",
    NLW: "Nile Wings Aviation Services",
    NLY: "Niki",
    NMA: "Nesma Airlines",
    NMB: "Air Namibia",
    NMD: "Nomad Aviation",
    NMI: "Pacific Wings",
    NML: "Gambia New Millennium Air",
    NOA: "Norontair",
    NOC: "Norcopter",
    NOF: "Fonnafly",
    NOH: "No. 32 (The Royal) Squadron RAF",
    NOK: "Nok Air",
    NOL: "National Overseas Airlines Company",
    NON: "Servicios Aereos Latinoamericanos",
    NOR: "Norsk Helikopter",
    NOS: "Neos",
    NOT: "Linea Aerea Costa Norte",
    NOV: "Nova Airline",
    NOW: "Royal Norwegian Air Force",
    NOY: "Noy Aviation",
    NPC: "Western Air Couriers",
    NPL: "Air Nepal International",
    NPO: "Novosibirsk Aviation Production Association",
    NPR: "Air Napier",
    NPT: "Atlantic Airlines",
    NPX: "Northeast Aviation",
    NRC: "North Sea Airways",
    NRD: "Nordic Regional",
    NRE: "Aviones Are",
    NRG: "Ross Aviation",
    NRK: "Naturelink Charter",
    NRL: "Nolinor Aviation",
    NRN: "Royal Netherland Navy",
    NRO: "Aero Rent JSC",
    NRP: "Aeronord-Grup",
    NRR: "Natureair",
    NRS: "Atlantic Richfield Company",
    NRT: "Norestair",
    NRV: "North Vancouver Airlines",
    NRW: "Polizeifliegerstaffel Nordrhein-Westfalen",
    NRX: "Norse Air Charter",
    NRZ: "Servicios Aereos Monarrez",
    NSA: "Nile Safaris Aviation",
    NSC: "Societe De Transport Aerien De Mauritanie",
    NSE: "SATENA",
    NSF: "Northamptonshire School of Flying",
    NSK: "Air Intersalonika",
    NSL: "Neric",
    NSM: "Global Jet Corporation",
    NSO: "Aerolineas Sosa",
    NSP: "Novosibirsk Aircraft Repairing Plant",
    NSR: "National Air Charter",
    NSS: "Northstar Aviation",
    NSW: "Country Connection Airlines",
    NTA: "Northern Thunderbird Air",
    NTB: "Servicios Aereos Del Norte",
    NTC: "Gibson Aviation",
    NTD: "Aero Norte",
    NTE: "Interaire",
    NTG: "Servicios Intergrales De Aviacion",
    NTH: "Hokkaido Air System",
    NTJ: "NextJet",
    NTK: "Natioanl Air Traffic Controllers Association",
    NTL: "Air Anatolia",
    NTM: "North American Airlines",
    NTR: "TNT International Aviation",
    NTS: "Cirrus Air",
    NTT: "Inter Tropic Airlines",
    NTV: "Air Inter Ivoire",
    NTW: "Nationwide Airlines",
    NTX: "Northern Jet Management",
    NUL: "Aeroservicios De Nuevo Leon",
    NUN: "Nunasi-Central Airlines",
    NVA: "Nile Valley Aviation Company",
    NVC: "NAV CANADA",
    NVD: "Nordic Solutions",
    NVG: "Novogorod Air Enterprise",
    NVI: "Avial NV Aviation Company",
    NVJ: "Fly International Airways",
    NVK: "Nizhnevartovskavia",
    NVL: "Navigator Airlines",
    NVM: "Naviera Mexicana",
    NVP: "Navegacao Aerea De Portugal",
    NVR: "Novair",
    NVY: "Royal Navy",
    NWA: "Northwest Airlines",
    NWD: "New World Jet Corporation",
    NWE: "Northwest Aero Associates",
    NWG: "Airwing",
    NWL: "North-Wright Airways",
    NWN: "Northwinds Northern",
    NWO: "Northwood Headquarters (RAF",
    NWR: "Northwest Regional Airlines",
    NWS: "NordWind Airlines",
    NWT: "Northwest Territorial Airways",
    NWW: "North West Airlines",
    NWZ: "Nationwide Airlines (Zambia)",
    NXA: "Air Next",
    NXB: "NEXT Brasil",
    NXF: "Nextflight Aviation",
    NXS: "Nexus Aviation",
    NXT: "National Express",
    NYA: "Nanyah Aviation",
    NYB: "Belgian Navy",
    NYH: "New York Helicopter",
    NYL: "Mid Airlines",
    NYS: "Nyasa Express",
    NYT: "Yeti Airlines ",
    NZA: "Nayzak Air Transport",
    NZM: "Mount Cook Airlines",
    OAA: "Oxley Aviation",
    OAB: "Orbit Airlines Azerbaijan",
    OAC: "Oriental Airlines",
    OAD: "Orscom Tourist Installations Company",
    OAE: "Omni Air International",
    OAI: "Orbit International Airlines",
    OAL: "Olympic Airlines",
    OAN: "Orbit Atlantic Airways",
    OAO: "Arkhangelsk 2 Aviation Division",
    OAP: "COAPA AIR",
    OAR: "Orbit Regional Airlines",
    OAV: "Omni - Aviacao e Tecnologia",
    OAW: "Helvetic Airways",
    OAX: "Operational Aviation Services",
    OBA: "Aerobanana",
    OBK: "Amako Airlines",
    OBS: "Orbest",
    OBT: "Orbit Airlines",
    OCA: "Aserca Airlines",
    OCE: "Heliocean",
    OCM: "O'Connor Airlines",
    OCN: "O Air",
    OCO: "Ostend Air College",
    OCS: "Ocean Sky (UK)",
    ODI: "Jonsson",
    ODM: "Pan African Airways",
    ODS: "Odessa Airlines",
    ODY: "Odyssey International",
    OEA: "Orient Thai Airlines",
    OEC: "Christophorus Flugrettungsverein",
    OED: "Orion Air Charter",
    OFA: "Helitaxi Ofavi",
    OFF: "Challenge International Airlines",
    OGI: "Aerogisa",
    OGJ: "Bakoji Airlines Services",
    OGN: "Origin Pacific Airways",
    OHK: "Oasis Hong Kong Airlines",
    OHY: "Onur Air",
    OIC: "Iwamoto Crane Co Ltd",
    OIX: "Orion-x",
    OJY: "Florida Air",
    OKA: "Okay Airways",
    OKJ: "Okada Airlines",
    OKL: "Oklahoma Department of Public Safety",
    OKP: "Okapi Airways",
    OKS: "Slok Air Gambia",
    OKT: "Soko Aviation",
    OLA: "Overland Airways",
    OLC: "Solar Cargo",
    OLE: "Operadora de Lineas Ejecutivas",
    OLO: "Soloflight",
    OLR: "Colaereos",
    OLS: "Sol Lineas Aereas",
    OLT: "Ostfriesische Lufttransport",
    OLV: "Aerolineas Olve",
    OLX: "Olimex Aerotaxi",
    OLY: "Olympic Aviation",
    OMA: "Oman Air",
    OME: "Homer Air",
    OMF: "Omniflys",
    OMG: "Aeromega",
    OML: "Organizacoes Mambra",
    OMR: "Minair",
    OMS: "Omskavia Airline",
    ONE: "Oceanair",
    ONG: "Sonnig SA",
    ONM: "Presidencia de La Republica de Guinea Ecuatorial",
    ONR: "Air One Nine",
    ONT: "Air Ontario",
    OOM: "Zoom Airlines",
    OOT: "Out Of The Blue Air Safaris",
    OPA: "Opal Air",
    OPC: "Krystel Air Charter",
    OPS: "Jet-Ops",
    OPT: "Flight Options",
    OPV: "Operadora de Veulos Ejectutivos",
    ORA: "Long Island Airlines",
    ORB: "Orenburg Airlines",
    ORC: "Orchid Airlines",
    ORD: "Orange Air Services",
    ORE: "Orange Aviation",
    ORF: "Oman Royal Flight",
    ORG: "Orenburzhie",
    ORJ: "Orange Air Sierra Leone",
    ORK: "Orca Air",
    ORL: "On Air Limited",
    ORM: "Orel State Air Enterprise",
    ORN: "Orient Airways",
    ORO: "C N Air",
    ORP: "Aerocorp",
    ORR: "Operadora Turistica Aurora",
    ORS: "Action Air",
    ORX: "Orbit Express Airlines",
    ORZ: "Zorex",
    OSA: "Open Sky Aviation",
    OSH: "Osh Avia",
    OSL: "Sosoliso Airlines",
    OSN: "Aerosan",
    OSO: "Aviapartner Limited Company",
    OSS: "Servicios Aereos Noticiosos",
    OST: "Airline Alania",
    OSW: "JetAfrica Swaziland",
    OSY: "Open Skies Consultative Commission",
    OTA: "Organizacion De Transportes Aereos",
    OTG: "One Two Go Airlines",
    OTJ: "Fly Romania",
    OTL: "South Airlines",
    OTM: "Onetime Airlines Zambia",
    OTN: "LASTP",
    OTP: "Operadora de Transportes Aereos",
    OTR: "Orient Airlines",
    OUL: "Air Atonabee",
    OVA: "Aero Nova",
    OVC: "Aerovic",
    OVE: "Aeromover",
    OVI: "Aerovias Ejecutivas",
    OVV: "Orient Air",
    OWL: "Miami Valley Aviation",
    OXE: "Oxaero",
    OXO: "Million Air",
    OYE: "Koda International",
    OZJ: "Ozjet Airlines",
    OZR: "Ozark Air Lines",
    OZU: "Hozu-Avia",
    OZW: "Skywest Airlines",
    PAA: "Pan American Airways",
    PAB: "Pacific Air Boats",
    PAC: "Polar Air Cargo",
    PAD: "Professional Express Courier Service",
    PAE: "Paisajes Espanoles",
    PAG: "Perimeter Aviation",
    PAH: "Panorama Air Tour",
    PAI: "Paradise Airways",
    PAJ: "Aliparma",
    PAK: "Pacific Alaska Airlines",
    PAL: "Philippine Airlines",
    PAM: "Phoenix Air Service",
    PAO: "Polynesian Airlines",
    PAP: "Langtry Flying Group",
    PAR: "Pacific Rim Airways",
    PAS: "Pelita Air Service",
    PAT: "Priority Air Transport",
    PAV: "VIP Avia",
    PAW: "Pointair Burkina",
    PAX: "Pan Air",
    PAZ: "Point Afrique Niger",
    PBA: "PB Air",
    PBB: "Polizeihubschrauberstaffel Brandenburg",
    PBD: "Pobeda",
    PBN: "Pacific Blue",
    PBT: "Air Parabet",
    PBU: "Air Burundi",
    PBW: "Polizeihubschrauberstaffel Baden-Wurtemberg",
    PBY: "Pearl Air Services",
    PCA: "Penya De L'Aire",
    PCC: "Perforadora Central",
    PCE: "Pace Airlines",
    PCF: "Pacific Air Express",
    PCG: "Aeropostal Cargo de Mexico",
    PCH: "Pilatus Flugzeugwerke",
    PCJ: "Pacific Jet",
    PCK: "Air Pack Express",
    PCL: "Pinnacle Air Group",
    PCM: "Westair Industries",
    PCN: "Princeton Aviation Corporation",
    PCO: "Pacific Coastal Airline",
    PCR: "PAC Air",
    PCS: "Air Palace",
    PCV: "Pacific Aviation (Australia)",
    PCW: "Trans-Pacific Orient Airways",
    PCX: "Pacific Aviation (United States)",
    PDA: "Podilia-Avia",
    PDC: "Potomac Air",
    PDD: "Phillips Alaska",
    PDF: "Pelican Air Services",
    PDG: "PLM Dollar Group",
    PDI: "Paradise Island Airways",
    PDQ: "PDQ Air Charter",
    PDR: "COMAV",
    PDT: "Piedmont Airlines (1948-1989)",
    PDV: "Elicar",
    PDY: "Pen-Avia",
    PEA: "Pan Europeenne Air Service",
    PEB: "Benders Air",
    PEC: "Pacific East Asia Cargo Airlines",
    PEI: "Panamedia",
    PEL: "Aeropelican Air Services",
    PEM: "Pem-Air",
    PEN: "Peninsula Airways",
    PER: "Pioneers Limited",
    PET: "Aerotransporte Petrolero",
    PEV: "Pegasus Aviation",
    PEX: "Pelican Express",
    PFA: "Pacific Flight Services",
    PFI: "Aerolineas Chihuahua",
    PFL: "Pacific Flier",
    PFN: "Pan African Air Services",
    PFR: "Pacificair Airlines",
    PFS: "Prairie Flying Service",
    PFT: "Air Cargo Express International",
    PFZ: "Propflight Air Services",
    PGA: "Portugalia",
    PGF: "Paragon Global Flight Support",
    PGH: "Pulkovo Aircraft Services",
    PGI: "Panagra Airways",
    PGL: "Premiair Aviation Services",
    PGP: "Perm Airlines",
    PGS: "Tauranga Aer Club",
    PGT: "Pegasus Airlines",
    PGX: "Paragon Air Express",
    PHA: "Phoenix Air Group",
    PHB: "Phoebus Apollo Aviation",
    PHC: "Petroleum Helicopters de Colombia",
    PHD: "Duncan Aviation",
    PHE: "Pawan Hans",
    PHG: "Phoenix Aviation",
    PHH: "Polizeihubschrauberstaffel Hessen",
    PHI: "Philips Aviation Services",
    PHJ: "Pim Air",
    PHL: "Phillips Michigan City Flying Service",
    PHM: "Petroleum Helicopters",
    PHN: "Phoenix Air Lines",
    PHO: "L&L Flygbildteknik",
    PHR: "Al Farana Airline",
    PHS: "Polizeihubschrauberstaffel Sachsen",
    PHT: "Pan Havacilik Ve Ticaret",
    PHU: "Pannon Air Service",
    PHV: "Phenix Aviation",
    PHW: "Ave.com",
    PHY: "Phoenix Avia",
    PIA: "Pakistan International Airlines",
    PIC: "Jetstar Pacific",
    PIE: "Air South West",
    PIF: "Aeroservicios California Pacifico",
    PIK: "Polizeihuberschrauberstaffel Sachsen-Anhalt",
    PIL: "Canada Jet Charters",
    PIM: "Pinframat",
    PIN: "Pacific International Airlines",
    PIO: "Pioneer Airlines",
    PIR: "Pamir Airways",
    PIV: "Sokol",
    PIX: "Metropix UK",
    PJA: "Private Jet Management",
    PJE: "Private Jet Expeditions",
    PJP: "Princely Jets",
    PJS: "Jet Aviation",
    PKA: "AST Pakistan Airways",
    PKP: "Mountain Air Express",
    PKR: "Pakker Avio",
    PKV: "Псковавиа",
    PKW: "Pak West Airlines",
    PKZ: "Prime Aviation",
    PLA: "Polynesian Air-Ways",
    PLB: "Polynesian Blue",
    PLC: "Police Aviation Services",
    PLF: "Polish Air Force",
    PLI: "Aeroper",
    PLL: "Air Pal",
    PLM: "Air Pullmantur",
    PLN: "Planar",
    PLR: "Northwestern Air",
    PLT: "South Carolina Aeronautics Commission",
    PLU: "Pilatus PC-12 Center De Mexico",
    PLX: "Pool Aviation",
    PLY: "Puma Linhas Aereas",
    PLZ: "Planet Airways",
    PMA: "Pan Malaysian Air Transport",
    PMC: "Primas Courier",
    PME: "Premiair Fliyng Club",
    PMK: "Palair Macedonia",
    PMM: "Primair",
    PMO: "Polar Airlines de Mexico",
    PMR: "Servicios Aereos Premier",
    PMS: "Planemaster Services",
    PMT: "PMTair",
    PMU: "Premium Aviation",
    PMV: "Polizeihubschrauberstaffel Mecklenburg-Vorpommern",
    PMW: "Paramount Airways",
    PMX: "Petroleos Mexicanos",
    PMY: "Phetchabun Airline",
    PNA: "Universal Airlines",
    PNC: "Pan-Air",
    PND: "Pond Air Express",
    PNE: "Peninter Aerea",
    PNF: "Panafrican Airways",
    PNG: "Puerto Rico National Guard",
    PNH: "Panh",
    PNL: "Aero Personal",
    PNM: "Panorama",
    PNP: "Pineapple Air",
    PNR: "PAN Air",
    PNS: "Survey Udara (Penas)",
    PNT: "Balmoral Central Contracts",
    PNU: "Aero Servicios Platinum",
    PNW: "Palestinian Airlines",
    PNX: "Imperial Airways",
    PNY: "Polish Navy",
    POA: "Portuguese Army",
    POB: "Servicios Aereos Poblanos",
    POC: "Pocono Air Lines",
    POE: "Porter Airlines",
    POF: "Police Aux Frontiers",
    POI: "BGB Air",
    POL: "Rikspolisstyrelsen",
    PON: "Portuguese Navy",
    POR: "Porteadora De Cosola",
    POT: "Polet",
    POV: "Meridian",
    POW: "Hagondale Limited",
    POY: "Apoyo Aereo",
    PPA: "Propheter Aviation",
    PPC: "Palau Asia Pacific Airlines",
    PPG: "Phoenix Air Transport",
    PPH: "Polizeihibschrauberstaffel Neidersachsen",
    PPK: "Ramp 66",
    PPL: "Air Pegasus",
    PPM: "Pacific Pearl Airways",
    PPQ: "Personas Y Pasquetes Por Air",
    PPS: "Butte Aviation",
    PPW: "Royal Phnom Penh Airways",
    PQA: "Pacific Coast Airlines",
    PQW: "PanAm World Airways",
    PRA: "Pars Aviation Service",
    PRC: "Pacific Air Charter",
    PRD: "Presidential Aviation",
    PRE: "Precision Airlines",
    PRF: "Precision Air",
    PRG: "Empresa Aero-Servicios Parrague",
    PRH: "Pro Air",
    PRI: "Aerotransportes Privados",
    PRL: "Pearl Air",
    PRM: "Prime Air",
    PRN: "Pirinair Express",
    PRO: "Propair",
    PRP: "PRT Aviation",
    PRR: "Paramount Airlines",
    PRT: "Atlantic Coast Jet",
    PRV: "Provincial Express",
    PRX: "VIP Avia",
    PRY: "Priority Air Charter",
    PRZ: "Air Paradise International",
    PSA: "Pacific Island Aviation",
    PSB: "Syrian Pearl Airlines",
    PSC: "Pascan Aviation",
    PSD: "President Airlines",
    PSE: "Aeroservicio Sipse",
    PSF: "Plymouth School of Flying",
    PSG: "Aviones Para Servirle",
    PSH: "Red Aviation",
    PSI: "Pont International Airline Services",
    PSL: "Aeroservicios Corporativos De San Luis",
    PSN: "Potosina Del Aire",
    PSP: "Publiservicios Aereos",
    PSR: "Polestar Aviation",
    PSS: "TSSKB-Progress",
    PST: "Parsa",
    PSV: "Servicios Aereos Profesionales",
    PSW: "Pskovavia",
    PSX: "Pacific Southwest Airlines",
    PSZ: "Pro Air Service",
    PTA: "Ptarmigan Airways",
    PTB: "Passaredo Transportes Aereos",
    PTC: "Patria Cargas Aereas",
    PTD: "Aero Servicio Pity",
    PTE: "Aero Copter",
    PTH: "Proteus Helicopteres",
    PTI: "Privatair",
    PTK: "Petropavlovsk-Kamchatsk Air Enterprise",
    PTL: "Providence Airline",
    PTM: "Southeastern Airways",
    PTN: "Pantanal Linhas Aéreas",
    PTO: "North West Geomatics",
    PTP: "Palau Trans Pacific Airline",
    PTQ: "Port Townsend Airways",
    PTR: "Nova Scotia Department of Lands and Forests",
    PTS: "Points of Call Airlines",
    PTT: "Promotora Industria Totolapa",
    PTV: "Puntavia Air Services",
    PTY: "Petty Transport",
    PUA: "PLUNA",
    PUE: "Aeropuelche",
    PUR: "Spurwing Airlines",
    PUT: "Aeroput",
    PUV: "Publivoo",
    PVA: "Aerotransportes Privados",
    PVG: "Privilege Style L",
    PVI: "Panavia",
    PVK: "Association of Private Pilots of Kazakhstan",
    PVL: "Professione VOlare",
    PVO: "Bearing Supplies Limited",
    PVU: "Peau Vava%Gʻ%@u",
    PVV: "Continental Airways",
    PWA: "Priester Aviation",
    PWC: "Pratt and Whitney Canada",
    PWD: "Pan Am World Airways",
    PWF: "Private Wings Flugcharter",
    PWL: "Powell Air",
    PXA: "Pecotox Air",
    PXP: "Pacific Air Transport",
    PXX: "Aroostook Aviation",
    PYA: "Pouya Air",
    PYB: "All America BOPY",
    PYC: "Aeropycsa",
    PYN: "Haverfordwest Air Charter Services",
    PYR: "Pyramid Air Lines",
    PYZ: "Players Air",
    PZA: "Aereo Taxi Paraza",
    PZR: "Sky Trek International Airlines",
    PZY: "Zapolyarie Airlines",
    QAC: "Qatar Air Cargo",
    QAF: "Qatar Amiri Flight",
    QAH: "Quick Airways Holland",
    QAJ: "Quick Air Jet Charter",
    QAQ: "Qurinea Air Service",
    QAS: "Quisqueya Airlines",
    QAT: "Aero Taxi",
    QAX: "QatXpress",
    QCC: "Qwest Commuter Corporation",
    QCL: "Air Class Lineas Aereas",
    QEA: "Aviation Consultancy Office",
    QER: "SOCHI AIR CHATER",
    QFA: "Qantas",
    QFZ: "Fars Air Qeshm",
    QGA: "Windrose Air",
    QKC: "Aero Taxi Aviation",
    QLA: "Aviation Quebec Labrador",
    QNA: "Queen Air",
    QNK: "Kabo Air",
    QNT: "Qanot Sharq",
    QNZ: "JetConnect",
    QQQ: "ENTERair",
    QRT: "4D Air",
    QSC: "African Safari Airways",
    QTR: "Qatar Airways",
    QTX: "Quantex Environmental",
    QUE: "Quebec Government Air Service",
    QUI: "Aero Quimmco",
    QVR: "Kvadro Aero",
    QWA: "Qwestair",
    QWL: "Qwila Air",
    QXE: "Horizon Air",
    RAA: "Rynes Aviation",
    RAB: "Rainbow Air (RAI)",
    RAC: "Icar Air",
    RAD: "Alada",
    RAE: "Régional",
    RAF: "Farnas Aviation Services",
    RAG: "Regio Air",
    RAH: "Regent Air",
    RAI: "Aerotur-Air",
    RAJ: "Raji Airlines",
    RAK: "Riga Airclub",
    RAL: "Roswell Airlines",
    RAM: "Royal Air Maroc",
    RAN: "Renan",
    RAP: "Air Center Helicopters",
    RAQ: "Rath Aviaton",
    RAR: "Air Rarotonga",
    RAS: "Jim Ratliff Air Service",
    RAT: "Chief Rat Flight Services",
    RAU: "Uganda Royal Airways",
    RAV: "Reed Aviation",
    RAW: "Royal Airways",
    RAX: "Royal Air Freight",
    RAY: "Rainbow Air Canada",
    RAZ: "Rijnmond Air Services",
    RBA: "Royal Brunei Airlines",
    RBB: "Rabbit-Air",
    RBC: "Republicair",
    RBD: "Trans World Express",
    RBE: "Aur Rum Benin",
    RBG: "Air Arabia Egypt",
    RBH: "Regal Bahamas International Airways",
    RBJ: "Aeroserivios Del Bajio",
    RBN: "Red Baron Aviation",
    RBT: "Robinton Aero",
    RBU: "Airbus France",
    RBV: "Air Roberval",
    RBW: "Shandong Airlines Rainbow Jet",
    RBX: "Arubaexel",
    RBY: "Vision Airlines (V2)",
    RCA: "Richland Aviation",
    RCB: "Real Aero Club De Baleares",
    RCC: "Air Charters Eelde",
    RCD: "Real Aeroclub De Ternerife",
    RCE: "Aerocer",
    RCF: "Aeroflot-Cargo",
    RCG: "Royal Air Cargo",
    RCH: "Air Mobility Command",
    RCI: "Air Cassai",
    RCJ: "Raytheon Corporate Jets",
    RCK: "Faroejet",
    RCO: "Aero Renta De Coahuila",
    RCP: "Aerocorp",
    RCQ: "Aerolineas Regionales",
    RCT: "Ryan Air Service",
    RCU: "Atlantic S.L.",
    RCX: "Air Service Center",
    RCY: "Package Express",
    RDD: "Adygeya Airlines",
    RDE: "II Lione Alato Arl",
    RDK: "Irish Air Transport",
    RDL: "Roadair Lines",
    RDM: "Air Ada",
    RDN: "Dinar",
    RDP: "Eurojet Romania",
    RDR: "Goodridge (UK) Limited",
    RDS: "Rhoades Aviation",
    RDV: "Red Sea Aviation",
    RDW: "Valair AG (Helicoptere)",
    RDZ: "Rodze Air",
    REA: "Aer Arann",
    REB: "Rebus",
    REC: "Trans Reco",
    RED: "International Committee of the Red Cross",
    REF: "Reef Air",
    REG: "Regional Air Services",
    REI: "Ray Aviation",
    REJ: "SA Airlink Regional",
    REK: "Reem Air",
    REL: "Reliance Aviation",
    REN: "Aero-Rent",
    REO: "Rio Airways",
    REP: "Regional Paraguaya",
    RER: "Servicio Aereo Regional Regair",
    RES: "Australian Maritime Safety Authority",
    REU: "Air Austral",
    REW: "Regional Air Express",
    REX: "Ram Air Freight",
    REY: "Aero-Rey",
    REZ: "Cal-West Aviation",
    RFA: "Raleigh Flying Service",
    RFC: "Aero Africa",
    RFD: "Aerotransportes Rafilher",
    RFF: "Russian Federation Air Force",
    RFI: "National Air Traffic Services",
    RFJ: "Royal Falcon",
    RFL: "Interfly",
    RFR: "Royal Air Force",
    RFS: "Rossair",
    RFT: "Scoala Superioara De Aviatie Civila",
    RFX: "J P Hunt Air Carriers",
    RGA: "Royal Ghanaian Airlines",
    RGC: "Servicios Aereos Regiomontanos",
    RGG: "TransRussiaAirlines",
    RGL: "Regional Air Lines",
    RGM: "Rangemile Limited",
    RGN: "Cygnus Air",
    RGO: "Argo",
    RGP: "River State Government of Nigeria",
    RGR: "Region Air",
    RGS: "Renown Aviation",
    RGT: "Airbourne School of Flying",
    RGY: "Regency Airlines",
    RHA: "Robin Hood Aviation",
    RHC: "Redhill Aviation",
    RHD: "Bond Air Services",
    RHL: "Air Archipels",
    RIA: "Rich International Airways",
    RIC: "Richardson's Airway",
    RID: "Ridder Avia",
    RIF: "Aviation Ministry of the Interior of the Russian Federation",
    RIL: "Regional Air",
    RIM: "Rimrock Airlines",
    RIS: "Aeris Gestion",
    RIT: "Asian Spirit",
    RIU: "Riau Airlines",
    RJA: "Royal Jordanian",
    RJD: "Rotana Jet",
    RJM: "Millen Corporation",
    RJS: "Aeroservicios Jet",
    RJT: "RA Jet Aeroservicios",
    RJZ: "Royal Jordanian Air Force",
    RKA: "Air Afrique",
    RKC: "DAS Airlines",
    RKH: "Royal Khmer Airlines",
    RKM: "RAK Airways",
    RKT: "Rotormotion",
    RKW: "Rockwell Collins Avionics",
    RLA: "Airlinair",
    RLE: "Rico Linhas A",
    RLK: "Air Nelson",
    RLL: "Air Leone",
    RLM: "Royal American Airways",
    RLN: "Aero Lanka",
    RLR: "Business Airfreight",
    RLS: "S-Air",
    RLT: "Reliant Airlines",
    RLU: "Rusline",
    RLV: "Real Aviation",
    RLX: "Go2Sky",
    RLZ: "Air Alize",
    RMA: "Rocky Mountain Airways",
    RMD: "Air Amder",
    RME: "Armenian Airlines",
    RMF: "Royal Malaysian Air Force",
    RMG: "Rumugu Air & Space Nigeria",
    RMI: "Point Airlines",
    RMK: "Simrik Airlines",
    RML: "Armenian International Airways",
    RMO: "Arm-Aero",
    RMP: "Servicios De Rampa Y Mostrador",
    RMS: "Tas Aviation",
    RMT: "Ram Aircraft Corporation",
    RMU: "C.S.P.",
    RMV: "Romavia",
    RMX: "Air Max",
    RNA: "Nepal Airlines",
    RNB: "Rosneft-Baltika",
    RND: "Rutland Aviation",
    RNE: "Air Salone",
    RNG: "Ornage Aircraft Leasing",
    RNM: "Aeronem Air Cargo",
    RNR: "Air Cargo Masters",
    RNS: "Ronso",
    RNV: "Armavia",
    RNX: "1Time Airline",
    RNY: "Rainbow Air US",
    ROA: "Reno Air",
    ROB: "ROYAL BRITAIN",
    ROC: "Rocky Mountain Airlines",
    ROD: "Aerodan",
    ROE: "Aeroeste",
    ROF: "Romanian Air Force",
    ROG: "Fundació Rego",
    ROH: "Aero Gen",
    ROI: "Avior Airlines",
    ROJ: "Royal Jet",
    ROK: "National Airlines",
    ROL: "Aeroel Airways",
    ROM: "Aeromar",
    RON: "Nauru Air Corporation",
    ROO: "Aeroitalia",
    ROP: "Royal Oman Police",
    ROR: "Roraima Airways",
    ROS: "Rossair Europe",
    ROT: "Tarom",
    ROU: "Transportes Aereos I.R. Crusoe",
    ROV: "Rover Airways International",
    ROX: "Roblex Aviation",
    ROY: "Conifair Aviation",
    RPA: "Republic Airlines",
    RPB: "AeroRep",
    RPC: "Aerolineas Del Pacifico",
    RPH: "Republic Express Airlines",
    RPK: "Royal Airlines",
    RPO: "Rainbow Air Polynesia",
    RPS: "Global Air Charter",
    RPX: "BAC Express Airlines",
    RQX: "Air Engiadina-BRN",
    RRA: "Royal Rwanda Airlines",
    RRC: "Aero Roca",
    RRE: "Aerotransportes Internacionales De Torreon",
    RRF: "Royal Air Force",
    RRJ: "AirRussia",
    RRL: "Rolls-Royce Limited",
    RRM: "Acvila Air-Romanian Carrier",
    RRR: "Royal Air Force",
    RRS: "Boscombe Down DERA (Formation)",
    RRT: "Transportes Aereos Sierra",
    RRU: "Cirrus",
    RRV: "Mombasa Air Safari",
    RRY: "Tbilisi Aviation University",
    RRZ: "Rollright Aviation",
    RSA: "Elisra Airlines",
    RSB: "Rubystar",
    RSC: "Aerolineas Ejecutivas Tarascas",
    RSD: "Russia State Transport",
    RSE: "SNAS Aviation",
    RSF: "Royal Saudi Air Force",
    RSH: "Air Sahara",
    RSI: "Air Sunshine",
    RSJ: "RusJet",
    RSK: "DSWA",
    RSL: "Rio Sul Servi",
    RSM: "Air Somalia",
    RSN: "Royal Swazi National Airways",
    RSO: "Aero Asia International",
    RSP: "Jet Suite",
    RSQ: "International SOS WIndhoek",
    RSR: "Aero-Service",
    RSS: "Rossair",
    RST: "Resort Air",
    RSU: "Aerosur",
    RSV: "Red Sky Ventures",
    RSY: "I-Fly",
    RTE: "Aeronorte",
    RTH: "Artis",
    RTL: "Rheintalflug",
    RTM: "Trans Am Compania",
    RTN: "Raytheon Aircraft Company",
    RTO: "Arhabaev Tourism Airlines",
    RTQ: "Air Turquoise",
    RTR: "Rotatur",
    RTS: "Relief Transport Services",
    RTU: "Aerotucan",
    RTV: "Nortavia",
    RUA: "Rwanda Airlines",
    RUC: "Rutas Aereas",
    RUD: "Air Anastasia",
    RUE: "Rainbow Air Euro",
    RUH: "Rusich-T",
    RUM: "Air Rum",
    RUN: "ACT Havayollari",
    RUR: "Rusaero",
    RUS: "Cirrus Airlines",
    RUZ: "Rusuertol",
    RVC: "Richards Aviation",
    RVE: "Airventure",
    RVI: "Aero Servicios",
    RVL: "Airvallee",
    RVM: "River Ministries Air Charter",
    RVN: "Raven Air",
    RVP: "Air VIP",
    RVR: "Raven Air",
    RVT: "Aircompany Veteran",
    RVV: "Reeve Aleutian Airways",
    RWA: "Rwanda Airways",
    RWB: "Alliance Express Rwanda",
    RWC: "Arrow Ecuador Arrowec",
    RWD: "Rwandair Express",
    RWE: "Royal West Airlines",
    RWG: "C&M Airways",
    RWL: "RWL Luftfahrtgesellschaft",
    RWS: "Air Whitsunday",
    RWW: "Fly Europa",
    RWY: "Aerogroup 98 Limited",
    RWZ: "Red Wings",
    RXA: "Regional Express",
    RXP: "Royal Aviation Express",
    RXR: "REXAIR VIRTUEL",
    RXT: "Aeroxtra",
    RXX: "Express Air Charter",
    RYA: "Ryan Air Services",
    RYB: "Royal Bahrain Airlines",
    RYL: "Royal Aruban Airline",
    RYN: "Ryan International Airlines",
    RYR: "Ryanair",
    RYS: "Royal Sky",
    RYT: "Raya Jet",
    RYZ: "Ryazan State Air Enterprise",
    RZA: "Jet Fighter Flights",
    RZL: "Aero Zambia",
    RZN: "Aero Zano",
    RZO: "SATA International",
    RZR: "Zephyr Express",
    RZV: "Z-Avia",
    RZZ: "Anoka Air Charter",
    SAA: "South African Airways",
    SAB: "Sky Way Air",
    SAC: "SASCO Airlines",
    SAE: "SOCHI AIR EXPRESS",
    SAF: "Republic of Singapore Air Force",
    SAG: "SOS Flygambulans",
    SAH: "Sayakhat Airlines",
    SAI: "Shaheen Air International",
    SAJ: "Golden Eagle Air Services (FAA)",
    SAK: "Red Arrows Display Squadron",
    SAL: "Spike Airlines",
    SAM: "SAM Colombia",
    SAN: "Servicios Aereos Nacionales (SAN)",
    SAO: "Sahel Aviation Service",
    SAP: "Avia Jaynar",
    SAQ: "Springbank Aviation",
    SAS: "Scandinavian Airlines System",
    SAT: "SATA Air Acores",
    SAU: "United Aviation Services",
    SAV: "Samal Air",
    SAW: "Sham Wing Airlines",
    SAX: "Sabah Air",
    SAY: "ScotAirways",
    SAZ: "Swiss Air-Ambulance",
    SBA: "STA-MALI",
    SBB: "Steinman Aviation",
    SBC: "Emoyeni Air Charter",
    SBD: "Snowbird Airlines",
    SBF: "Seven Bar Flying Service",
    SBG: "Sabre Incorporated",
    SBH: "Aerosaab",
    SBI: "S7 Airlines",
    SBJ: "Trans Sahara Air",
    SBK: "Air Srpska",
    SBL: "Sobel Airlines of Ghana",
    SBM: "Sky Bahamas",
    SBO: "Stabo Air",
    SBQ: "Smithkline Beacham Clinical Labs",
    SBR: "Saber Aviation",
    SBS: "Seaborne Airlines",
    SBT: "Taftan Airlines",
    SBU: "Saint Barth Commuter",
    SBX: "North Star Air Cargo",
    SBZ: "Scibe Airlift",
    SCA: "South Central Air",
    SCC: "Seacoast Airlines",
    SCD: "Associated Aviation",
    SCE: "Scenic Airlines",
    SCF: "Socofer",
    SCH: "CHC Airways",
    SCI: "Servicios Aereos San Cristobal",
    SCJ: "Business Jet Sweden",
    SCK: "Sky Cam",
    SCL: "Switfair Cargo",
    SCM: "American Jet International",
    SCN: "South American Airlines",
    SCO: "Scoot",
    SCP: "Scorpio",
    SCR: "Si-Chang Flying Service",
    SCS: "South African Non Scheduled Airways",
    SCT: "SAAB-Aircraft",
    SCU: "Air Scorpio",
    SCV: "Servicios Aereos Del Centro",
    SCW: "Malmo Aviation",
    SCX: "Sun Country Airlines",
    SDA: "St. Andrews Airways",
    SDB: "Sukhoi Design Bureau Company",
    SDC: "Sunrise Airlines",
    SDD: "Skymaster Air Taxi",
    SDF: "Sundorph Aeronautical Corporation",
    SDH: "Servicio De Helicopteros",
    SDI: "San Dima Air",
    SDJ: "Club 328",
    SDK: "SADELCA - Sociedad Aerea Del Caqueta",
    SDL: "Skydrift",
    SDM: "Rossiya-Russian Airlines",
    SDN: "Spirit of Africa Airlines",
    SDO: "Air Santo Domingo",
    SDP: "Aero Sudpacifico",
    SDR: "City Airline",
    SDS: "United Kingdom Civil Aviation Authority",
    SDU: "Sud Airlines",
    SDV: "Servicios Aereos Del Vaupes",
    SDX: "Servicio Tecnico Aero De Mexico",
    SDY: "Island Express",
    SDZ: "Sudan Pezetel for Aviation",
    SEA: "Southeast Air",
    SEB: "Servicios Aereos Luce",
    SEC: "3D Aviation",
    SED: "Sedona Air Center",
    SEE: "Shaheen Air Cargo",
    SEF: "Aero Servicios Ejecutivas Del Pacifico",
    SEG: "Eagle International",
    SEH: "Sky Express",
    SEI: "Transportes Aereos Sierra Madre",
    SEJ: "Spicejet",
    SEK: "Skyjet",
    SEL: "Sentel Corporation",
    SEM: "Cape Central Airways",
    SEN: "Sevenair",
    SEO: "Selcon Airlines",
    SEQ: "Sky Eyes",
    SER: "Aero California",
    SES: "Servicio Aereo Saltillo",
    SET: "SAETA",
    SEU: "XL Airways France",
    SEV: "Serair Transworld Press",
    SEY: "Air Seychelles",
    SFA: "SEFA",
    SFB: "Air Sofia",
    SFC: "Shuswap Flight Centre",
    SFE: "Sefofane Air Charters",
    SFF: "Safewings Aviation Company",
    SFG: "Sun Freight Logistics",
    SFJ: "Star Flyer",
    SFL: "Southflight Aviation",
    SFM: "A-Safar Air Services",
    SFN: "Safiran Airlines",
    SFP: "Safe Air",
    SFR: "Safair",
    SFS: "Southern Frontier Air Transport",
    SFT: "Skyfreight",
    SFU: "Solent Flight",
    SFX: "S.K. Logistics",
    SFY: "Gulf Flite Center",
    SGA: "Air Saigon",
    SGB: "Sky King",
    SGC: "Southern Right Air Charter",
    SGD: "Sky Gate International Aviation",
    SGF: "STAC Swiss Government Flights",
    SGG: "Senegal Airlines",
    SGH: "Servisair",
    SGI: "Servicios Aereos Agricolas",
    SGK: "Skyward Aviation",
    SGM: "Sky Aircraft Service",
    SGN: "Siam GA",
    SGP: "Sagolair Transportes Ejecutivos",
    SGR: "Eastwind Airlines",
    SGS: "Saskatchewan Government Executive Air Service",
    SGT: "Skygate",
    SGU: "Samgau",
    SGV: "Aerosegovia",
    SGX: "Saga Airlines",
    SGY: "Skagway Air Service",
    SHA: "Sharp Airlines",
    SHB: "Shabair",
    SHC: "Sky Harbor Air Service",
    SHD: "Sahara Airlines",
    SHE: "Shell Aircraft",
    SHF: "Royal Air Force",
    SHG: "Shoprite Group",
    SHH: "Airshare Holdings",
    SHI: "Seoul Air International",
    SHJ: "Sharjah Ruler's Flight",
    SHK: "Shorouk Air",
    SHL: "Samson Aviation",
    SHM: "Sheltam Aviation",
    SHN: "Shaheen Airport Services",
    SHO: "Sheremetyevo-Cargo",
    SHP: "Service Aerien Francais",
    SHQ: "Shanghai Airlines Cargo",
    SHR: "Shooter Air Courier",
    SHS: "Shura Air Transport Services",
    SHT: "British Airways Shuttle",
    SHU: "Sakhalinskie Aviatrassy (SAT)",
    SHV: "Shavano Air",
    SHW: "Shawnee Airline",
    SHX: "Slim Aviation Services",
    SHY: "Sky Airlines",
    SIA: "Singapore Airlines",
    SIB: "Sibaviatrans",
    SIE: "Sierra Express",
    SIH: "Skynet Airlines",
    SII: "Aero Servicios Ejecutivos Internacionales",
    SIJ: "Seco International",
    SIL: "Servicios Aeronauticos Integrales",
    SIM: "Star Air",
    SIO: "Sirio",
    SIP: "Air Spirit",
    SIR: "Salair",
    SIS: "Saber Airlines",
    SIT: "SITA",
    SIV: "Slovenian Armed Forces",
    SIW: "Sirio Executive",
    SIY: "Aerosiyusa",
    SIZ: "Aero Silza",
    SJA: "Servicios Aereos Especiales De Jalisco",
    SJC: "Servicios Ejecutivos Continental",
    SJE: "Sunair 2001",
    SJJ: "Spirit Aviation",
    SJL: "Servicios Especiales Del Pacifico Jalisco",
    SJM: "Svyaz Rossiya",
    SJN: "Air San Juan",
    SJO: "Spring Airlines Japan",
    SJS: "Southjet",
    SJT: "Swiss Jet",
    SJU: "Skyjet Airlines",
    SJY: "Sriwijaya Air",
    SKA: "Rio Air Express",
    SKB: "Skybus Airlines",
    SKC: "Skymaster Air Lines",
    SKD: "Sky Harbor Air Service",
    SKE: "Sky Tours",
    SKF: "Skycraft",
    SKG: "Skycraft Air Transport",
    SKH: "British Sky Broadcasting",
    SKI: "Skyking",
    SKK: "Skylink Aviation",
    SKL: "Skycharter (Malton)",
    SKM: "Fayetteville Flying Service and Scheduled Skyways System",
    SKN: "Skyline Aviation Services",
    SKO: "Scottish Airways Flyers",
    SKP: "Aero-North Aviation Services",
    SKQ: "Labcorp",
    SKR: "Skyscapes Air Charters",
    SKS: "Sky Service",
    SKT: "Skystar Airways",
    SKU: "Sky Airline",
    SKV: "Sky Regional",
    SKW: "SkyWest",
    SKX: "Skyways Express",
    SKY: "Skymark Airlines",
    SKZ: "Skyway Enterprises",
    SLA: "Sierra National Airlines",
    SLB: "Slok Air",
    SLC: "Salsa d\\\\'Haiti",
    SLD: "Silver Air",
    SLE: "Streamline",
    SLF: "Starfly",
    SLG: "Saskatchewan Government",
    SLH: "Silverhawk Aviation",
    SLI: "Aerolitoral",
    SLK: "SilkAir",
    SLL: "Slovak Airlines",
    SLM: "Surinam Airways",
    SLN: "Sloane Aviation",
    SLO: "Edgartown Air",
    SLP: "Salpa Aviation",
    SLS: "Servicios Aereos Slainte",
    SLU: "Avio Sluzba",
    SLV: "Stella Aviation",
    SLW: "Salama Airlines Nigeria",
    SLX: "Sete Linhas Aereas",
    SLY: "Sky Line for Air Services",
    SLZ: "Super Luza",
    SMA: "SMA Airlines",
    SMC: "Sabang Merauke Raya Air Charter",
    SMD: "Servicios Aereos La Marquesa",
    SME: "Semos",
    SMF: "Smalandsflyg",
    SMG: "RAF St Mawgan Search and Rescue",
    SMH: "Smithair",
    SMI: "Aero Sami",
    SMJ: "Avient Aviation",
    SMK: "Semeyavia",
    SML: "Smith Air (1976)",
    SMM: "Summit Airlines",
    SMQ: "Samar Air",
    SMR: "Somon Air",
    SMS: "Linhas Aereas Santomenses",
    SMT: "Skyline",
    SMW: "Carpatair Flight Training",
    SMX: "Alitalia Express",
    SMY: "Sama Airlines",
    SMZ: "RAF Scampton",
    SNA: "Senator Aviation Charter",
    SNB: "Sterling Airlines",
    SNC: "Air Cargo Carriers",
    SND: "Air Samarkand",
    SNE: "Servicios Aereos De Nicaragua (SANSA)",
    SNF: "Shans Air",
    SNG: "Air Senegal International",
    SNH: "Senair Services",
    SNI: "Savanah Airlines",
    SNJ: "Skynet Asia Airways",
    SNK: "Southeast Airlines (Sun Jet International)",
    SNL: "Soonair Lines",
    SNM: "Servizi Aerei",
    SNO: "Delta Air Charter",
    SNP: "Sun Pacific International",
    SNQ: "Sun Quest Executive Air Charter",
    SNS: "Societe Centrafricaine De Transport Aerien",
    SNT: "Suncoast Aviation",
    SNU: "Snunit Aviation",
    SNV: "Sudanese States Aviation",
    SNW: "Sun West Airlines",
    SNX: "Sun Air Aviation Services",
    SNY: "Air Sandy",
    SOA: "Southern Air Charter",
    SOB: "Stabo Freight",
    SOC: "Southern Cargo Air Lines",
    SOD: "Aerolineas Sol",
    SOE: "Air Soleil",
    SOG: "Aero Soga",
    SOH: "Southern Ohio Aviation Sales",
    SOI: "Southern Aviation",
    SOL: "Solomon Airlines",
    SOM: "Somali Airlines",
    SON: "Sunshine Air Tours",
    SOO: "Southern Air",
    SOP: "Solinair",
    SOR: "Sonair Servico Aereo",
    SOT: "Southeast Correct Craft",
    SOU: "Southern Airways",
    SOV: "Saratov Aviation Division",
    SOW: "Sowind Air",
    SOX: "Solid Air",
    SOY: "Island Aviation",
    SOZ: "Sat Airlines",
    SPA: "Sierra Pacific Airlines",
    SPB: "Springbok Classic Air",
    SPC: "Skyworld Airlines",
    SPD: "Airspeed Aviation",
    SPE: "Sprague Electric Company",
    SPF: "Space World Airline",
    SPG: "Springdale Air Service",
    SPH: "Sapphire Executive Air",
    SPI: "South Pacific Island Airways",
    SPJ: "Air Service",
    SPK: "Diamond Aviation",
    SPL: "Servicios Corporativos Aereos De La Laguna",
    SPM: "Air Saint Pierre",
    SPN: "Skorpion Air",
    SPO: "Aeroservicios Ejecutivos Del Pacifico",
    SPP: "Sapphire Aviation",
    SPQ: "Servicios Aereos Palenque",
    SPR: "Provincial Airlines",
    SPS: "SPASA",
    SPT: "Speed Aviation",
    SPU: "Southeast Airmotive",
    SPV: "Servicios Privados De Aviacion",
    SPW: "Speedwings",
    SPX: "Service People Gesselschaft fur Charter und Service",
    SPY: "Asian Aerospace Service",
    SPZ: "Airworld",
    SQA: "Slovak National Aeroclub",
    SQC: "Singapore Airlines Cargo",
    SQF: "Slovak Air Force",
    SQH: "SeaPort Airlines",
    SQL: "Servicos De Alquiler",
    SQR: "Alsaqer Aviation",
    SRA: "Sair Aviation",
    SRB: "Solar Air",
    SRC: "Searca",
    SRD: "Search and Rescue 22",
    SRF: "Transportes Aereos San Rafael",
    SRG: "Search and Rescue 202",
    SRH: "Siem Reap Airways",
    SRI: "Air Safaris and Services",
    SRJ: "C Air Jet Airlines",
    SRK: "Sky Work Airlines",
    SRL: "Swedline Express",
    SRN: "Sprintair",
    SRO: "Servicios Aereos Ejecutivos Saereo",
    SRP: "Polizeihubschrafterstaffel Rheinland-Pfalz",
    SRQ: "South East Asian Airlines",
    SRR: "Star Air",
    SRS: "Selkirk Remote Sensing",
    SRT: "Trans Asian Airlines",
    SRU: "Star Up",
    SRV: "Aero Servicio Corporativo",
    SRW: "Sarit Airlines",
    SRX: "Sierra Expressway Airlines",
    SRY: "ViaAir",
    SRZ: "Strato Air Services",
    SSA: "All America US",
    SSB: "Sasair",
    SSC: "Southern Seaplane",
    SSD: "Star Service International",
    SSE: "Servicios Aereos Sunset",
    SSF: "Severstal",
    SSG: "Slovak Government Flying Service",
    SSH: "Heritiage Flight (Valley Air Services)",
    SSI: "Vision Airlines",
    SSK: "Skystar International",
    SSL: "Air Sultan",
    SSM: "Aero 1 Pro-Jet",
    SSN: "Airquarius Air Charter",
    SSO: "Special Scope Limited",
    SSP: "Starspeed",
    SSQ: "Sunstate Airlines",
    SSS: "SAESA",
    SST: "Sunwest Airlines",
    SSU: "SASCA",
    SSV: "Skyservice Airlines",
    SSW: "Streamline Aviation",
    SSX: "Lynx Aviation (L3/SSX)",
    SSY: "Sky Aviation",
    SSZ: "Specsavers Aviation",
    STA: "Star Aviation",
    STB: "Status-Alpha Airline",
    STC: "Stadium City Limited",
    STD: "Servicios De Aerotransportacion De Aguascalientes",
    STE: "Semitool Europe",
    STF: "SFT-Sudanese Flight",
    STG: "Sedalia",
    STH: "South-Airlines",
    STI: "Sontair",
    STJ: "Sella Aviation",
    STK: "Aeropac",
    STL: "Stapleford Flight Centre",
    STN: "RAF St Athan",
    STO: "Streamline Ops",
    STP: "STP Airways",
    STQ: "Star Air",
    STR: "Red Star",
    STS: "Starling Airlines Spain",
    STT: "Air St. Thomas",
    STU: "Servicios de Transportes A",
    STV: "Southern Aviation",
    STW: "South West Air Corporation",
    STX: "Stars Away Aviation",
    STY: "Styrian Airways",
    SUA: "Silesia Air",
    SUB: "Suburban Air Freight",
    SUD: "Sudan Airways",
    SUE: "Aerolineas Del Sureste",
    SUF: "Sun Air (Fiji)",
    SUG: "Sunu Air",
    SUH: "Sun Light",
    SUI: "Swiss Air Force",
    SUK: "Superior Aviation Services",
    SUM: "State Unitary Air Enterprise",
    SUN: "Antillana De Navegacion Aerea",
    SUO: "Aeroservicios De San Luis",
    SUP: "Aerosuper",
    SUR: "Sun Air",
    SUS: "Sun Air of Scandinavia",
    SUT: "Sistemas Aeronauuticos 2000",
    SUU: "Star West Aviation",
    SUV: "Sundance Air",
    SUW: "Interavia Airlines",
    SUY: "Aerial Surveys (1980) Limited",
    SVA: "Saudi Arabian Airlines",
    SVD: "St. Vincent Grenadines Air (1990)",
    SVE: "Aero Servicios Expecializados",
    SVF: "Swedish Armed Forces",
    SVG: "SVG Air",
    SVH: "Sterling Helicopters",
    SVI: "Servicios De Transporte Aereo",
    SVJ: "Silver Air",
    SVK: "Air Slovakia",
    SVL: "Sevastopol-Avia",
    SVM: "Aeroservicios Monterrey",
    SVN: "Savanair (Angola)",
    SVO: "Servicios Aeronauticos De Oriente",
    SVR: "Ural Airlines",
    SVS: "Servicios Aereos Saar",
    SVT: "Seven Four Eight Air Services",
    SVW: "Global Jet Luxembourg",
    SVX: "Security Aviation",
    SVY: "Cooper Aerial Surveys",
    SWA: "Southwest Airlines",
    SWB: "Swissboogie Parapro",
    SWC: "South West Air",
    SWD: "Southern Winds Airlines",
    SWE: "Swedeways",
    SWF: "Galair International",
    SWG: "Sunwing Airlines",
    SWH: "Adler Aviation",
    SWI: "Sunworld Airlines",
    SWJ: "Stateswest Airlines",
    SWK: "General Aerospace",
    SWL: "Trans Jet Airways",
    SWM: "Sky Angkor Airlines (ZA)",
    SWN: "West Air Sweden",
    SWO: "Surninam International Victory Airline",
    SWP: "Star Work Sky",
    SWQ: "Swift Air (Interstate Equipment Leasing)",
    SWR: "Swiss International Air Lines",
    SWS: "Sunwest Aviation (Lindquist Investment)",
    SWT: "Swiftair",
    SWU: "Swiss European Air Lines",
    SWV: "Swe Fly",
    SWW: "Shovkoviy Shlyah",
    SWX: "Swazi Express Airways",
    SWY: "Sky Jet",
    SWZ: "Servair",
    SXA: "Southern Cross Aviation",
    SXC: "Sky Exec Aviation Services",
    SXE: "Southeast Express Airlines",
    SXM: "Servicios Aereos Sepecializados Mexicanos",
    SXP: "Sky Express",
    SXR: "Sky Express",
    SXS: "SunExpress",
    SXT: "Servicios De Taxi Aereo",
    SXX: "Satellite Aero",
    SXY: "Safari Express Cargo",
    SYA: "Skyways",
    SYC: "Systec 2000",
    SYE: "Sheba Aviation",
    SYF: "Sky One Express Airlines",
    SYG: "Synergy Aviation",
    SYI: "Sonalysts",
    SYJ: "Slate Falls Airways",
    SYK: "Satsair",
    SYL: "Aircompany Yakutia",
    SYN: "Syncrude Canada",
    SYR: "Syrian Arab Airlines",
    SYS: "Shawbury Flying Training Unit",
    SYT: "Aerosud Aviation",
    SYV: "Special Aviation Systems",
    SYX: "Skywalk Airlines",
    SYY: "South African Historic Flight",
    SZA: "Aerolineas De El Salvador",
    SZB: "Aerolineas heredas santa maria",
    SZL: "Swaziland Airlink",
    SZT: "Servicios Aeronauticos Z",
    SZZ: "SUR Lineas Aereas",
    TAA: "Aeroservicios de La Costa",
    TAC: "Turbot Air Cargo",
    TAD: "Tranporte Aereo Dominicano",
    TAE: "TAME",
    TAG: "TAG Aviation USA",
    TAH: "Air Moorea",
    TAJ: "Tunisavia",
    TAK: "Tatarstan Airlines",
    TAL: "Talair",
    TAM: "TAM Brazilian Airlines",
    TAN: "Zanair",
    TAO: "Aeromar",
    TAP: "TAP Portugal",
    TAQ: "Taunus Air Gmbh",
    TAR: "Tunisair",
    TAS: "Lotus Air",
    TAT: "Grupo TACA",
    TAU: "Transportes Aereos Tauro",
    TAV: "Compania de Servicios Aereos Tavisa",
    TAX: "Travelair",
    TAY: "TNT Airways",
    TBA: "Transbrasil",
    TBC: "Turbine Air Cargo UK",
    TBD: "Thunderbird Tours",
    TBG: "Tropical Airways",
    TBH: "Trinity Air Bahamas",
    TBI: "TAB Express International",
    TBL: "Aerotrebol",
    TBM: "Taban Air Lines",
    TBN: "Teebah Airlines",
    TBO: "Aero Taxi de Los Cabos",
    TBR: "Tubelair",
    TBS: "Servicios Aereos Tribasa",
    TBX: "Tobago Express",
    TBZ: "TrasBrasil",
    TCA: "Tropican Air Services",
    TCB: "Transporte del Caribe",
    TCC: "Trans Continental Airlines",
    TCD: "Tchad Airlines",
    TCE: "Trans-Colorado Airlines",
    TCF: "Shuttle America",
    TCG: "Thai Air Cargo",
    TCH: "Transcontinental Air",
    TCI: "Air Turks and Caicos",
    TCL: "Coastal Air Transport",
    TCM: "Teledyne Continental Motors",
    TCN: "Trans Continental Airlines",
    TCO: "Aerotranscolombina de Carga",
    TCP: "Transcorp Airways",
    TCR: "Lanaes Aereas Trans Costa Rica",
    TCT: "Transcontinental Sur",
    TCU: "Transglobal Airways Corporation",
    TCV: "TACV",
    TCW: "Thomas Cook Airlines",
    TCX: "Thomas Cook Airlines",
    TCY: "Twin Cities Air Service",
    TDA: "TDA Toa Domestic Airlines",
    TDB: "Welch Aviation",
    TDC: "Tadair",
    TDE: "Tellavia / Flight One",
    TDG: "Air Cargo Express",
    TDI: "Transportes Aereos de Ixtlan",
    TDK: "Transavia Denmark",
    TDM: "Tandem Aero",
    TDO: "TRADO",
    TDR: "Trade Air",
    TDT: "Atlas Helicopters",
    TDV: "Taxi Aero Nacional Del Evora",
    TDX: "Tradewinds Airlines",
    TDY: "Air Today",
    TEA: "Executive Turbine Aviation",
    TEB: "Tenir Airlines",
    TED: "Aero Servicios Azteca",
    TEE: "West Freugh DTEO",
    TEF: "Tecnicas Fotograficas",
    TEH: "Tempelhof Airways",
    TEL: "Telford Aviation",
    TEM: "Tech-Mont Helicopter Company",
    TEN: "Tennessee Airways",
    TEP: "Transeuropean Airlines",
    TER: "Territorial Airlines",
    TES: "TAES Transportes Aereos de El Salvador",
    TET: "Tepavia-Trans Airlines",
    TEX: "Catex",
    TEZ: "Tez Jet Airlines",
    TFA: "Trans-Florida Airlines",
    TFB: "Tair Airways",
    TFF: "Talon Air",
    TFG: "MAS Airways",
    TFH: "Thai Flying Helicopter Service",
    TFI: "Transport Facilitators",
    TFK: "Transafrik International",
    TFL: "Arkefly",
    TFN: "Norwegian Aviation College",
    TFO: "Transportes Aereos del Pacifico",
    TFT: "Thai Flying Service",
    TFU: "213 Flight Unit",
    TFY: "Tayside Aviation",
    TGA: "Air Togo",
    TGC: "TG Aviation",
    TGE: "Trabajos Aereos",
    TGI: "Transportes Aereos Regionales",
    TGM: "TAG Aviation Espana",
    TGN: "Trigana Air Service",
    TGO: "Transport Canada",
    TGT: "SAAB Nyge Aero",
    TGW: "Tiger Airways",
    TGX: "Transair Gabon]",
    TGY: "Trans Guyana Airways",
    TGZ: "Georgian Airways",
    THA: "Thai Airways International",
    THB: "Spark Air",
    THC: "Tar Heel Aviation",
    THD: "611897 Alberta Limited",
    THE: "Toumai Air Tchad",
    THF: "Touraine Helicoptere",
    THG: "Thai Global Airline",
    THI: "TransHolding",
    THJ: "Thai Jet Intergroup",
    THK: "Turk Hava Kurumu Hava Taksi Isletmesi",
    THM: "Airmark Aviation",
    THN: "International Security Assistance Force",
    THO: "TACA De Honduras",
    THR: "Tehran Airline",
    THS: "TransBrasil Airlines",
    THT: "Air Tahiti Nui",
    THU: "Thunder Airlines",
    THY: "Turkish Airlines",
    THZ: "Trans Helicoptere Service",
    TIA: "Trans International Airlines",
    TIB: "TRIP Linhas A",
    TIC: "Travel International Air Charters",
    TIE: "Time Air",
    TIH: "S C Ion Tiriac",
    TIK: "Tic Air",
    TIL: "Tajikistan International Airlines",
    TIM: "TEAM Transportes Aereos",
    TIN: "Taino Tours",
    TIP: "C and M Aviation",
    TIR: "Antair",
    TIS: "Tesis",
    TIW: "Transcarga Intl Airways",
    TJA: "T.J. Air",
    TJK: "Tajikair",
    TJN: "Tien-Shan",
    TJS: "Tyrolean Jet Service",
    TJT: "Twin Jet",
    TKC: "Tikal Jets Airlines",
    TKE: "Take Air Line",
    TKR: "Canadian Interagency Forest Fire Centre",
    TKS: "Tomsk-Avia",
    TKX: "Tropical International Airways",
    TKY: "Thai Sky Airlines",
    TLA: "Translift Airways",
    TLB: "Atlantique Air Assistance",
    TLC: "Caribbean Express",
    TLD: "Aero Taxi Autlan",
    TLE: "Aero Util",
    TLF: "Transport Africa",
    TLL: "Trans Atlantic Airlines",
    TLM: "Tramm Airlines",
    TLO: "Eagle Canyon Airlines",
    TLP: "Tulip Air",
    TLR: "Air Libya Tibesti",
    TLS: "TLC Air",
    TLT: "Turtle Airways",
    TLU: "Aero Toluca Internactional",
    TLV: "Travelair",
    TLW: "Teamline Air",
    TLX: "Telesis Transair",
    TLY: "Top-Fly",
    TMA: "Trans Mediterranean Airlines",
    TMD: "Transmed Airlines",
    TME: "Aero Taxi del Centro de Mexico",
    TMG: "Tri-MG Intra Asia Airlines",
    TMH: "Taxis Turisticos Marakame",
    TMI: "Tamir Airways",
    TMK: "Tomahawk Airways",
    TML: "TAM",
    TMM: "TMC Airlines",
    TMP: "Arizona Express Airlines",
    TMQ: "TRAM",
    TMR: "Timberline Air",
    TMS: "Temsco Helicopters",
    TMT: "Trans Midwest Airlines",
    TMW: "Trans Maldivian Airways",
    TMX: "Tramon Air",
    TMY: "Transportes Aereos del Mundo Maya",
    TMZ: "Tranporte Amazonair",
    TNA: "TransAsia Airways",
    TNB: "Trans Air-Benin",
    TNC: "National Aviation Consultants",
    TND: "Aero Taxis Cessna",
    TNE: "Taxis Aereos del Noroeste",
    TNF: "Transafricaine",
    TNG: "Tennessee Air National Guard 164th Airlift Group",
    TNI: "Transair International Linhas Aereas",
    TNL: "Tengeriyn Ulaach Shine",
    TNM: "Tiara Air",
    TNO: "Aerotransporte de Carga Union",
    TNP: "Transped Aviation",
    TNR: "Tanana Air Services",
    TNS: "Transilvania",
    TNT: "Trans North Turbo Air",
    TNU: "TransNusa Air",
    TNV: "Transnorthern",
    TNW: "Trans Nation Airways",
    TNX: "Trener Ltd",
    TNY: "Twin Town Leasing Company",
    TOB: "Tobruk Air",
    TOC: "Aerotropical",
    TOF: "RAF Topcliffe Flying Training Unit",
    TOH: "Air Tomisko",
    TOJ: "TOJ Airlines",
    TOK: "Airlines PNG",
    TOL: "Tol-Air Services",
    TOM: "Thomsonfly",
    TON: "Aero Tanala",
    TOP: "Top Air",
    TOR: "Toronto Airways",
    TOS: "Tropic Air",
    TOT: "Totavia",
    TOW: "Tower Air",
    TOX: "Neosiam Airways",
    TOY: "Toyota Canada",
    TPA: "TAMPA",
    TPB: "Aero Tropical",
    TPC: "Air Cal",
    TPD: "Top Speed",
    TPF: "Taxis Aereos del Pacifico",
    TPG: "Transportes Aereos Pegaso",
    TPK: "Air Horizon",
    TPL: "TAR Interpilot",
    TPM: "Transpais Aereo",
    TPN: "Transportacion Aerea del Norte",
    TPO: "Aero Taxi del Potosi",
    TPP: "Transpac Express",
    TPR: "Taxis Aereos De Parral",
    TPS: "TAPSA Transportes Aereos Petroleros",
    TPT: "Transportes Aereo del Sureste",
    TPU: "Trans American Airlines (Trans Am)",
    TPV: "Thai Pacific Airlines Business",
    TPX: "Transportes Aereos De Xalapa",
    TPY: "Trans-Provincial Airlines",
    TPZ: "Transportes La Paz",
    TQF: "United Kingdom Royal VIP Flights",
    TQM: "JM Family Aviation",
    TQN: "Taquan Air Services",
    TQR: "Transportacion Aerea De Queretaro",
    TQS: "Aeroturquesa",
    TRA: "Transavia Holland",
    TRB: "Ukraine Transavia",
    TRC: "Trans Air Charter",
    TRD: "Trans Island Air",
    TRF: "Taxi Air Fret",
    TRG: "TRAGSA (Medios Aereos)",
    TRH: "Airmark Aviation",
    TRI: "Executive Flight Operations Ontario Government",
    TRJ: "Trans Euro Air",
    TRK: "Turkuaz Airlines",
    TRL: "Starlite Aviation",
    TRM: "Transport Aerien de Mauritanie",
    TRN: "Servicios Aereos Corporativos",
    TRO: "Tropic Airlines-Air Molokai",
    TRP: "Maryland State Police",
    TRQ: "Turdus Airways",
    TRR: "Tramson Limited",
    TRS: "AirTran Airways",
    TRT: "Trans Arabian Air Transport",
    TRU: "Triangle Airline (Uganda)",
    TRW: "Transwestern Airlines of Utah",
    TRY: "Tristar Airlines",
    TRZ: "TransMeridian Airlines",
    TSA: "Transair France",
    TSC: "Air Transat",
    TSD: "TAF-Linhas Aereas",
    TSE: "Transmile Air Services",
    TSG: "Trans-Air-Congo",
    TSH: "Regional 1",
    TSI: "Transport'air",
    TSJ: "Trast Aero",
    TSK: "Trast Aero",
    TSL: "Thai Aviation Services",
    TSM: "Trans Sayegh Airport Services",
    TSN: "Trans-Air Services",
    TSO: "Transaero Airlines",
    TSP: "Transportes Aereos Inter",
    TSQ: "airtransse",
    TSR: "Trans Service Airlift",
    TSS: "Tri-State Aero",
    TST: "TRAST",
    TSU: "Gulf & Caribbean Cargo / Contract Air Cargo",
    TSV: "Tropair Airservices",
    TSW: "Transwings",
    TSX: "Thai Star Airlines",
    TSY: "Tristar Air",
    TTA: "TTA - Sociedade de Transporte e Trabalho Aereo",
    TTB: "Aerolineas Turisticas del Caribe",
    TTC: "Transteco",
    TTE: "Avcenter",
    TTF: "224th Flight Unit",
    TTH: "Tarhan Tower Airlines",
    TTL: "Total Linhas Aereas",
    TTM: "Societe Tout Tranport Mauritanien",
    TTP: "Triple O Aviation",
    TTR: "Transportaciones Y Servicios Aereos",
    TTS: "Transporte Aereo Tecnico Ejecutivo",
    TTZ: "Transair",
    TUA: "Turkmenistan Airlines",
    TUB: "TUI Airlines Belgium",
    TUC: "Turismo Aereo de Chile",
    TUD: "Flight Alaska",
    TUI: "Tuninter",
    TUK: "Ocean Wings Commuter Service",
    TUL: "Tulpar Air",
    TUM: "Tyumenspecavia",
    TUN: "Air Tungaru",
    TUO: "Taxi Aereo Turistico",
    TUP: "Aviastar-Tu",
    TUR: "ATUR",
    TUS: "ABSA - Aerolinhas Brasileiras",
    TUX: "Tulpar Air Service",
    TUY: "Linea Turistica Aerotuy",
    TUZ: "Tuna Aero",
    TVA: "Trans America Airlines",
    TVF: "Transavia France",
    TVH: "Trabajos Aereos Vascongados",
    TVI: "Tiramavia",
    TVJ: "Thai Vietjet Air",
    TVL: "Travel Service",
    TVO: "Transavio",
    TVR: "Tavrey Airlines",
    TVS: "Travel Service",
    TWA: "Trans World Airlines",
    TWB: "Tway Airlines",
    TWD: "Turkish Wings Domestic",
    TWE: "Transwede Airways",
    TWF: "247 Jet Ltd",
    TWJ: "Twinjet Aircraft Sales",
    TWL: "Tradewinds Aviation",
    TWM: "Transairways",
    TWN: "Avialeasing Aviation Company",
    TWO: "Twente Airlines",
    TWW: "Trans Air Welwitchia",
    TXA: "Texair Charter",
    TXB: "Bell Helicopter Textron",
    TXC: "Transaviaexport",
    TXD: "Aerotaxis del Noroeste",
    TXE: "Transilvania Express",
    TXF: "Aerotaxis Alfe",
    TXI: "Aereotaxis",
    TXL: "Taxi Aereo Cozatl",
    TXM: "Taxi Aereo de Mexico",
    TXN: "Texas National Airlines",
    TXO: "Taxis Aereos de Sinaloa",
    TXP: "Texas Spirit",
    TXR: "Taxirey",
    TXS: "Texas Airlines",
    TXT: "Texas Air Charters",
    TXU: "ATESA Aerotaxis Ecuatorianos",
    TXV: "Puerto Vallarta Taxi Aereo",
    TXW: "Texas Wings",
    TXX: "Austin Express",
    TXZ: "Tex Star Air Freight",
    TYF: "Tayflite",
    TYG: "Trygg-Flyg",
    TYR: "Tyrolean Airways",
    TYS: "TransHolding System",
    TYW: "Tyrol Air Ambulance",
    TZA: "Aero Tomza",
    TZE: "Transporte Aereo Ernesto Saenz",
    TZK: "Tajikstan",
    TZT: "Air Zambezi",
    TZU: "Servicios Aereos Tamazula",
    UAA: "University Air Squadron",
    UAB: "United Arabian Airlines",
    UAC: "United Air Charters",
    UAD: "University Air Squadron",
    UAE: "Emirates",
    UAF: "United Arab Emirates Air Force",
    UAG: "Afra Airlines",
    UAH: "University Air Squadron",
    UAI: "Union Africaine des Transports",
    UAJ: "University Air Squadron",
    UAK: "Kiev Aviation Plant",
    UAL: "United Airlines",
    UAR: "Aerostar Airlines",
    UAT: "Ukraine Atlantic",
    UAY: "University of Birmingham Air Squadron (RAF)",
    UBA: "Myanma Airways",
    UBD: "United Airways",
    UBG: "US-Bangla Airlines",
    UCA: "CommutAir",
    UCC: "Uganda Air Cargo",
    UCH: "US Airports Air Charter",
    UCK: "Air Division of the Eastern Kazakhstan Region",
    UCO: "Ucoaviacion",
    UCR: "Aero-Charter Ukraine",
    UCS: "United Carriers Systems",
    UDA: "Psudiklat Perhubungan Udara/PLP",
    UDC: "DonbassAero",
    UDN: "Dniproavia",
    UEA: "United Eagle Airlines",
    UED: "Air LA",
    UEJ: "Jetcorp",
    UES: "Ues-Avia Aircompany",
    UFA: "State Flight Academy of Ukraine",
    UFS: "UFS",
    UGA: "Uganda Airlines",
    UGC: "Urgemer Canarias",
    UGL: "Inter-Island Air",
    UGN: "Yuzhnaya Aircompany",
    UGP: "Shar Ink",
    UGX: "East African",
    UHL: "Ukrainian Helicopters",
    UHS: "Ulyanovsk Higher Civil Aviation School",
    UIA: "Uni Air",
    UJR: "Universal Jet Rental de Mexico",
    UJT: "Universal Jet Aviation",
    UJX: "AtlasGlobal Ukraine",
    UKA: "Buzzaway Limited",
    UKI: "UK International Airlines",
    UKL: "Ukraine Air Alliance",
    UKM: "UM Airlines",
    UKN: "Ukraine Air Enterprise",
    UKP: "United Kingdom Home Office",
    UKR: "Air Ukraine",
    UKS: "Ukrainian Cargo Airways",
    UKU: "Second Sverdlovsk Air Enterprise",
    UKW: "Lviv Airlines",
    UKZ: "Fly Zoom",
    ULA: "Zuliana de Aviacion",
    ULS: "Carroll Air Service",
    ULT: "Ultrair",
    UMA: "Lineas Aereas del Humaya",
    UMB: "Air Umbria",
    UMK: "Yuzhmashavia",
    UMS: "Unitemp-M",
    UNC: "Uni-Fly",
    UND: "Atuneros Unidos de California",
    UNF: "Union Flights",
    UNJ: "Universal Jet",
    UNR: "Rivne Universal Avia",
    UNS: "Unsped Paket Servisi",
    UNT: "Servicios Aereos Universitarios",
    UNU: "Unifly Servizi Aerei",
    UNY: "Lund University School of Aviation",
    UPA: "Air Foyle",
    UPL: "Ukrainian Pilot School",
    UPS: "United Parcel Service",
    URA: "Aircompany Rosavia",
    URG: "Air Urga",
    URJ: "Star Air",
    URN: "Turan Air",
    URP: "ARP 410 Airlines",
    URR: "Aurora Airlines",
    URV: "Uraiavia",
    URY: "Century Aviation",
    USA: "US Airways",
    USB: "Tusheti",
    USC: "US Check Airlines",
    USF: "USAfrica Airways",
    USH: "US Helicopter",
    USJ: "US Jet",
    USK: "Skif-Air",
    USN: "Smarkand Aero Servise",
    UST: "Austro Aereo",
    USX: "US Express",
    UTA: "UTair Aviation",
    UTG: "UTAGE",
    UTM: "TAPC Aviatrans Aircompany",
    UTN: "Trans-Ulgii",
    UTR: "Utair South Africa",
    UTS: "Ukrainian State Air Traffic Service Enterprise",
    UTT: "Transarabian Transportation Services",
    UTU: "Urartu-Air",
    UTX: "Avfinity",
    UTY: "Alliance Airlines",
    UVA: "Universal Airways",
    UVG: "Universal Airlines",
    UVM: "Uvavemex",
    UVN: "United Aviation",
    UVS: "Air Universal",
    UVT: "Auvia Air",
    UWW: "LSM International ",
    UYC: "Cameroon Airlines",
    UZA: "Constanta Airline",
    UZB: "Uzbekistan Airways",
    UZS: "Samarkand Airways",
    VAA: "Van Air Europe",
    VAB: "Airtrans Ltd",
    VAC: "Vacationair",
    VAD: "Aero Taxi Los Valles",
    VAE: "Air Evans",
    VAG: "Vega",
    VAI: "Avalair",
    VAL: "Voyageur Airways",
    VAM: "Ameravia",
    VAN: "Caravan Air",
    VAP: "Phuket Air",
    VAS: "ATRAN Cargo Airlines",
    VAT: "Visionair",
    VAU: "V Australia Airlines",
    VAV: "Angkor Air",
    VAX: "V Air",
    VAZ: "Airlines 400",
    VBA: "V Bird Airlines Netherlands",
    VBC: "AVB-2004 Ltd",
    VBD: "V-Berd-Avia",
    VBG: "North-West Air Transport Company - Vyborg",
    VBI: "Virgin Blue",
    VBW: "Air Burkina",
    VCA: "VICA - Viacao Charter Aereos",
    VCH: "Consorcio Helitec",
    VCI: "CI-Tours",
    VCM: "Volare Air Charter Company",
    VCN: "Execujet Charter",
    VCR: "Cruiser Linhas Aereas",
    VCT: "Viscount Air Service",
    VCV: "Conviasa",
    VCX: "Ocean Airlines",
    VDA: "Volga-Dnepr Airlines",
    VDO: "Servicios Aereos Avandaro",
    VDR: "Air Vardar",
    VEA: "Vega Airlines",
    VEC: "Venescar Internacional",
    VEE: "Victor Echo",
    VEG: "Aerovega",
    VEI: "Virgin Express Ireland",
    VEJ: "Aero Ejecutivos",
    VEN: "Transaven",
    VER: "Almaver",
    VES: "Vieques Air Link",
    VEX: "Virgin Express",
    VFC: "Vasco Air",
    VFT: "VZ Flights",
    VGA: "Air Vegas",
    VGC: "Vanguardia en Aviacion en Colima",
    VGD: "Vanguard Airlines",
    VGF: "Aerovista Gulf Express",
    VGN: "Virgin Nigeria Airways",
    VGS: "Stichting Vliegschool 16Hoven",
    VGV: "Vologda State Air Enterprise",
    VHA: "VH-Air Industrie",
    VHM: "VHM Schul-und-Charterflug",
    VHT: "Corporate Flight International",
    VIA: "VIA Líneas Aéreas",
    VIB: "Vibroair Flugservice",
    VIC: "VIP Servicios Aereos Ejecutivos",
    VID: "Aviaprad",
    VIE: "VIP Empresarial",
    VIF: "VIF Luftahrt",
    VIG: "Vega Aviation",
    VIH: "Vichi",
    VIK: "Viking Airlines",
    VIM: "Air VIA",
    VIN: "Vinair - Helicoptereos",
    VIP: "Tag Aviation UK",
    VIR: "Virgin Atlantic Airways",
    VIS: "Vision Air International",
    VIV: "Aeroenlaces Nacionales",
    VIZ: "Aerovis Airlines",
    VJA: "ValueJet",
    VJC: "VietJet Air",
    VJE: "Avjet International (FZE)",
    VJM: "Viajes Ejecutivos Mexicanos",
    VJT: "Vistajet",
    VKG: "Mytravel Airways",
    VKH: "Viking Hellas",
    VKJ: "VickJet",
    VLA: "Valan International Cargo Charter",
    VLE: "Volare Airlines",
    VLF: "DFS UK Limited",
    VLG: "Vueling Airlines",
    VLI: "Avolar Aerolineas",
    VLK: "Vladivostok Air",
    VLL: "RAF Valley SAR Training Unit",
    VLM: "VLM Airlines",
    VLN: "Valan Limited",
    VLO: "Varig Log",
    VLR: "Aerolineas Villaverde",
    VLS: "Aero Virel",
    VLT: "Vertical-T Air Company",
    VLU: "Valuair",
    VLV: "Avialift Vladivostok",
    VLX: "Biz Jet Charter",
    VMA: "Vero Monmouth Airlines",
    VME: "Aviacion Comercial de America",
    VMM: "Grupo Vuelos Mediterraneo",
    VMP: "Execujet Scandinavia",
    VMR: "Aero Vilamoura",
    VNA: "Empresa Aviacion Interamericana",
    VNE: "Empresa Venezolana",
    VNG: "Aero Servicios Vanguardia",
    VNK: "Vipport Joint Stock Company",
    VNP: "Virgin Pacific",
    VNT: "Avient Air Zambia",
    VNZ: "Tbilaviamsheni",
    VOA: "Viaggio Air",
    VOE: "VOLOTEA Airways",
    VOG: "Voyager Airlines",
    VOI: "Volaris",
    VOL: "Blue Chip Jet",
    VOO: "Volotea",
    VOS: "Rovos Air",
    VOZ: "Virgin Australia",
    VPA: "VIP Air",
    VPB: "Veteran Air",
    VPP: "Vintage Props and Jets",
    VPV: "VIP-Avia",
    VQI: "Flyme (VP)",
    VRA: "Vertair",
    VRB: "Silverback Cargo Freighters",
    VRC: "Taxi de Veracruz",
    VRD: "Virgin America",
    VRE: "Volare Airlines",
    VRI: "Aerotaxi Villa Rica",
    VRL: "Voar Lda",
    VRN: "VRG Linhas Aereas",
    VRO: "Aerovitro",
    VRS: "Sirvair",
    VRT: "Averitt Air Charter",
    VSA: "Avstar Aviation",
    VSB: "Vickers Limited",
    VSC: "AVESCA",
    VSG: "Visig Operaciones Aereas",
    VSN: "Vision Airways Corporation",
    VSO: "Voronezh Aircraft Manufacturing Society",
    VSP: "VASP",
    VSR: "Aviostart AS",
    VSS: "Virign Islands Seaplane Shuttle",
    VSV: "Scat Air",
    VTA: "Air Tahiti",
    VTC: "Vuelos Especializados Tollocan",
    VTE: "Corporate Flight Management",
    VTG: "Aviacao Transportes Aereos e Cargas",
    VTH: "Vuelos Corporativos de Tehuacan",
    VTI: "Air Vistara",
    VTK: "Vostok Airlines",
    VTL: "Victor Tagle Larrain",
    VTM: "Aeronaves TSM",
    VTS: "Everts Air Alaska/Everts Air Cargo",
    VTT: "Avia Trans Air Transport",
    VTV: "Vointeh",
    VTX: "Verataxis",
    VTY: "Air Midwest",
    VUE: "AD Aviation",
    VUL: "Elios",
    VUN: "Air Ivoire",
    VUO: "Aerovuelox",
    VUR: "Vuelos Internos Privados VIP",
    VUS: "Vuela Bus",
    VVA: "Aviast Air",
    VVC: "VivaColombia",
    VVF: "Dunyaya Bakis Hava Tasimaciligi",
    VVG: "Aerovilla",
    VVM: "Viva Macau",
    VVN: "88",
    VWA: "Virginwings",
    VXG: "Avirex",
    VXN: "Sunset Aviation",
    VXP: "Mali Air Express",
    VXX: "Aviaexpress Aircompany",
    VYT: "RAF Valley Flying Training Unit",
    VZA: "ViznAir",
    VZL: "Vzlyet",
    VZR: "Aviazur",
    WAA: "Westair Aviation",
    WAB: "Aero Industries Inc",
    WAC: "West African Cargo Airlines",
    WAD: "Waddington FTU",
    WAE: "Western Air Express",
    WAF: "Flamenco Airways",
    WAG: "Wisconsin Air National Guard",
    WAJ: "AirAsia Japan",
    WAK: "Wings of Alaska",
    WAL: "Western Airlines",
    WAM: "Air Taxi & Cargo",
    WAP: "Arrow Panama",
    WAR: "NZ Warbirds Association",
    WAS: "Walsten Air Services",
    WAT: "Wings Air Transport",
    WAU: "Wizz Air Ukraine",
    WAV: "Warbelow's Air Ventures",
    WAW: "Wings Airways",
    WAY: "Airways",
    WBA: "Finncomm Airlines",
    WBR: "Multi-Aero",
    WCA: "West Coast Airways",
    WCB: "West Africa Airlines",
    WCC: "Sport Air Travel",
    WCG: "West Coast Airlines",
    WCO: "Columbia Helicopters",
    WCP: "Primaris Airlines",
    WCR: "West Caribbean Costa Rica",
    WCW: "West Caribbean Airways",
    WCY: "Viking Express",
    WDA: "Wimbi Dira Airways",
    WDG: "Ministry of Agriculture",
    WDK: "Oxford Air Services",
    WDL: "WDL Aviation",
    WDR: "Air Net Private Charter",
    WDS: "Four Winds Aviation",
    WDY: "Chicago Express",
    WEA: "White Eagle Aviation",
    WEB: "WebJet Linhas A",
    WEC: "Universal Airlines",
    WEL: "Veles",
    WEN: "WestJet Encore",
    WER: "AeroWorld ",
    WES: "Western Express Air Lines",
    WEV: "Victoria International Airways",
    WEW: "West Wind Aviation",
    WEX: "Wings Express",
    WFC: "Swift Copters",
    WFD: "BAE Systems",
    WFO: "Wilbur's Flight Operations",
    WFX: "Westfalia Express VA",
    WGA: "Vega Air Company",
    WGC: "Worldways",
    WGP: "Williams Grand Prix Engineering",
    WGS: "Airwings oy",
    WGT: "Lion Air Services",
    WHE: "Westland Helicopters",
    WHR: "Hummingbird Helicopter Service",
    WHS: "Wiking Helikopter Service",
    WHT: "White",
    WHY: "Air Sorel",
    WIA: "Windward Islands Airways International",
    WIF: "Widerøe",
    WIG: "Wiggins Airways",
    WIL: "Aero Air",
    WIN: "Winlink",
    WIS: "Paccair",
    WIT: "Wittering FTU",
    WIW: "V-avia Airline",
    WIZ: "Micromatter Technology Solutions",
    WJA: "WestJet",
    WKH: "Kharkov Aircraft Manufacturing Company",
    WLA: "Airwaves Airlink",
    WLB: "Wings of Lebanon Aviation",
    WLC: "Welcome Air",
    WLG: "Volga Aviaexpress",
    WLK: "Skyrover CC",
    WLR: "Air Walser",
    WLS: "Williams Air",
    WLT: "Aviation Partners",
    WLV: "Aviation North",
    WLX: "West Air Luxembourg",
    WML: "Chantilly Air",
    WNA: "Winair",
    WNR: "Wondair on Demand Aviation",
    WOA: "World Airways",
    WOK: "Kovar Air",
    WOL: "Wings Aviation",
    WON: "Wings Air",
    WOW: "Air Southwest",
    WPA: "Western Pacific Airservice",
    WPK: "Air-Lift Associates",
    WPR: "Auckland Regional Rescue Helicopter Trust",
    WPT: "Wapiti Aviation",
    WRA: "White River Air Services",
    WRC: "Wind Rose Aviation",
    WRF: "Wright Air Service",
    WRR: "WRA Inc",
    WRT: "Wright Air Lines",
    WSA: "Westgates Airlines",
    WSC: "Westair Cargo Airlines",
    WSF: "West African Airlines",
    WSG: "Wasaya Airways",
    WSI: "Wind Spirit Air",
    WSL: "Westflight Aviation",
    WSM: "Wisman Aviation",
    WSS: "World Scale Airlines",
    WST: "Western Air",
    WTA: "Africa West",
    WTC: "Weasua Air Transport Company",
    WTF: "West African Air Transport",
    WTJ: "Whitejets",
    WTN: "BAE Systems",
    WTP: "Westpoint Air",
    WTV: "Western Aviators",
    WVA: "Hand D Aviation",
    WVL: "Wizz Air Hungary",
    WWD: "Westward Airways",
    WWI: "Worldwide Jet Charter",
    WWL: "Whyalla Airlines",
    WWM: "World Wing Aviation",
    WWS: "Worldwide Aviation Services",
    WYC: "Wycombe Air Centre",
    WYG: "Wyoming Airlines",
    WYT: "2 Sqn No 1 Elementary Flying Training School",
    WZP: "Zip",
    WZZ: "Wizz Air",
    XAA: "Aeronautical Radio Inc",
    XAB: "Xabre Aerolineas",
    XAC: "Air Charter World",
    XAD: "Certified Air Dispatch",
    XAF: "Executive Air Fleet",
    XAH: "Executive Aircraft Services",
    XAK: "Airkenya",
    XAM: "AMR Services Corporation",
    XAN: "Southjet cargo",
    XAO: "Airline Operations Services",
    XAP: "Direct Air trading as Midway Connection",
    XAR: "Travel Express Aviation Services",
    XAS: "PHH Aviation System",
    XAT: "AT and T Aviation Division",
    XAU: "XAIR USA",
    XAV: "Aviaprom Enterprises",
    XAX: "AirAsia X",
    XBG: "City of Bangor",
    XBM: "CBM America",
    XBO: "Baseops International",
    XCC: "Ecoturistica de Xcalak",
    XCL: "Contel ASC",
    XCO: "Compuflight Operations Service",
    XCS: "Compuserve Incorporated",
    XCT: "Aero Costa Taxi Aereo",
    XCX: "Citibank",
    XDA: "Bureau Veritas",
    XDD: "Lockheed DUATS",
    XDS: "Dispatch Services",
    XDT: "Date Transformation Corp",
    XDY: "Dynair Services",
    XEC: "Air Executive Charter",
    XEL: "Excel Charter",
    XER: "Xerox Corporation",
    XFS: "American Flight Service Systems",
    XFX: "Airways Corporation of New Zealand",
    XGA: "General Aviation Terminal",
    XGG: "IMP Group Aviation Services",
    XGS: "Global System",
    XGW: "Global Weather Dynamics",
    XIA: "Irving Oil",
    XJA: "Assistance Aeroportuaire de L'Aeroport de Paris",
    XJC: "Xclusive Jet Charter Limited",
    XJT: "Xjet Limited",
    XKA: "Kavouras Inc",
    XKX: "ASECNA",
    XLA: "Excel Airways",
    XLB: "Aircraft Performance Group",
    XLD: "Jeppesen Data Plan",
    XLG: "Lockheed Air Terminal",
    XLK: "Safarilink Aviation",
    XLL: "Air Excel",
    XLT: "Empressa Brasileira de Infra-Estrutura Aeroportuaria-Infraero",
    XMA: "Martin Aviation Services",
    XME: "Australian air Express",
    XMG: "AMS Group",
    XMR: "Irish Aviation Authority",
    XMS: "British Airways",
    XMX: "SENEAM",
    XNA: "Express Net Airlines",
    XNC: "Canadian National Telecommunications",
    XNR: "Taxi Aero Del Norte",
    XNS: "Navtech System Support",
    XNT: "Notams International",
    XNV: "Navinc Airlines Services",
    XOJ: "XOJET",
    XPA: "Pan Am Weather Systems",
    XPE: "Amira Air",
    XPG: "Southport Air Service",
    XPL: "Express Line Aircompany",
    XPR: "Air-Rep",
    XPS: "XP Internation",
    XPT: "XPTO",
    XPX: "Phoenix Flight Operations",
    XRA: "Intensive Air",
    XSA: "Spectrum Air Service",
    XSL: "Embry-Riddle Aeronautical University",
    XSN: "Stephenville Aviation Services",
    XSR: "Executive AirShare",
    XSS: "Aero Express Intercontinental",
    XTA: "Servicios Aereos Textra",
    XTJ: "Advance Aviation Services",
    XTO: "Express Tours",
    XTR: "Sector Airlines",
    XWS: "WSI Corporation",
    XWW: "World Weatherwatch",
    XXS: "Skyplan Services",
    XXV: "AASANA",
    XXX: "ASL",
    XYZ: "Island Air Express",
    YAK: "Yakolev",
    YBE: "Stewart Aviation Services",
    YCC: "Ciel Canadien",
    YCP: "Canadian National Airways",
    YEL: "Yellowtail",
    YEP: "YES Airways",
    YFS: "Young Flying Service",
    YOG: "Central Aviation",
    YRG: "Air Yugoslavia",
    YYY: "ICAO",
    YZR: "Yangtze River Express",
    YZZ: "LSM AIRLINES ",
    ZAI: "Zaire Aero Service",
    ZAK: "Airlink Zambia",
    ZAN: "Zantop International Airlines",
    ZAR: "Zairean Airlines",
    ZAS: "ZAS Airlines of Egypt",
    ZAW: "Zoom Airways",
    ZBA: "Boskovic Air Charters Limited",
    ZCS: "Southjet connect",
    ZMA: "Zambezi Airlines",
    ZNA: "Zenith International Airline",
    ZTF: "Mongolian International Air Lines ",
    ZTT: "ZABAIKAL AIRLINES",
    ZXY: "Japan Regio",
    ZYZ: "Caribbean Wings",
    ZZM: "Agence Nationale des Aerodromes et de la Meteorologie",
    ZZZ: "Zabaykalskii Airlines",
}

export const resolveOperatorFromCallsign = (callsign: string) => {
    const prefix = callsign.trim().slice(0, 3).toUpperCase()
    return AIRLINE_ICAO_PREFIXES[prefix] ?? null
}
